/**
 * Spanish message catalog. Covers the same in-scope keys as the English base for the
 * localized surfaces (sidebar nav, footer status, user menu, settings shell, language
 * panel). A key absent here falls back to English via the resolver, so partial coverage
 * degrades cleanly rather than showing a raw key.
 */
import type { Messages } from "../types";

export const es: Messages = {
  "nav.group.build":   "Crear",
  "nav.group.ship":    "Publicar",
  "nav.group.operate": "Operar",
  "nav.group.account": "Cuenta",
  "nav.group.help":    "Centro de ayuda",

  "nav.item.dashboard":     "Panel",
  "nav.item.projects":      "Proyectos",
  "nav.item.deployments":   "Despliegues",
  "nav.item.mobileBuilds":  "Compilaciones moviles",
  "nav.item.marketplace":   "Mercado",
  "nav.item.creativeStudio": "Estudio creativo",
  "nav.item.library":       "Biblioteca",
  "nav.item.creatorStudio": "Estudio de creador",
  "nav.item.billing":       "Facturacion",
  "nav.item.settings":      "Configuracion",
  "nav.item.helpCenter":    "Centro de ayuda",
  "nav.item.capabilities":  "Capacidades",
  "nav.item.whatsNew":      "Novedades",
  "nav.item.documents":     "Documentos",
  "nav.item.engineering":   "Ingenieria",

  "footer.status.nominal":  "sistemas en orden",
  "footer.status.degraded": "degradado",
  "footer.status.outage":   "interrupcion detectada",

  "menu.billing":              "Facturacion",
  "menu.settings":             "Configuracion",
  "menu.platformIntelligence": "Inteligencia de plataforma",
  "menu.signOut":              "Cerrar sesion",
  "menu.admin":                "Administrador",
  "menu.userFallback":         "Usuario",
  "menu.language":             "Idioma",

  "settings.item.profile":       "Perfil",
  "settings.item.security":      "Seguridad",
  "settings.item.notifications": "Notificaciones",
  "settings.item.billing":       "Facturacion y creditos",
  "settings.item.apiKeys":       "Claves de API",
  "settings.item.connectors":    "Conectores",
  "settings.item.aiPreferences": "Comportamiento de IA",
  "settings.item.memory":        "Memoria",
  "settings.item.export": "Exportación de datos",
  "settings.item.audit": "Registro de auditoría",
  "settings.item.privacy":       "Privacidad y datos",
  "settings.item.systemStatus":  "Estado del sistema",
  "settings.item.language":      "Idioma de la interfaz",
  "settings.item.appearance":    "Apariencia",
  "settings.item.team":          "Equipo",
  "settings.item.webhooks":      "Webhooks",
  "settings.item.helpCenter":    "Centro de ayuda",
  "settings.item.dangerZone":    "Zona de peligro",
  "settings.item.integrations":  "Integraciones",

  "settings.title":    "Configuracion",
  "settings.subtitle": "Gestiona tu cuenta, seguridad, integraciones y datos desde un solo lugar.",

  "language.title":       "Idioma de la interfaz",
  "language.description": "Elige el idioma de la interfaz de Nuvra. Esto no cambia el idioma de las aplicaciones que creas.",
  "language.label":       "Idioma",
  "language.search":      "Buscar idiomas",
  "language.noMatch":     "Ningun idioma coincide con tu busqueda.",
  "language.current":          "Actual",
  "language.autoSaved":         "Guardado automáticamente",
  "language.clearSearch":       "Borrar búsqueda",
  "language.trySearch":         "Prueba un nombre de idioma o un código ISO.",
  "language.scope":             "Se aplica únicamente a la interfaz de Nuvra. El idioma del software que creas se controla por separado.",
  "language.region.global":     "Global",
  "language.region.americas":   "Américas",
  "language.region.europe":     "Europa",
  "language.region.middle_east":"Oriente Medio",
  "language.region.africa":     "África",
  "language.region.asia":       "Asia",

  "notFound.kicker":    "Ruta no encontrada",
  "notFound.body":      "Esta ruta no existe en la fabrica. Vuelve a un sistema conocido.",
  "notFound.goBack":    "Volver",
  "notFound.dashboard": "Panel",

  "auth.backToHome":  "Volver al inicio",
  "auth.backToLogin": "Volver al inicio de sesion",
  "auth.email":       "Correo electronico",
  "auth.password":    "Contrasena",
  "auth.or":          "o",
  "auth.error":       "Error",
  "auth.signIn":      "Iniciar sesion",

  "auth.login.welcome":     "Bienvenido de nuevo",
  "auth.login.subtitle":    "Accede a tu fabrica de software con IA",
  "auth.login.forgot":      "Olvidaste tu contrasena?",
  "auth.login.noAccount":   "No tienes una cuenta?",
  "auth.login.signUpLink":  "Registrate",
  "auth.login.failedTitle": "Error al iniciar sesion",

  "auth.signup.title":           "Crea tu cuenta",
  "auth.signup.subtitle":        "Tu fabrica de software con IA te espera",
  "auth.signup.fullName":        "Nombre completo",
  "auth.signup.namePlaceholder": "Tu nombre",
  "auth.signup.createAccount":   "Crear cuenta",
  "auth.signup.haveAccount":     "Ya tienes una cuenta?",
  "auth.signup.loginLink":       "Inicia sesion",
  "auth.signup.failedTitle":     "Error al registrarse",
  "auth.signup.createdTitle":    "Cuenta creada!",
  "auth.signup.createdBody":     "Sesion iniciada. Tu espacio de trabajo esta listo.",

  "auth.forgot.title":          "Olvidaste tu contrasena",
  "auth.forgot.subtitleSent":   "Revisa tu correo para ver el enlace de restablecimiento",
  "auth.forgot.subtitlePrompt": "Introduce tu correo y te enviaremos un enlace de restablecimiento",
  "auth.forgot.sentBefore":     "Enviamos un enlace de restablecimiento de contrasena a",
  "auth.forgot.sentAfter":      ". El enlace expira en 60 minutos.",
  "auth.forgot.tryAnother":     "Probar con otro correo",
  "auth.forgot.send":           "Enviar enlace de restablecimiento",
  "auth.forgot.remember":       "Recuerdas tu contrasena?",

  "auth.reset.titleSuccess":    "Contrasena actualizada",
  "auth.reset.title":           "Establecer nueva contrasena",
  "auth.reset.subtitleSuccess": "Redirigiendote al inicio de sesion...",
  "auth.reset.subtitle":        "Elige una contrasena segura para tu cuenta",
  "auth.reset.successBody":     "Tu contrasena se restablecio correctamente. Te redirigiremos a la pagina de inicio de sesion en breve.",
  "auth.reset.verifying":       "Verificando tu enlace de restablecimiento...",
  "auth.reset.newPassword":     "Nueva contrasena",
  "auth.reset.confirmPassword": "Confirmar contrasena",
  "auth.reset.submit":          "Restablecer contrasena",
  "auth.reset.mismatchTitle":   "Las contrasenas no coinciden",
  "auth.reset.mismatchBody":    "Asegurate de que ambas contrasenas sean identicas.",
  "auth.reset.tooShortTitle":   "Contrasena demasiado corta",
  "auth.reset.tooShortBody":    "La contrasena debe tener al menos 8 caracteres.",
  "auth.reset.failedTitle":     "Error al restablecer",

  // Deploy page
  "deploy.header.kicker":   "Operaciones de despliegue",
  "deploy.header.title":    "Desplegar",
  "deploy.header.subtitle": "Previsualiza, promueve, publica, observa, revierte.",
  "deploy.header.refresh":  "Actualizar",
  "deploy.loadingProjects": "Cargando proyectos...",
  "deploy.noProjects":      "No se encontraron proyectos. Crea uno desde el Panel primero.",
  "deploy.env.preview":     "Vista previa",
  "deploy.env.production":  "Produccion",
  "deploy.env.domain":      "Dominio",
  "deploy.env.live":        "En vivo",
  "deploy.env.none":        "Ninguno",
  "deploy.tab.deploy":      "Desplegar",
  "deploy.tab.domains":     "Dominios",
  "deploy.tab.publishing":  "Publicacion",
  "deploy.tab.lineage":     "Historial",
  "deploy.tab.visibility":  "Visibilidad",
  "deploy.hosted.liveTitle":         "Tu aplicacion esta en vivo",
  "deploy.hosted.notPublishedTitle": "Aun no publicada",
  "deploy.hosted.subtitle":          "Nuvra aloja tu aplicacion en su runtime: publicacion instantanea, sin paso de compilacion.",
  "deploy.hosted.manageVisibility":  "Gestionar visibilidad",
  "deploy.hosted.liveUrl":           "URL en vivo",
  "deploy.hosted.vanityUrl":         "URL personalizada",
  "deploy.hosted.liveHint":          "Vuelve a compilar en el espacio de trabajo del proyecto para actualizar la aplicacion en vivo al instante. Usa la pestana Visibilidad para gestionar la URL personalizada o hacer la aplicacion privada.",
  "deploy.hosted.notPublishedHint":  "Compila tu aplicacion en el espacio de trabajo del proyecto, luego publicala con el interruptor de publicacion para hacerla accesible publicamente en una URL para compartir.",
  "deploy.time.justNow":    "ahora mismo",
  "deploy.time.minutesAgo": "hace {n}m",
  "deploy.time.hoursAgo":   "hace {n}h",

  // Projects page (the founder's project list)
  "projects.title":             "Proyectos",
  "projects.loading":           "Cargando tus proyectos",
  "projects.countOne":          "1 proyecto",
  "projects.countOther":        "{count} proyectos",
  "projects.new":               "Nuevo proyecto",
  "projects.search":            "Buscar proyectos",
  "projects.loadingList":       "Cargando proyectos",
  "projects.emptyTitle":           "Empieza tu primer proyecto de Nuvra.",
  "projects.emptyBody":            "Describe lo que quieres construir y Nuvra lo llevará por la cadena de ingeniería real. Tu proyecto se convierte en el espacio de trabajo duradero para ello.",
  "projects.page.eyebrow":             "Espacio de trabajo",
  "projects.page.subtitle":            "Tus proyectos, despliegues en vivo y actividad de ingeniería en un solo lugar.",
  "projects.page.searchPlaceholder":   "Buscar proyectos por nombre o descripción",
  "projects.page.sort":                "Ordenar",
  "projects.page.sortRecent":          "Recientes",
  "projects.page.sortName":            "Nombre",
  "projects.page.sortStatus":          "Estado",
  "projects.page.gridView":            "Vista de cuadrícula",
  "projects.page.listView":            "Vista de lista",
  "projects.page.readyBadge":          "Listo para construir",
  "projects.page.createProject":       "Crear proyecto",
  "projects.page.mobileBuild":         "Compilación móvil",
  "projects.page.yourProjects":        "Tus proyectos",
  "projects.page.yourProjectsBody":    "Abre un espacio de trabajo para seguir con la ingeniería donde la dejaste.",
  "projects.page.clearSearch":         "Borrar la búsqueda",
  "projects.page.tabAll":              "Todos",
  "projects.page.tabActive":           "Construyendo",
  "projects.page.tabDeployed":         "En vivo",
  "projects.page.tabDraft":            "Borrador / Atención",
  "projects.page.noMatchTitle":        "Ningún proyecto coincide",
  "projects.page.noMatchBody":         "Prueba con otro nombre, otra descripción u otro filtro.",
  "projects.page.usage":               "Uso",
  "projects.page.usageBody":           "El uso de la cuenta de un vistazo. El coste de cada compilación está disponible en la compilación correspondiente.",
  "projects.page.currentAccount":      "Cuenta actual",
  "projects.page.creditsRemaining":    "Créditos restantes",
  "projects.page.liveProjects":        "Proyectos en vivo",
  "projects.page.autoTopUp":           "Recarga automática",
  "projects.page.on":                  "Activada",
  "projects.page.off":                 "Desactivada",
  "projects.page.plan":                "Plan",
  "projects.page.manage":              "Gestionar",
  "projects.quick.newLabel":           "Proyecto nuevo",
  "projects.quick.newDesc":            "Empieza desde cero",
  "projects.quick.mobileLabel":        "Compilación móvil",
  "projects.quick.mobileDesc":         "iOS y Android",
  "projects.quick.marketLabel":        "Mercado",
  "projects.quick.marketDesc":         "Explorar extensiones",
  "projects.quick.deployLabel":        "Desplegar",
  "projects.quick.deployDesc":         "Publica tu proyecto",
  "projects.page.statProjects":        "Proyectos",
  "projects.page.statLive":            "En vivo",
  "projects.page.statBuilding":        "Construyendo",
  "projects.page.statAttention":       "Requiere atención",
  "projects.page.statCredits":         "Créditos",
  "projects.page.footerNav":           "Navegación del pie de página",
  "projects.page.footerDocs":          "Documentación",
  "projects.page.footerSupport":       "Soporte",
  "projects.page.footerStatus":        "Estado",
  "projects.page.footerPricing":       "Precios",
  "projects.noMatch":           "Ningun proyecto coincide con \"{query}\".",
  "projects.openLive":          "Abrir en vivo",
  "projects.actions":           "Acciones del proyecto",
  "projects.openWorkspace":     "Abrir espacio de trabajo",
  "projects.shareProject":      "Compartir proyecto",
  "projects.deleteProject":     "Eliminar proyecto",
  "projects.deleteTitle":       "Eliminar este proyecto?",
  "projects.deleteBody":        "\"{name}\" se eliminara de forma permanente. Esto no se puede deshacer.",
  "projects.cancel":            "Cancelar",
  "projects.delete":            "Eliminar",
  "projects.deleting":          "Eliminando...",
  "projects.status.building":   "Construyendo",
  "projects.status.live":       "En vivo",
  "projects.status.error":      "Error de construccion",
  "projects.status.draft":      "Borrador",
  "projects.error.title":   "No pudimos cargar tus proyectos",
  "projects.error.body":    "Suele ser temporal. Tus proyectos estan a salvo. Reintenta ahora o recarga la pagina.",
  "projects.error.details": "Detalles tecnicos",
  "projects.error.retry":   "Reintentar",
  "projects.time.justNow":      "Ahora mismo",
  "projects.time.minAgo":       "hace {min} min",
  "projects.time.hourAgo":      "hace {h} hora",
  "projects.time.hoursAgo":     "hace {h} horas",
  "projects.time.yesterday":    "Ayer",
  "projects.time.daysAgo":      "hace {d} dias",
  "projects.toast.deleteFail":  "No se pudo eliminar el proyecto",
  "projects.toast.deleted":     "Proyecto eliminado",
  "projects.toast.deletedBody": "\"{name}\" se elimino.",

  // Billing page (plan, usage, payments, auto top-up)
  "billing.title":            "Facturacion",
  "billing.subtitle": "Tu plan, tus creditos y cada pago, con seguimiento en tiempo real.",
  "billing.transparency":     "Los pagos, creditos y uso se registran en tiempo real con total transparencia.",
  "billing.tab.plan":         "Plan",
  "billing.tab.usage":        "Uso",
  "billing.tab.payments":     "Pagos",
  "billing.tab.settings":     "Ajustes",
  "billing.planBadge":        "Plan {tier}",
  "billing.creditsAvailable": "{balance} creditos disponibles",
  "billing.viewPlans":        "Ver planes",
  "billing.creditsUsedUp":    "Tus creditos se han agotado. Mejora tu plan para seguir construyendo.",
  "billing.upgradeTitle":     "Mejora tu plan",
  "billing.perMonth":         "/mes",
  "billing.paymentsInstant":  "Los pagos se procesan al instante una vez que las claves de la pasarela esten configuradas en tu despliegue.",
  "billing.creditBalance":    "Saldo de creditos",
  "billing.live":             "En vivo",
  "billing.creditsUsed":      "{used} creditos usados",
  "billing.paidBillingOptionsTitle": "Los creditos adicionales estan disponibles en los planes de pago",
  "billing.paidBillingOptionsBody": "Mejora a un plan de pago para comprar creditos adicionales cuando los necesites.",
  "billing.paymentPreferences": "Preferencias de pago",
  "billing.paymentPreferencesBody": "Las opciones de pago estan disponibles despues de elegir un plan de pago.",
  "billing.autoTopupBody": "Compra automaticamente el paquete de creditos seleccionado cuando tu saldo alcance el umbral.",
  "billing.acuUpgrade": "Tus creditos incluidos se han agotado. Mejora a un plan de pago para seguir construyendo.",
  "billing.lowBalance":       "Tu saldo de creditos es bajo. Recarga o activa la recarga automatica.",
  "billing.txHistory":        "Historial de transacciones",
  "billing.securePayments":   "Todos los pagos se procesan de forma segura. Los creditos se agregan solo despues de confirmar el pago.",
  "billing.topupPaidTitle":   "Las recargas son una funcion de pago",
  "billing.topupPaidBody":    "Mejora a un plan de pago para comprar creditos adicionales en cualquier momento de tu ciclo de facturacion.",
  "billing.seePlans":         "Ver planes",
  "billing.autoTopup":        "Recarga automatica",
  "billing.autoPaidTitle":    "La recarga automatica es una funcion de pago",
  "billing.autoPaidBody":     "Mejora a un plan de pago para activar las recargas automaticas de creditos.",
  "billing.enableAuto":       "Activar recarga automatica",
  "billing.thresholdZero":    "Cuando el saldo llegue a 0",
  "billing.thresholdBelow":   "Cuando este por debajo de {n} creditos",
  "billing.choosePackage":    "Elige un paquete de recarga",
  "billing.packageOption":    "{amount} creditos · {price}",
  "billing.autoTriggerNote":  "La recarga automatica solo se activa despues de confirmar el pago.",
  "billing.loading":          "Cargando...",
  "billing.pastDueTitle": "Tu pago no se completo",
  "billing.pastDueBody": "Tu plan termina en {days} si el pago no se procesa. No se eliminara nada de lo que has creado.",
  "billing.pastDueToday": "Tu plan termina hoy si el pago no se procesa. No se eliminara nada de lo que has creado.",
  "billing.pastDueDays": "{n} dias",
  "billing.pastDueOneDay": "1 dia",
  "billing.updateCard": "Actualizar metodo de pago",
  "billing.cancelPlan": "Cancelar plan",
  "billing.cancelling": "Cancelando...",
  "billing.creditsLabel": "Creditos disponibles",
  "billing.usedOfTotal": "{used} usados de {total}",
  "billing.planPrice": "${price}/mes",
  "billing.planFree": "Gratis",

  // Marketplace page (buyer/discovery storefront)
  "marketplace.eyebrow":            "Marketplace",
  "marketplace.heroTitle":          "Software de produccion, listo para lanzar",
  "marketplace.heroSubtitle":       "Plantillas, plugins, componentes y flujos de IA creados en Nuvra. Instalalos en un solo paso y hazlos tuyos.",
  "marketplace.sell":               "Vende en Nuvra",
  "marketplace.trust.verifiedLabel":  "Creadores verificados",
  "marketplace.trust.verifiedSub":    "Vendedores con identidad verificada",
  "marketplace.trust.secureLabel":    "Pago seguro",
  "marketplace.trust.secureSub":      "Pagos gestionados por la pasarela",
  "marketplace.trust.licensingLabel": "Licencias claras",
  "marketplace.trust.licensingSub":   "Cada articulo indica su licencia",
  "marketplace.prefill.loading":    "Preparando los metadatos del articulo a partir de tu proyecto.",
  "marketplace.prefill.error":      "La precarga de metadatos no esta disponible. Completa los detalles del articulo manualmente.",
  "marketplace.prefill.ready":      "Metadatos del articulo listos",
  "marketplace.prefill.issueOne":   "{n} problema",
  "marketplace.prefill.issueOther": "{n} problemas",
  "marketplace.prefill.readyBadge": "listo",
  "marketplace.prefill.dismiss":    "descartar",
  "marketplace.prefill.titleLabel": "Titulo",
  "marketplace.prefill.shortDesc":  "Descripcion breve",
  "marketplace.prefill.continue":   "Continuar en Creator Studio",
  "marketplace.featured":           "Destacados",
  "marketplace.trending":           "Tendencias",
  "marketplace.trendingTitle":      "Lo que esta ganando impulso",
  "marketplace.trendingNote":       "Clasificado a partir de actividad real del marketplace durante los ultimos 7 dias.",
  "marketplace.mostUsed":           "Mas adoptados",
  "marketplace.mostUsedTitle":      "Usados por compradores reales",
  "marketplace.mostUsedNote":       "Ordenado por adopciones completadas. Sin cifras inventadas.",
  "marketplace.searchPlaceholder":  "Buscar en el marketplace",
  "marketplace.sort.newest":        "Mas recientes",
  "marketplace.sort.popular":       "Mas populares",
  "marketplace.sort.rating":        "Mejor valorados",
  "marketplace.sort.priceAsc":      "Precio: bajo",
  "marketplace.sort.priceDesc":     "Precio: alto",
  "marketplace.filterAll":          "Todos",
  "marketplace.error":              "No se pudo cargar el catalogo. Intentalo de nuevo en un momento.",
  "marketplace.retry":              "Reintentar",
  "marketplace.resultOne":          "{total} resultado",
  "marketplace.resultOther":        "{total} resultados",
  "marketplace.resultsFor":         " para \"{search}\"",
  "marketplace.free":               "Gratis",
  "marketplace.perMonth":           "/mes",
  "marketplace.license.standard":   "Licencia estandar",
  "marketplace.license.extended":   "Licencia ampliada",
  "marketplace.license.enterprise": "Licencia empresarial",
  "marketplace.aria.verified":      "Creador verificado",
  "marketplace.aria.sort":          "Ordenar publicaciones",
  "marketplace.aria.featured":      "Articulo destacado",
  "marketplace.aria.close":         "Cerrar",
  "marketplace.by":                 "por {name}",
  "marketplace.installOne":         "{n} instalacion",
  "marketplace.installOther":       "{n} instalaciones",
  "marketplace.reviews.loading":    "Cargando resenas.",
  "marketplace.reviews.none":       "Aun no hay resenas.",
  "marketplace.reviews.title":      "Resenas",
  "marketplace.toast.added":        "Agregado a tu biblioteca",
  "marketplace.toast.addedBody":    "{title} esta listo para usar.",
  "marketplace.toast.failed":       "No se pudo completar",
  "marketplace.toast.failedBody":   "Intentalo de nuevo.",
  "marketplace.toast.signInBody":   "Inicia sesion e intentalo de nuevo.",
  "marketplace.getFree":            "Obtener gratis",
  "marketplace.buy":                "Comprar {price}",
  "marketplace.empty.noResults":    "Sin resultados para \"{search}\"",
  "marketplace.empty.open":         "El marketplace esta abierto",
  "marketplace.empty.tryDifferent": "Prueba con otro termino de busqueda o borra el filtro.",
  "marketplace.empty.beFirst":      "Se el primero en publicar. Crea un proyecto y publicalo en Creator Studio.",
  "marketplace.empty.clear":        "Borrar busqueda",
  "marketplace.empty.build":        "Crea algo",

  // Mobile Builds page (native packaging dashboard)
  "mobile.eyebrow":              "Compilaciones nativas",
  "mobile.title":                "Compilaciones moviles",
  "mobile.subtitle":             "Genera artefactos nativos de Android e iOS a partir de tu proyecto.",
  "mobile.projectAria":          "Proyecto",
  "mobile.target.aab.label":     "Android App Bundle",
  "mobile.target.aab.desc":      "Paquete firmado para Google Play Store.",
  "mobile.target.apk.label":     "APK de Android",
  "mobile.target.apk.desc":      "APK firmado para instalacion directa y sideloading.",
  "mobile.target.ipa.label":     "Archivo de app iOS",
  "mobile.target.ipa.desc":      "Archivo firmado para enviar a la App Store.",
  "mobile.target.twa.label":     "Trusted Web Activity",
  "mobile.target.twa.desc":      "Tu aplicacion web como paquete de Play Store.",
  "mobile.github.connectedAs":   "Conectado como",
  "mobile.github.connectedTail": ". Las compilaciones se guardan en tu repositorio y se empaquetan en CI.",
  "mobile.github.connect":       "Conecta GitHub para empaquetar compilaciones nativas. El codigo de tu proyecto se sube a tu propio repositorio y el artefacto firmado se compila en CI.",
  "mobile.build":                "Compilar",
  "mobile.buildAgain":           "Compilar de nuevo",
  "mobile.buildStarted":         "Compilacion iniciada",
  "mobile.track":                "Seguir",
  "mobile.err.selectProject":    "Selecciona un proyecto primero.",
  "mobile.err.connectGithub":    "Conecta GitHub para compilar.",
  "mobile.err.buildFirst":       "Compila el proyecto antes de generar artefactos.",
  "mobile.err.couldNotStart":    "No se pudo iniciar la compilacion.",
  "mobile.noProjectsHint":       "Crea un proyecto primero y luego vuelve aqui para generar artefactos nativos.",
  "mobile.historyTitle":         "Historial de compilaciones",
  "mobile.succeeded":            "{n} exitosas",
  "mobile.failed":               "{n} fallidas",
  "mobile.running":              "{n} en curso",
  "mobile.loadingHistory":       "Cargando el historial de compilaciones",
  "mobile.noBuilds":             "Aun no hay compilaciones.",
  "mobile.noBuildsHint":         "Elige un objetivo arriba para generar tu primer artefacto firmado.",
  "mobile.startProject":         "Iniciar un proyecto",
  "mobile.time.dayAgo":          "hace {d}d",
  "mobile.time.hourAgo":         "hace {h}h",
  "mobile.time.minAgo":          "hace {m}m",
  "mobile.time.justNow":         "ahora mismo",
  "mobile.comingSoon":           "Proximamente",
  "mobile.openProject": "Abrir proyecto",
  "mobile.projectContext": "Compilar desde el proyecto",
  "mobile.noProjectSelected": "Selecciona un proyecto para continuar",
  "mobile.stat.builds": "Compilaciones",
  "mobile.stat.successRate": "Tasa de exito",
  "mobile.stat.lastBuild": "Ultima compilacion",
  "mobile.readinessTitle": "Preparacion de compilacion",
  "mobile.readinessSubtitle": "Nuvra validara tu proyecto y cuenta antes de admitir una compilacion nativa. No se muestra ningun estado hasta que el backend real lo registre.",
  "mobile.github.ready": "GitHub conectado",
  "mobile.github.required": "Se requiere conexion con GitHub",
  "mobile.projectSelected": "Proyecto seleccionado",
  "mobile.buildCenterEyebrow": "Entrega nativa",
  "mobile.buildCenterTitle": "Elige un destino de compilacion",
  "mobile.buildCenterSubtitle": "Crea el artefacto nativo que necesita tu proyecto. Cada accion usa la ruta existente de compilacion y facturacion gobernadas.",
  "mobile.filter.all": "Todos",
  "mobile.filter.android": "Android",
  "mobile.filter.ios": "iOS",
  "mobile.refresh": "Actualizar historial de compilaciones",
  "mobile.recentBuildsTitle": "Compilaciones recientes",
  "mobile.artifactReady": "Artefacto registrado",
  "mobile.intelligenceTitle": "Inteligencia y gobierno de compilacion",
  "mobile.intelligenceSubtitle": "Observabilidad real, historial de versiones, procedencia de firma y gobierno de compilacion para este proyecto.",

  // Platform Intelligence page (systems visibility)
  "platform.title":                    "Inteligencia de plataforma",
  "platform.subtitle":                 "Visibilidad en vivo de los sistemas de orquestacion, lanzamiento, compilacion y auditoria",
  "platform.live":                     "En vivo",
  "platform.eyebrow":                  "Consola de operador",
  "platform.adminOnly":                "Visibilidad de operador. Esta consola no forma parte del producto para clientes.",
  "platform.verifying":                "Verificando acceso...",
  "platform.loading":                  "Cargando...",
  "platform.stat.projects":            "Proyectos",
  "platform.stat.releases":            "Lanzamientos",
  "platform.stat.builds":              "Compilaciones moviles",
  "platform.stat.audit":               "Entradas de auditoria",
  "platform.releaseEvents.title":      "Eventos del pipeline de lanzamiento",
  "platform.releaseEvents.empty":      "Aun no se han registrado eventos de lanzamiento.",
  "platform.releaseEvents.emptyHint":  "Los eventos aparecen aqui cuando se activan los lanzamientos.",
  "platform.audit.title":              "Registro de auditoria de lanzamientos",
  "platform.audit.empty":              "Aun no hay entradas de auditoria.",
  "platform.audit.emptyHint":          "Cada decision de lanzamiento, uso de credenciales y envio se registra aqui.",
  "platform.mobile.title":             "Cola de compilaciones moviles",
  "platform.mobile.empty":             "Aun no se han registrado compilaciones moviles.",
  "platform.mobile.emptyHint":         "Las compilaciones AAB, APK e IPA aparecen aqui cuando se activan desde Deploy.",
  "platform.infra.title":              "Sistemas de infraestructura",
  "platform.infra.active":             "activo",

  // What's New page (actualizaciones del producto para clientes)
  "whatsNew.eyebrow":         "Novedades del producto",
  "whatsNew.title":           "Novedades",
  "whatsNew.subtitle":        "Descubre lo que hemos lanzado, mejorado y hecho mas seguro en Nuvra.",
  "whatsNew.shippedOnly":     "Solo actualizaciones publicadas",
  "whatsNew.latest":          "Lo ultimo",
  "whatsNew.seeIncluded":     "Ver lo incluido",
  "whatsNew.hideDetails":     "Ocultar detalles",
  "whatsNew.customerValue":   "Describimos los cambios por el resultado que obtienes, no por la implementacion interna de Nuvra.",
  "whatsNew.search":          "Buscar actualizaciones…",
  "whatsNew.filter":          "Filtrar",
  "whatsNew.all":             "Todas",
  "whatsNew.releases":        "Nuevos lanzamientos",
  "whatsNew.improvements":    "Mejoras",
  "whatsNew.security":        "Seguridad",
  "whatsNew.history":         "Historial del producto",
  "whatsNew.earlier":         "Actualizaciones anteriores",
  "whatsNew.matching":        "Actualizaciones coincidentes",
  "whatsNew.update":          "actualizacion",
  "whatsNew.updates":         "actualizaciones",
  "whatsNew.noMatches":       "Ninguna actualizacion coincide con estos filtros.",
  "whatsNew.clearFilters":    "Borrar filtros",
  "whatsNew.helpPitch":       "Si necesitas ayuda con una funcion, abre el Centro de ayuda o contacta con Soporte de Nuvra.",
  "whatsNew.helpCenter":      "Centro de ayuda",
  "whatsNew.support":         "Soporte",

  "whatsNew.customerOutcome": "Creado para tu flujo de trabajo",
  "whatsNew.filterUpdates":  "Filtrar novedades del producto",

  // Command Center (dashboard creation surface)
  "cc.prompt":      "Que construimos?",
  "cc.eyebrow":     "Nuevo proyecto",
  "cc.chipsLabel":  "Empezar con una dirección",
  "cc.flow.describe": "Describir",
  "cc.flow.plan":    "Planificar",
  "cc.flow.build":   "Construir",
  "cc.flow.ship":    "Publicar",
  "cc.promptNamed": "Que construimos, {name}?",
  "cc.subtitle":    "Describe el producto. Nuvra construye el sistema.",
  "cc.degraded":    "Algunos servicios degradados",
  "cc.outage":      "Interrupcion del servicio en curso",
  "cc.viewStatus":  "Ver estado →",
  "cc.recentProjects": "Proyectos recientes",
  "cc.recentProjectsHint": "Continua donde lo dejaste.",
  "cc.viewAllProjects": "Ver todos",
  "cc.recentProjectsLoading": "Cargando proyectos recientes",
  "cc.projectsLoadErrorTitle": "No se pudieron cargar tus proyectos",
  "cc.projectsLoadErrorBody": "Tus proyectos están a salvo. Comprueba tu conexión e inténtalo de nuevo.",
  "cc.retryProjects": "Intentar de nuevo",

  // Connectors page (integration catalog)
  "connectors.page.title":               "Integraciones",
  "connectors.page.subtitle":            "Conecta los servicios que tu proyecto usa de verdad. Las credenciales quedan protegidas en la bóveda cifrada de conectores de Nuvra y cada conexión se puede probar antes de usarla.",
  "connectors.page.project":             "Proyecto",
  "connectors.page.selectProject":       "Seleccionar proyecto",
  "connectors.page.noProject":           "Ningún proyecto seleccionado",
  "connectors.page.statConnected":       "Conectados",
  "connectors.page.statHealthy":         "Correctos",
  "connectors.page.statAttention":       "Requieren atención",
  "connectors.page.search":              "Buscar integraciones, capacidades o proveedores",
  "connectors.page.tabAll":              "Todas",
  "connectors.page.tabConnected":        "Conectadas",
  "connectors.page.tabAttention":        "Requieren atención",
  "connectors.page.clearFilters":        "Borrar los filtros",
  "connectors.page.pickProjectTitle":    "Selecciona un proyecto para conectar servicios",
  "connectors.page.pickProjectBody":     "Puedes explorar el catálogo de integraciones ahora. Los cambios de conexión siempre se limitan a un proyecto real de Nuvra.",
  "connectors.page.suggestedTitle":      "Sugeridas para este proyecto",
  "connectors.page.suggestedBody":       "Derivadas de las capacidades que ya existen en el esquema Nuvra del proyecto.",
  "connectors.page.notConnectedYet":     "Aún sin conectar",
  "connectors.page.noMatchTitle":        "Ninguna integración coincide",
  "connectors.page.noMatchBody":         "Prueba con otro proveedor, otra capacidad u otra categoría. Nuvra nunca inventa un conector solo para llenar este catálogo.",
  "connectors.page.securityTitle":       "Seguridad de las conexiones",
  "connectors.page.securityBody":        "Nuvra guarda las credenciales de los conectores en su bóveda protegida, mantiene los secretos en el servidor, limita la gestión de conectores al proyecto propietario y ofrece una prueba de conectividad real antes de usar un servicio.",
  "connectors.page.suggestedBadge":      "Sugerido",

  // Collaboration page (live session environment)
  "collab.title":              "Colaboracion",
  "collab.conn.connected":     "Conectado",
  "collab.conn.reconnecting":  "Reconectando",
  "collab.conn.disconnected":  "Desconectado",
  "collab.noProjectTitle":     "Ningun proyecto seleccionado",
  "collab.noProjectBody":      "Abre un proyecto y ve a Colaboracion para ver el estado de la sesion en vivo.",
  "collab.urlParam":           "Parametro de URL:",
  "collab.security":           "Observabilidad de seguridad",
  "collab.active":             "activo",
  "collab.participants.aria":  "Participantes de la sesion",
  "collab.participants":       "Participantes",
  "collab.activity.aria":      "Flujo de actividad",
  "collab.activity":           "Actividad en vivo",
  "collab.ai.aria":            "Orquestacion de IA",
  "collab.ai":                 "Ejecucion de IA",

  // Help Center page (support + documentation)
  "help.eyebrow":          "Soporte",
  "help.title":            "Centro de ayuda",
  "help.subtitle":         "Guias para construir, desplegar y operar tus productos en Nuvra.",
  "help.searchPlaceholder":"Buscar articulos de ayuda...",
  "help.noResults":        "Ningun articulo coincide con \"{query}\". Prueba con otro termino o escribenos a",
  "help.stillNeedHelp":    "Aun necesitas ayuda?",
  "help.emailPrefix":      "Escribe a nuestro equipo a",
  "help.emailSuffix":      ". Leemos todos los mensajes.",
  "help.systemStatus":     "Estado del sistema",
  "help.whatsNew":         "Novedades",
  "help.startHere":         "Empieza aquí",
  "help.documentation":     "Documentación",
  "help.guides":            "Guías",
  "help.searchResults":     "Resultados de búsqueda",
  "help.clearSearch":       "Borrar búsqueda",
  "help.resources":         "Recursos",
  "help.searchHint":        "Buscar",
  "help.gettingStarted.title": "Primeros pasos",
  "help.gettingStarted.q1": "Como funciona Nuvra",
  "help.gettingStarted.a1": "Describe el producto que quieres construir. Nuvra crea un proyecto y desarrolla una aplicacion real y funcional con modelos de datos, paginas, autenticacion y almacenamiento persistente. Cada nueva solicitud de compilacion refina ese mismo producto.",
  "help.gettingStarted.q2": "Los proyectos son el objeto principal",
  "help.gettingStarted.a2": "Cada producto que construyes es un proyecto con su propio espacio de trabajo: Resumen, Requisitos, Arquitectura, Solicitudes de compilacion, Compilaciones, Archivos, Entornos e Integraciones. Enviar un nuevo prompt desde el panel inicia un proyecto nuevo e independiente.",
  "help.building.title": "Construccion y proyectos",
  "help.building.q1": "Enviar una solicitud de compilacion",
  "help.building.a1": "Abre un proyecto y envia una solicitud de compilacion que describa el cambio, la funcion, la correccion o la actualizacion de arquitectura que quieres. Nuvra realiza el trabajo y lo registra como un registro de compilacion que muestra el analisis, el trabajo realizado y los archivos y resultados que produjo.",
  "help.building.q2": "Revisar lo que se construyo",
  "help.building.a2": "Las secciones Resumen, Arquitectura y Compilaciones muestran las paginas, los modelos de datos, las rutas y el historial completo de compilaciones de tu proyecto, generados a partir de tu aplicacion real, no de muestras.",
  "help.deploying.title": "Despliegue y entornos",
  "help.deploying.q1": "Staging y Produccion",
  "help.deploying.a1": "Cada proyecto tiene entornos de Staging y Produccion. Las nuevas compilaciones llegan a Staging automaticamente; promueves a Produccion cuando estas listo y puedes revertir a cualquier version verificada anterior.",
  "help.deploying.q2": "Publicar en las tiendas de aplicaciones",
  "help.deploying.a2": "Los proyectos moviles se pueden compilar de forma nativa para Android e iOS y publicar en Google Play y la App Store de Apple, con despliegues escalonados que tu controlas y manejo cifrado de tus credenciales de firma.",
  "help.integrations.title": "Integraciones",
  "help.integrations.q1": "Conectar servicios a un proyecto",
  "help.integrations.a1": "Conecta integraciones como GitHub y tu base de datos desde el espacio de trabajo de un proyecto. Las integraciones estan limitadas a ese unico proyecto, asi que cada producto gestiona sus propias conexiones.",
  "help.billing.title": "Facturacion y creditos",
  "help.billing.q1": "Como funcionan los creditos",
  "help.billing.a1": "Construir consume creditos. Tu saldo actual se muestra en el encabezado del espacio de trabajo y en la pagina de Facturacion. Cuando te quedes con poco, recarga o mejora tu plan para seguir construyendo.",
  "help.billing.q2": "Planes y recargas",
  "help.billing.a2": "Elige un plan que se ajuste a tu uso o compra una recarga unica. Los creditos se aplican en cuanto se confirma tu pago.",
  "help.account.title": "Cuenta y seguridad",
  "help.account.q1": "Autenticacion de dos factores",
  "help.account.a1": "Activa la autenticacion de dos factores con aplicacion de autenticacion (TOTP) en Ajustes → Seguridad para requerir un segundo paso al iniciar sesion.",
  "help.account.q2": "Claves de API",
  "help.account.a2": "Crea claves de API de solo lectura y con alcance limitado en Ajustes → Claves de API para leer tus proyectos, compilaciones y despliegues mediante programacion. El secreto se muestra una sola vez al crearlo.",
  "help.account.q3": "Exportar tus datos",
  "help.account.a3": "Descarga una copia JSON de los datos de tu cuenta en cualquier momento en Ajustes → Centro de exportacion.",
  "help.account.q4": "Registros de auditoria",
  "help.account.a4": "Revisa un registro inmutable de las acciones realizadas en tu cuenta y proyectos en Ajustes → Registros de auditoria.",

  // Magic-link sign-in landing page
  "magicLink.missingToken": "A este enlace de inicio de sesion le falta el token. Solicita uno nuevo.",
  "magicLink.invalid":      "Este enlace de inicio de sesion no es valido o ha caducado.",
  "magicLink.signingIn":    "Iniciando tu sesion...",
  "magicLink.backToSignIn": "Volver a iniciar sesion",

  // Share-link join page
  "join.missingToken":   "A este enlace para compartir le falta el token.",
  "join.accessGranted":  "Ahora tienes acceso de {role}.",
  "join.invalid":        "Este enlace no es valido o ha caducado.",
  "join.joining":        "Uniendote al proyecto...",
  "join.goToDashboard":  "Ir al panel",

  // Creator Studio (seller operating system)
  "cs.loading":            "Cargando Creator Studio.",
  "cs.loadError":          "No se pudo cargar Creator Studio. Intentalo de nuevo en un momento.",
  "cs.retry":              "Reintentar",
  "cs.eyebrow":            "Creator Studio",
  "cs.sellBlocked":        "La venta no esta disponible en tu cuenta en este momento.",
  "cs.planAndAbove":       "Plan {tier} y superiores",
  "cs.upsellTitle":        "Convierte lo que construyes en ingresos.",
  "cs.upsellBody":         "Publica en el Marketplace de Nuvra, conserva el 80% de cada venta y recibe pagos en un calendario fijo y automatico.",
  "cs.becomeCreator":      "Convertirte en creador",
  "cs.noFees":             "Sin cuotas iniciales. Sin cuotas de publicacion.",
  "cs.keep80Title":        "Conserva el 80% de cada venta",
  "cs.keep80Sub":          "Tu parte de cada transaccion",
  "cs.keep80Body":         "Conservas la mayor parte de cada venta. Sin costos iniciales y sin cuotas de publicacion, nunca.",
  "cs.paidTitle":          "Pagos en un calendario fijo",
  "cs.paidSub":            "Automatico, dos veces al mes",
  "cs.scheduleWithPolicy": "Se emite automaticamente el {dates}. Pago minimo {amount}.",
  "cs.scheduleDefault":    "Se emite automaticamente dos veces al mes.",
  "cs.earnings":           "Ingresos",
  "cs.payouts":            "Pagos",
  "cs.yourListings":       "Tus articulos",
  "cs.affiliate":          "Afiliados",
  "cs.analytics":          "Analiticas",
  "cs.verification":       "Verificacion",
  "cs.noEarningsYet":      "Aun no hay ingresos",
  "cs.next":               "Proximo {date}",
  "cs.automaticSchedule":  "Calendario automatico",
  "cs.nPublished":         "{n} publicados",
  "cs.nothingPublished":   "Aun no hay nada publicado",
  "cs.active":             "Activo",
  "cs.notEnrolled":        "No inscrito",
  "cs.available":          "Disponible",
  "cs.noAnalytics":        "Aun no hay analiticas",
  "cs.verified":           "Verificado",
  "cs.inReview":           "En revision",
  "cs.notVerified":        "No verificado",
  "cs.insideEyebrow":      "Dentro de Creator Studio",
  "cs.whatYouUnlock":      "Lo que desbloqueas con Pro",
  "cs.locked":             "Bloqueado",
  "cs.nProjectsOne":       "{n} proyecto en tu espacio de trabajo",
  "cs.nProjectsOther":     "{n} proyectos en tu espacio de trabajo",
  "cs.becomeToPublish":    "Conviertete en creador para publicar tu trabajo en el marketplace.",
  "cs.unlockPublishing":   "Desbloquear publicacion",
  "cs.openOnUpgrade":      "Se abren en cuanto mejoras tu plan. Hasta entonces, nada aqui esta activo.",
  "cs.controlPlaneTitle":  "Tu centro de control de creador",
  "cs.controlPlaneSub":    "Crea, publica, crece y gana, todo en un solo lugar.",
  "cs.newProject":         "Nuevo proyecto",
  "cs.seeYourWork":        "Mira tu trabajo en el marketplace",
  "cs.browseCatalog":      "Explora el catalogo publico como lo hacen los compradores.",
  "cs.openMarketplace":    "Abrir Marketplace",
  "cs.yourPath":           "Tu camino",
  "cs.journeyTitle":       "Crea, publica, crece, gana",
  "cs.step.create":        "Crear",
  "cs.step.publish":       "Publicar",
  "cs.step.verify":        "Verificar",
  "cs.step.earn":          "Ganar",
  "cs.step.createDesc":    "Construye un proyecto en tu espacio de trabajo.",
  "cs.step.publishDesc":   "Publica una compilacion en el marketplace.",
  "cs.step.verifyDesc":    "Verifica para habilitar los pagos.",
  "cs.step.earnDesc":      "Los ingresos llegan despues de tu primera venta.",
  "cs.toast.published":      "Publicado",
  "cs.toast.publishedBody":  "Tu articulo ya esta activo.",
  "cs.toast.publishFail":    "No se pudo publicar",
  "cs.toast.publishFailBody":"Revisa los detalles del articulo e intentalo de nuevo.",
  "cs.toast.removed":        "Eliminado",
  "cs.toast.removedBody":    "El articulo ha sido eliminado.",
  "cs.toast.removeFail":     "No se pudo eliminar",
  "cs.toast.tryAgain":       "Intentalo de nuevo.",
  "cs.listings":           "Articulos",
  "cs.publishFirstBody":   "Publica tu primera creacion para empezar a vender en el marketplace.",
  "cs.publishFirst":       "Publica tu primera creacion",
  "cs.nSold":              "{n} vendidos",
  "cs.publish":            "Publicar",
  "cs.remove":             "Eliminar",
  "cs.totalInstalls":      "Instalaciones totales",
  "cs.totalRevenue":       "Ingresos totales",
  "cs.totalViews":         "Vistas totales",
  "cs.avgRating":          "Valoracion media",
  "cs.noRatings":          "Aun no hay valoraciones",
  "cs.earningsAppear":     "Los ingresos apareceran aqui despues de tu primera venta.",
  "cs.totalEarned":        "Total ganado",
  "cs.pending":            "Pendiente",
  "cs.paidOut":            "Pagado",
  "cs.nextPayout":         "Proximo pago",
  "cs.onSchedule":         "En calendario",
  "cs.minimumPayout":      "Pago minimo",
  "cs.issuedOn":           "Se emite automaticamente el {dates}",
  "cs.issuedDefault":      "Se emite automaticamente el 15 y el ultimo dia de cada mes",
  "cs.history":            "Historial",
  "cs.loadingDot":         "Cargando.",
  "cs.payoutHistoryEmpty": "El historial de pagos aparecera aqui.",
  "cs.scheduled":          "Programado",
  "cs.toast.refRequired":    "Referencia requerida",
  "cs.toast.refRequiredBody":"Agrega un id de sesion del proveedor o un enlace que controles.",
  "cs.toast.submitted":      "Enviado para revision",
  "cs.toast.submittedBody":  "Un especialista lo confirmara.",
  "cs.toast.submitFail":     "No se pudo enviar",
  "cs.verifiedCreator":    "Creador verificado",
  "cs.payoutsEnabled":     "Los pagos estan habilitados en tu cuenta.",
  "cs.verifInReview":      "Verificacion en revision",
  "cs.confirmShortly":     "Lo confirmaremos en breve.",
  "cs.verifyToReceive":    "Verifica para recibir pagos",
  "cs.completeVerif":      "Completa la verificacion para habilitar los pagos a tu cuenta.",
  "cs.refPlaceholder":     "Id de sesion del proveedor o un enlace que controles",
  "cs.aria.kind":          "Tipo de procedencia",
  "cs.aria.project":       "Selecciona un proyecto",
  "cs.submit":             "Enviar",
  "cs.toast.affJoined":      "Te uniste al programa de afiliados",
  "cs.toast.joinFail":       "No se pudo unir",
  "cs.toast.genFail":        "No se pudo generar el enlace",
  "cs.toast.linkCopied":     "Enlace copiado",
  "cs.affEarnTitle":       "Gana refiriendo ventas del marketplace",
  "cs.affEarnBody":        "Unete al programa de afiliados para generar enlaces de referencia con seguimiento.",
  "cs.joinProgram":        "Unirse al programa",
  "cs.clicks":             "Clics",
  "cs.conversions":        "Conversiones",
  "cs.approved":           "Aprobado",
  "cs.genLink":            "Generar enlace de referencia",
  "cs.copyLink":           "Copiar enlace",
  "cs.commissions":        "Comisiones",
  "cs.publishingReadiness":"Preparacion para publicar",

  // Project workspace - build stage labels (the real backend stages over SSE)
  "pw.stage.queued":        "Producto",
  "pw.stage.blueprint":     "Arquitectura",
  "pw.stage.schema":        "Datos",
  "pw.stage.generate":      "Ingenieria",
  "pw.stage.security_scan": "Seguridad",
  "pw.stage.write_files":   "Ingenieria",
  "pw.stage.validate":      "QA",
  "pw.stage.deploy_pages":  "Lanzamiento",
  "pw.stage.complete":      "En vivo",
  "pw.stage.intent":        "Reflexionando",
  "pw.stage.constraints":   "Verificando",
  "pw.stage.agents":        "Disenando",
  "pw.stage.planning":      "Planificando",
  "pw.stage.validation":    "Comprobando",
  "pw.stage.explanation":   "Razonando",
  "pw.stage.enriching":     "Sintetizando",

  // Project workspace - navigation
  "pw.nav.conversation": "Construccion",
  "pw.nav.overview":     "Resumen",
  "pw.nav.requirements": "Requisitos",
  "pw.nav.architecture": "Arquitectura",
  "pw.nav.builds":       "Construcciones",
  "pw.nav.code":         "Archivos",
  "pw.nav.analytics":                  "Analiticas",
  "pw.analytics.window.today":         "Hoy",
  "pw.analytics.window.7d":            "7 dias",
  "pw.analytics.window.30d":           "30 dias",
  "pw.analytics.window.all":           "Todo el tiempo",
  "pw.analytics.visitors":             "Visitantes",
  "pw.analytics.pageViews":            "Vistas de pagina",
  "pw.analytics.deployments":          "Despliegues",
  "pw.analytics.builds":               "Builds completados",
  "pw.analytics.successRate":          "Exito de builds",
  "pw.analytics.previous":             "era {n}",
  "pw.analytics.trend.new":            "nuevo",
  "pw.analytics.unavailable":          "No se pudo leer",
  "pw.analytics.noBuildsYet":          "Ningun build terminado",
  "pw.analytics.none":                 "Sin actividad",
  "pw.analytics.noneHint":             "Cuando tu app este desplegada y la visiten, las visitas apareceran aqui.",
  "pw.analytics.failed":               "No se pudieron cargar las analiticas",
  "pw.analytics.failedHint":           "Es un problema de visualizacion, no de tu app. Intentalo en un momento.",
  "pw.analytics.degraded":             "Algunas cifras no se pudieron leer, esta vista esta incompleta. Los numeros mostrados son reales; los que faltan estan marcados.",
  "pw.analytics.computedAt":           "Medido a las {time}",
  "pw.nav.environments": "Entornos",
  "pw.nav.memory":       "Memoria",
  "pw.nav.agent":        "Agente",
  "pw.nav.deployments":  "Despliegues",
  "pw.settings.title": "Ajustes del proyecto",
  "pw.settings.subtitle": "Administra la identidad y visibilidad de este proyecto sin salir de su espacio de trabajo.",
  "pw.settings.nameLabel": "Nombre del proyecto",
  "pw.settings.nameHint": "Este nombre aparece en el espacio de trabajo y en la lista de proyectos.",
  "pw.settings.publicLabel": "Hacer público el proyecto",
  "pw.settings.publicHint": "Los proyectos públicos pueden abrirse mediante su URL de ejecución compartida. Los proyectos privados siguen bajo control de su propietario.",
  "pw.settings.identityTitle": "Identidad del proyecto",
  "pw.settings.identityBody": "ID canónico del proyecto: {id}",
  "pw.settings.save": "Guardar cambios",
  "pw.settings.saving": "Guardando…",
  "pw.settings.saved": "Ajustes del proyecto guardados",
  "pw.settings.savedBody": "La identidad y visibilidad del proyecto están actualizadas.",
  "pw.settings.saveFailed": "No se pudieron guardar los ajustes del proyecto",
  "pw.settings.saveFailedBody": "No se guardaron los cambios. Inténtalo de nuevo.",
  "pw.nav.integrations": "Integraciones",
  "pw.nav.settings":     "Ajustes",
  "pw.nav.group.product":  "Tu producto",
  "pw.nav.group.changes":  "Cambios",
  "pw.nav.group.ship":     "Publicar",
  "pw.mode.stream":            "Actividad",
  "pw.mode.preview":           "Vista previa",
  "pw.mode.files":             "Archivos",
  "pw.nav.preview":        "Vista previa",
  "pw.nav.previewSoon":    "tras la primera compilacion",
  "pw.nav.previewAfterBuild": "Disponible despues de tu primera compilacion",
  "pw.nav.allProjects":  "Todos los proyectos",
  "pw.nav.buildsOne":    "{n} construccion",
  "pw.nav.buildsOther":  "{n} construcciones",
  "pw.nav.originalIntent": "Tu encargo",
  "pw.plan.button":      "Planificar",
  "pw.plan.buttonTitle": "Ve que construira este prompt y su costo, gratis, antes de compilar",
  "pw.plan.cost":        "La compilacion usa {n} creditos",
  "pw.plan.buildThis":   "Construir esto",
  "pw.plan.dismiss":     "Descartar plan",
  "pw.plan.failed":      "No se pudo derivar el plan. Intentalo de nuevo.",
  "pw.plan.entities":    "Datos",
  "pw.plan.workflows":   "Flujos",
  "pw.plan.security.standard": "Aislamiento de datos por usuario con seguridad a nivel de fila",
  "pw.plan.security.elevated": "Seguridad a nivel de fila, auditoria y roles",
  "pw.plan.security.critical": "Aislamiento estricto, auditoria completa y roles",
  "pw.defaultName":      "Nuevo proyecto",
  "pw.projectLoading": "Abriendo tu proyecto",
  "pw.projectLoadingBody": "Cargando el proyecto y restaurando su contexto de trabajo.",
  "pw.projectNotFoundTitle": "Proyecto no encontrado",
  "pw.projectNotFoundBody": "Es posible que el proyecto se haya movido, eliminado o que no tengas acceso.",
  "pw.projectLoadErrorTitle": "No se pudo cargar el proyecto",
  "pw.projectLoadErrorBody": "Tu proyecto no se modificó. Comprueba tu conexión e inténtalo de nuevo.",
  "pw.backToProjects": "Volver a proyectos",
  "pw.retryProject": "Intentar de nuevo",

  // Project workspace - files panel
  "pw.files.noBuild":      "Sin construccion aun",
  "pw.files.noBuildHint":  "Los archivos apareceran aqui cuando se complete la primera construccion.",
  "pw.files.pagesN":       "Paginas ({n})",
  "pw.files.componentsN":  "Componentes ({n})",
  "pw.files.dataModelsN":  "Modelos de datos ({n})",
  "pw.files.moreComponents": "+{n} componentes mas",

  // Project workspace - overview panel
  "pw.overview.statusBuilding": "Construyendo",
  "pw.overview.statusFailed":   "Ultima construccion fallida",
  "pw.overview.statusBuilt":    "Construido",
  "pw.overview.statusDeployed": "Desplegado",
  "pw.overview.statusNotBuilt": "Sin construir aun",
  "pw.overview.stat.pages":      "Paginas",
  "pw.overview.stat.components": "Componentes",
  "pw.overview.stat.dataModels": "Modelos de datos",
  "pw.overview.stat.builds":     "Construcciones",
  "pw.overview.openLiveApp":     "Abrir aplicacion en vivo",
  "pw.overview.originalRequirement": "Requisito original",

  // Project workspace - architecture panel
  "pw.arch.none":          "Sin arquitectura aun",
  "pw.arch.noneHint":      "Aparece cuando se completa la primera construccion.",
  "pw.arch.whyBuilt":      "Por que Nuvra construyo esto",
  "pw.arch.fieldsN":       "({n} campos)",
  "pw.arch.assumptions":   "Suposiciones de Nuvra",
  "pw.arch.youCanChange":  "Puedes cambiar: {areas}.",
  "pw.arch.whatChanged":   "Que cambio en esta construccion",
  "pw.arch.routesN":       "Rutas ({n})",
  "pw.arch.dataLayerN":    "Capa de datos ({n} modelos)",
  "pw.arch.capabilitiesN": "Capacidades ({n})",
  "pw.arch.componentsGeneratedOne":   "{n} componente generado.",
  "pw.arch.componentsGeneratedOther": "{n} componentes generados.",
  "pw.arch.showComponentsOnPage": "Mostrar componentes de esta pagina",
  "pw.arch.noComponentsOnPage":   "Sin componentes en esta pagina",
  "pw.arch.toggleComponents":     "Alternar componentes",
  "pw.arch.renamed":       "renombrada",
  "pw.arch.hiddenNextBuild": "oculta en la proxima construccion",
  "pw.arch.hidden":        "oculto",
  "pw.arch.saveName":      "Guardar nombre",
  "pw.arch.savePageName":  "Guardar nombre de pagina",
  "pw.arch.renameThisPage": "Renombrar esta pagina",
  "pw.arch.renamePage":    "Renombrar pagina",
  "pw.arch.hiddenRestore": "Oculta: se elimina de la aplicacion en la proxima construccion. Haz clic para restaurar.",
  "pw.arch.hidePage":      "Ocultar esta pagina para que la proxima construccion la elimine de la aplicacion",
  "pw.arch.showPage":      "Mostrar pagina",
  "pw.arch.hidePageLabel": "Ocultar pagina",
  "pw.arch.lockedKeep":    "Bloqueada: las proximas construcciones mantienen esta pagina tal cual",
  "pw.arch.lockPage":      "Bloquear esta pagina para que las proximas construcciones no la cambien",
  "pw.arch.unlockPage":    "Desbloquear pagina",
  "pw.arch.lockPageLabel": "Bloquear pagina",
  "pw.arch.compHiddenRestore": "Oculto: se elimina en la proxima construccion. Haz clic para restaurar.",
  "pw.arch.hideComponent": "Ocultar este componente de la aplicacion",
  "pw.arch.showComponent": "Mostrar componente",
  "pw.arch.hideComponentLabel": "Ocultar componente",

  // Project workspace - requirements panel
  "pw.req.originalRequirement": "Requisito original",
  "pw.req.noOriginal":     "No se registro ningun requisito original para este proyecto.",
  "pw.req.engineeringN":   "Solicitudes de ingenieria ({n})",
  "pw.req.noRequests":     "Sin solicitudes de cambio aun. Envia una en Solicitudes de construccion.",
  "pw.req.showingRecent":  "Mostrando las {shown} mas recientes",
  "pw.req.constraintsN":   "Restricciones ({n})",
  "pw.req.decisionsN":     "Decisiones ({n})",

  // Project workspace - environments panel
  "pw.env.staging":        "Preproduccion",
  "pw.env.production":     "Produccion",
  "pw.env.deployed":       "Desplegado",
  "pw.env.notDeployed":    "Sin desplegar",
  "pw.env.noUrl":          "Sin URL",
  "pw.env.none":           "Sin entornos aun",
  "pw.env.noneHint":       "Los entornos aparecen cuando se despliega tu primera construccion.",
  "pw.env.build":          "Construccion:",
  "pw.env.lastDeployment": "Ultimo despliegue:",
  "pw.env.envVars":        "Variables de entorno:",
  "pw.env.buildNone":      "ninguna",
  "pw.env.never":          "nunca",

  // Project workspace - activity panel
  "pw.activity.none":      "Sin actividad aun",
  "pw.activity.noneHint":  "El historial de construcciones aparecera aqui.",
  "pw.activity.buildFailed": "Construccion fallida",

  // Project workspace - memory panel
  "pw.mem.brain.architecture": "Arquitectura",
  "pw.mem.brain.decision":     "Decisiones",
  "pw.mem.brain.constraint":   "Restricciones",
  "pw.mem.brain.naming":       "Nomenclatura",
  "pw.mem.brain.rationale":    "Justificacion",
  "pw.mem.brain.history":      "Historial",
  "pw.mem.title":          "Memoria del proyecto",
  "pw.mem.subtitle":       "Todo lo que Nuvra sabe sobre este proyecto, acumulado en todas las construcciones.",
  "pw.mem.originalIntent": "Intencion original",
  "pw.mem.noPrompt":       "Sin prompt registrado",
  "pw.mem.builds":         "Construcciones",
  "pw.mem.pages":          "Paginas",
  "pw.mem.applicationRoutes": "Rutas de la aplicacion",
  "pw.mem.detectedCapabilities": "Capacidades detectadas",
  "pw.mem.emptyBrain":     "A medida que construyes, Nuvra recuerda aqui las decisiones, la arquitectura, la nomenclatura y las restricciones de este producto, y las aporta a cada construccion futura.",

  // Project workspace - header and status
  "pw.header.clickToRename": "Haz clic para renombrar",
  "pw.header.projectName": "Nombre del proyecto",
  "pw.identity.edit":               "Editar identidad del producto",
  "pw.identity.title":              "Identidad del producto",
  "pw.identity.productName":        "Nombre del producto",
  "pw.identity.saveName":           "Guardar",
  "pw.identity.regenerateLogo":     "Nuevo logo",
  "pw.identity.confirmedNote":      "Elegiste este nombre. Nuvra nunca lo sobrescribira.",
  "pw.identity.generatedNote":      "Generado desde tu producto. Editalo y sera permanente.",
  "pw.identity.saveFailed":         "No se pudo guardar la identidad del producto. Intentalo de nuevo.",
  "pw.status.building":    "Construyendo...",
  "pw.status.buildFailed": "Construccion fallida",
  "pw.status.built":       "Construido",
  "pw.status.latestFailed": "La ultima construccion fallo",
  "pw.status.draft":       "Borrador",
  "pw.status.ready":       "Listo",
  "pw.projectMenu.open": "Abrir el menu del proyecto",
  "pw.projectMenu.project": "Espacio de trabajo del proyecto",
  "pw.projectMenu.overview": "Resumen",
  "pw.projectMenu.tools": "Herramientas del proyecto",
  "pw.projectMenu.rename": "Renombrar proyecto",
  "pw.projectMenu.allProjects": "Todos los proyectos",
  "pw.projectMenu.accountSettings": "Configuracion de la cuenta",
  "pw.header.openLiveApp": "Abrir aplicacion en vivo",
  "pw.header.manage":      "Gestionar",
  "pw.header.manageTitle": "Gestionar este producto",
  "pw.header.codeTitle":   "Explora y descarga el codigo de tu proyecto",
  "pw.header.projectCode": "Codigo del proyecto",
  "pw.header.code":        "Codigo",
  "pw.header.manageLess":  "Menos",
  "pw.header.forkTitle":   "Bifurcar este producto en un nuevo proyecto",
  "pw.header.fork":        "Bifurcar",
  "pw.header.notesTitle":  "Notas de ingenieria",
  "pw.header.notes":       "Notas",
  "pw.header.notesPreset.explain":  "Explica este proyecto",
  "pw.header.notesPreset.next":     "Que deberia construir despues?",
  "pw.header.notesPreset.fail":     "Por que fallo mi ultima construccion?",
  "pw.header.notesPreset.analyze":  "Analiza mi arquitectura",
  "pw.header.notesPreset.missing":  "Que funciones me faltan?",
  "pw.header.askPlaceholder": "Pregunta sobre la arquitectura, las construcciones o el estado de este proyecto...",
  "pw.header.send":        "Enviar",
  "pw.header.goLive":      "Publicar",
  "pw.header.clearNotes":  "Borrar notas de ingenieria",

  // Project workspace - empty state and composer
  "pw.empty.title":        "¿Que construimos ahora?",
  "pw.empty.body":         "Describe una funcion, una correccion o un cambio de diseno. Nuvra lo integra en tu aplicacion.",
  "pw.mobile.readyTap":    "Tu producto esta listo, toca para abrir",
  "pw.mobile.previousTap": "La ultima construccion fallo. Abre la ultima version funcional",
  "pw.mobile.tapToOpen":   "Toca para abrir tu producto",
  "pw.mobile.draftTap":    "Abrir borrador, agrega credito para construirlo de verdad",
  "pw.dup.notice":         "Ya has construido esto. Abre la version existente o construyelo de nuevo.",
  "pw.dup.openExisting":   "Abrir existente",
  "pw.dup.buildAgain":     "Construir de nuevo",
  "pw.dup.dismiss":        "Descartar",
  "pw.composer.placeholderComplete": "Pide a Nuvra construir, cambiar o mejorar...",
  "pw.composer.placeholderBuilding": "Nuvra esta construyendo. Escribe tu siguiente cambio mientras trabaja...",
  "pw.composer.placeholderNew":      "Describe el producto a construir...",
  "pw.composer.sessionActive": "Compilando",
  "pw.composer.ariaLabel": "Compositor de mensajes del espacio de trabajo",
  "pw.composer.dropToAttach": "Suelta el archivo para adjuntar",
  "pw.composer.removeImage": "Quitar imagen",
  "pw.composer.hint":      "Intro para enviar",

  // Project workspace - preview panel
  "pw.preview.appDeployed": "Aplicacion desplegada",
  "pw.preview.builtRunnableTitle": "Tu proyecto esta listo",
  "pw.preview.builtRunnableBody": "Es una version jugable que se ejecuta como su propia pagina. Despliegala para jugar en vivo, o abre el codigo para revisar, editar y descargar.",
  "pw.preview.appDeployedBody": "Tu aplicacion esta en vivo. Abrela para interactuar, compartir y solicitar ediciones.",
  "pw.preview.openLiveApp": "Abrir aplicacion en vivo",
  "pw.preview.prevBuildTitle": "Version anterior",
  "pw.preview.prevBuildBody": "La ultima compilacion fallo. Este enlace abre tu ultima version exitosa, no la fallida.",
  "pw.preview.live":       "En vivo",
  "pw.preview.previousVersion": "Version anterior operativa",
  "pw.preview.draft":      "Borrador",
  "pw.preview.switchToMobile":  "Cambiar a vista movil",
  "pw.preview.switchToDesktop": "Cambiar a vista de escritorio",
  "pw.preview.mobileView":  "Vista movil",
  "pw.preview.desktopView": "Vista de escritorio",
  "pw.preview.copyShareable": "Copiar enlace para compartir",
  "pw.preview.copied":     "Copiado",
  "pw.preview.share":      "Compartir",
  "pw.preview.openProduct": "Abre tu producto",
  "pw.preview.open":       "Abrir",
  "pw.preview.takeLive":   "Publica tu producto",
  "pw.preview.goLive":     "Publicar",
  "pw.preview.reloadProduct": "Recarga tu producto",
  "pw.preview.reload":     "Recargar",
  "pw.preview.close":      "Cerrar",
  "pw.preview.lastBuildFailed": "La ultima compilacion fallo. Mostrando version anterior.",
  "pw.preview.rebuild":    "Recompilar",

  // Project workspace - status text and toasts
  "pw.build.understanding": "Entendiendo tu solicitud...",
  "pw.build.resumeRequest": "Continua construyendo este proyecto desde donde se detuvo la compilacion anterior.",
  "pw.build.sending":       "Enviando a Nuvra AI...",
  "pw.build.designing":     "Planificando la compilacion...",
  "pw.build.acuExhausted":  "Creditos agotados.",
  "pw.build.acuTopUp":      "Tus creditos gratuitos se han agotado. Recarga para seguir construyendo.",
  "pw.build.notAuthed":     "Sin autenticar. Por favor inicia sesion.",
  "pw.build.notAuthedShort": "Sin autenticar",
  "pw.build.productLive":   "Tu producto esta en vivo",
  "pw.build.productReady":  "Tu producto esta listo",
  "pw.build.scaffoldReady":  "Un borrador inicial esta listo. Agrega credito para crear el producto real.",
  "pw.build.scaffoldTitle":  "Esto es un borrador, no tu producto final",
  "pw.build.scaffoldBody":   "La generacion real para tu solicitud necesita credito de IA. Agrega credito y Nuvra creara el modelo de datos, las paginas y la logica completos de lo que describiste.",
  "pw.build.scaffoldCta":    "Agrega credito para construir de verdad",
  "pw.build.engineeredLive": "Informe de ingeniería listo",
  "pw.build.engineered":    "Tu aplicacion esta construida.",
  "bfr.failed": "Construccion fallida",
  "bfr.creditBlocked": "Esta construccion necesita mas creditos",
  "bfr.stalled": "Nuvra no pudo confirmar el resultado final de la construccion",
  "bfr.running": "Tu construccion sigue en curso",
  "bfr.completed": "Construccion completada",
  "bfr.retry": "Intentar de nuevo",
  "bfr.topUp": "Agregar creditos",
  "bfr.refresh": "Comprobar estado",
  "bfr.refunded": "Los creditos reservados fueron reembolsados para este intento fallido.",
  "bfr.notUsed": "No se usaron creditos en este intento.",
  "pw.build.failed":        "Construccion fallida",
  "pw.build.stillBuilding": "Aun construyendo. Tu construccion continua en el servidor y aparecera aqui en breve.",
  "pw.build.stalled": "Esta construccion no termino a tiempo.",
  "pw.build.stalledHint": "Nuvra aun no pudo confirmar el resultado final. Tu trabajo se conserva. Comprueba el estado antes de iniciar otra construccion.",
  "pw.ask.analyzing":       "Analizando...",
  "pw.ask.unableNow":       "No puedo responder ahora mismo. Por favor intentalo de nuevo.",
  "pw.ask.unableConn":      "No puedo responder. Comprueba tu conexion e intentalo de nuevo.",

  /* Settings - two-factor authentication panel (TOTP via Supabase Auth) */
  "settings.twofactor.title":                  "Autenticacion de dos factores",
  "settings.twofactor.loading":                "Cargando",
  "settings.twofactor.scanInstructions":       "Escanea este codigo QR con una app de autenticacion (Google Authenticator, 1Password, Authy), luego ingresa el codigo de 6 digitos para confirmar.",
  "settings.twofactor.qrAlt":                  "Codigo QR de dos factores",
  "settings.twofactor.manualKey":              "O ingresa esta clave manualmente",
  "settings.twofactor.codeLabel":              "Codigo de 6 digitos",
  "settings.twofactor.verifying":              "Verificando",
  "settings.twofactor.confirmOn":              "Confirmar y activar",
  "settings.twofactor.cancel":                 "Cancelar",
  "settings.twofactor.onTitle":                "La autenticacion de dos factores esta activa",
  "settings.twofactor.onDesc":                 "Se requiere un codigo de tu app de autenticacion al iniciar sesion.",
  "settings.twofactor.disable":                "Desactivar",
  "settings.twofactor.addStep":                "Anade un segundo paso al iniciar sesion",
  "settings.twofactor.addStepDesc":            "Usa una app de autenticacion para generar codigos de un solo uso.",
  "settings.twofactor.starting":               "Iniciando",
  "settings.twofactor.enable":                 "Activar",
  "settings.twofactor.toast.loadFailTitle":    "No se pudo cargar el estado de 2FA",
  "settings.twofactor.toast.startFailTitle":   "No se pudo iniciar la configuracion de 2FA",
  "settings.twofactor.toast.tryAgain":         "Intentalo de nuevo.",
  "settings.twofactor.toast.verifyFailTitle":  "La verificacion fallo",
  "settings.twofactor.toast.incorrectTitle":   "Codigo incorrecto",
  "settings.twofactor.toast.onTitle":          "Autenticacion de dos factores activada",
  "settings.twofactor.toast.onBody":           "Tu cuenta ahora requiere un codigo al iniciar sesion.",
  "settings.twofactor.toast.offTitle":         "Autenticacion de dos factores desactivada",
  "settings.twofactor.toast.offBody":          "Ya no se requieren codigos al iniciar sesion.",
  "settings.twofactor.toast.disableFailTitle": "No se pudo desactivar 2FA",
  "settings.twofactor.toast.secretCopiedTitle":"Clave copiada",
  "settings.twofactor.toast.secretCopiedBody": "Pegala en tu app de autenticacion.",

  /* Project workspace - marketplace "recommended to complete this product" block */
  "pw.rec.title":             "Recomendado para completar este producto",
  "pw.rec.subtitle":          "Brechas que Nuvra detecto en lo que construiste. Son sugerencias, no cambios.",
  "pw.rec.noMatches":         "Aun no hay activos que coincidan",
  "pw.rec.assetSingular":     "activo",
  "pw.rec.assetsPlural":      "activos",
  "pw.rec.fromFree":          "desde gratis",
  "pw.rec.fromPrice":         "desde ${price}",
  "pw.rec.findInMarketplace": "Buscar en el mercado",
  "pw.rec.free":              "Gratis",
  "pw.rec.compatible":        "compatible",
  "pw.rec.checkCompat":       "verificar compat",

  /* Public preview pages (SharedPreview, SlugPreview) and the auth callback */
  "preview.notAvailable":   "Esta aplicacion no esta disponible para vista previa publica.",
  "preview.linkProblem":    "El enlace puede ser privado, haber expirado o ser incorrecto.",
  "auth.completingSignIn":  "Completando inicio de sesion...",

  // Settings - Profile panel
  "settings.profile.infoTitle":      "Informacion del perfil",
  "settings.profile.avatarAlt":      "Avatar",
  "settings.profile.name":           "Nombre",
  "settings.profile.email":          "Correo",
  "settings.profile.saving":         "Guardando",
  "settings.profile.save":           "Guardar cambios",
  "settings.profile.onboardingTitle":"Introduccion",
  "settings.profile.onboardingBody": "Repite el recorrido de bienvenida para redescubrir funciones clave o explorar la plataforma de nuevo.",
  "settings.profile.restartTour":    "Reiniciar recorrido de introduccion",
  "settings.profile.toast.errorTitle":   "Error",
  "settings.profile.toast.updatedTitle": "Perfil actualizado",
  "settings.profile.toast.updatedBody":  "Tu nombre se ha guardado.",

  // Settings - Security panel
  "settings.security.changeTitle":     "Cambiar contrasena",
  "settings.security.currentPassword": "Contrasena actual",
  "settings.security.newPassword":     "Nueva contrasena",
  "settings.security.confirm":         "Confirmar",
  "settings.security.mismatchInline":  "Las contrasenas no coinciden.",
  "settings.security.updating":        "Actualizando",
  "settings.security.update":          "Actualizar contrasena",
  "settings.security.toast.mismatchTitle": "Las contrasenas no coinciden",
  "settings.security.toast.mismatchBody":  "Las nuevas contrasenas no coinciden.",
  "settings.security.toast.shortTitle":    "Demasiado corta",
  "settings.security.toast.shortBody":     "La contrasena debe tener al menos 8 caracteres.",
  "settings.security.toast.errorTitle":    "Error",
  "settings.security.toast.updatedTitle":  "Contrasena actualizada",
  "settings.security.toast.updatedBody":   "Tu contrasena se ha cambiado.",

  "settings.security.sessions.title":    "Sesiones activas",
  "settings.security.sessions.empty":    "No hay otras sesiones activas.",
  "settings.security.sessions.current":  "Sesion actual",
  "settings.security.sessions.revoke":   "Cerrar sesion",
  "settings.security.sessions.revoking": "Cerrando sesion",
  "settings.security.sessions.browser":  "Navegador",
  "settings.security.sessions.device":   "Dispositivo",
  "settings.security.sessions.lastSeen": "Ultima vez visto",
  "settings.security.sessions.location": "Ubicacion",

  "settings.security.devices.title": "Dispositivos de confianza",
  "settings.passkeys.title": "Llaves de acceso y biometria",
  "settings.security.posture.title": "Postura de seguridad",
  "settings.security.posture.mfa": "Doble factor",
  "settings.security.posture.on": "Activo",
  "settings.security.posture.off": "Inactivo",
  "settings.security.posture.passkeys": "Llaves de acceso",
  "settings.security.posture.devices": "Dispositivos activos",
  "settings.security.posture.signIns": "Inicios de sesion (30d)",
  "settings.security.posture.warnings": "Alertas (30d)",
  "settings.security.posture.window": "Agregado desde tus eventos de inicio de sesion, dispositivos, llaves de acceso y cronologia de seguridad de los ultimos 30 dias.",
  "settings.passkeys.intro": "Verificacion con una llave de acceso guardada en tu dispositivo. Face ID, Windows Hello y la huella digital se inscriben con el mismo aviso cuando tu dispositivo lo permite.",
  "settings.passkeys.unsupported": "Este navegador no admite llaves de acceso.",
  "settings.passkeys.namePlaceholder": "Nombra esta llave (p. ej. Mi telefono)",
  "settings.passkeys.add": "Anadir llave",
  "settings.passkeys.test": "Verificar ahora",
  "settings.passkeys.added": "Llave de acceso anadida.",
  "settings.passkeys.verified": "Identidad verificada con tu llave de acceso.",
  "settings.passkeys.failed": "La ceremonia de la llave no se completo.",
  "settings.passkeys.empty": "Aun no hay llaves de acceso. Anade una para verificar acciones sensibles.",
  "settings.passkeys.unnamed": "Llave sin nombre",
  "settings.passkeys.addedOn": "Anadida el",
  "settings.passkeys.lastUsed": "ultimo uso",
  "settings.passkeys.remove": "Eliminar la llave",
  "settings.security.devices.intro": "Dispositivos que han iniciado sesion en tu cuenta. Confia en los tuyos; revoca cualquiera que no reconozcas.",
  "settings.security.devices.empty": "Aun no hay dispositivos registrados. Apareceran tras tu proximo inicio de sesion.",
  "settings.security.devices.trusted": "De confianza",
  "settings.security.devices.revoked": "Revocado",
  "settings.security.devices.lastSeen": "Visto por ultima vez",
  "settings.security.devices.trust": "Confiar",
  "settings.security.devices.untrust": "Quitar confianza",
  "settings.security.devices.revoke": "Revocar",
  "settings.security.timeline.title": "Eventos de seguridad",
  "settings.security.timeline.empty": "Aun no hay eventos de seguridad.",

  "settings.security.strength.label":      "Fortaleza de la contrasena",
  "settings.security.strength.weak":       "Debil",
  "settings.security.strength.fair":       "Regular",
  "settings.security.strength.strong":     "Fuerte",
  "settings.security.strength.very_strong": "Muy fuerte",

  "settings.security.providers.title":      "Metodos de inicio de sesion",
  "settings.security.providers.desc":       "Gestiona como inicias sesion. Al menos un metodo debe permanecer activo.",
  "settings.security.providers.linked":     "Vinculado",
  "settings.security.providers.unlink":     "Desvincular",
  "settings.security.providers.unlinking":  "Desvinculando",
  "settings.security.providers.unlinkError": "No se pudo desvincular. Mantiene al menos un metodo de inicio de sesion activo.",
  "settings.security.providers.linkedAt":   "Vinculado",
  "settings.security.providers.addGoogle":  "Conectar Google",
  "settings.security.providers.addGithub":  "Conectar GitHub",

  "settings.security.email.title":      "Correo electronico",
  "settings.security.email.verified":   "Verificado",
  "settings.security.email.unverified": "Sin verificar",
  "settings.security.email.resend":     "Reenviar verificacion",
  "settings.security.email.resent":     "Correo de verificacion enviado.",
  "settings.security.email.resentError": "No se pudo enviar el correo de verificacion.",

  "settings.security.recovery.title":       "Correo de recuperacion",
  "settings.security.recovery.desc":        "Correo de respaldo para recuperar tu cuenta si pierdes acceso a tu direccion principal.",
  "settings.security.recovery.placeholder": "respaldo@ejemplo.com",
  "settings.security.recovery.save":        "Guardar correo de recuperacion",
  "settings.security.recovery.saving":      "Guardando",
  "settings.security.recovery.saved":       "Correo de recuperacion guardado.",
  "settings.security.recovery.error":       "No se pudo guardar el correo de recuperacion.",

  "settings.security.recs.title":   "Recomendaciones de seguridad",
  "settings.security.recs.mfa":     "Autenticacion de dos factores activada",
  "settings.security.recs.email":   "Correo electronico principal verificado",
  "settings.security.recs.recovery": "Correo de recuperacion configurado",
  "settings.security.recs.allGood": "Tu cuenta cumple todas las recomendaciones de seguridad.",
  "settings.security.recs.fix":     "Corregir",

  "settings.notifications.important": "Importante",
  "settings.notifications.summary.categories": "Categorías activas",
  "settings.notifications.summary.channels": "Canales activos",
  "settings.notifications.summary.behavior": "Modelo de entrega",
  "settings.notifications.summary.behaviorBody": "Las preferencias se aplican por tipo de notificación y canal de entrega. Los cambios se guardan en tu cuenta al realizarlos.",
  "settings.notifications.saveError": "No se pudo guardar este cambio.",
  "settings.notifications.dismiss": "Descartar",
  "language.summary.available": "Disponibles ahora",
  "language.summary.note": "Cobertura de traducción",
  "language.summary.noteBody": "Solo se pueden seleccionar idiomas con un catálogo completo de Nuvra. Esto evita que un cambio aparente de idioma termine silenciosamente en inglés.",
  "language.unavailableTitle": "Idiomas de interfaz adicionales",
  "language.unavailableBody": "Estos idiomas están definidos en Nuvra, pero todavía no tienen catálogos de interfaz completos y permanecerán no disponibles hasta que sus traducciones estén listas.",
  // Settings - Notifications panel
  "settings.notifications.title":     "Preferencias de notificaciones",
  "settings.notifications.loading":   "Cargando",
  "settings.notifications.description": "Elige dónde envía Nuvra cada tipo de actualización de cuenta y producto. Los cambios se guardan automáticamente.",
  "settings.notifications.colName":   "Notificación",
  "settings.notifications.colInApp":  "En la app",
  "settings.notifications.colEmail":  "Correo",
  "settings.notifications.saving":    "Guardando",
  "settings.notifications.saved":     "Guardado",
  "settings.notifications.recommended": "Recomendado",
  "settings.notifications.securityNote": "Los mensajes de seguridad y facturación pueden ser importantes para proteger tu cuenta o completar una transacción. Las preferencias controlan los canales disponibles; no desactivan las operaciones de cuenta necesarias.",
  "settings.notifications.type.product_updates":     "Actualizaciones del producto",
  "settings.notifications.type.deployment_alerts":   "Alertas de despliegue",
  "settings.notifications.type.marketplace_updates": "Novedades del marketplace",
  "settings.notifications.type.security_alerts":     "Alertas de seguridad",
  "settings.notifications.type.weekly_digest":       "Resumen semanal",
  "settings.notifications.type.team_mentions":       "Menciones del equipo",
  "settings.notifications.type.support_alerts":       "Actualizaciones de soporte",
  "settings.notifications.type.billing_alerts":       "Alertas de facturación",
  "settings.notifications.desc.product_updates":     "Nuevos lanzamientos, mejoras y anuncios de Nuvra.",
  "settings.notifications.desc.deployment_alerts":   "Fallos de despliegue, confirmaciones de finalización y eventos de producción.",
  "settings.notifications.desc.marketplace_updates": "Novedades del marketplace, plantillas y cambios de disponibilidad.",
  "settings.notifications.desc.security_alerts":     "Inicios de sesión, autenticación, recuperación y otros eventos de seguridad.",
  "settings.notifications.desc.weekly_digest":       "Un resumen periódico de actividad importante y actualizaciones del producto.",
  "settings.notifications.desc.team_mentions":       "Cuando otros miembros del equipo te mencionan o te involucran directamente.",
  "settings.notifications.desc.support_alerts":       "Respuestas y cambios de estado de Nuvra Support.",
  "settings.notifications.desc.billing_alerts":       "Pagos, suscripciones, créditos y eventos de facturación.",

  // Settings - API Keys panel
  "settings.apiKeys.createdBanner":  "Clave creada. Copiala ahora, no se volvera a mostrar.",
  "settings.apiKeys.dismiss":        "Descartar",
  "settings.apiKeys.createTitle":    "Crear clave de API",
  "settings.apiKeys.keyName":        "Nombre de la clave",
  "settings.apiKeys.keyNamePlaceholder": "p. ej. Pipeline CI/CD",
  "settings.apiKeys.scopes":         "Ambitos",
  "settings.apiKeys.creating":       "Creando",
  "settings.apiKeys.create":         "Crear clave",
  "settings.apiKeys.activeTitle":    "Claves activas",
  "settings.apiKeys.loading":        "Cargando",
  "settings.apiKeys.empty":          "Aun no hay claves de API. Crea una arriba.",
  "settings.apiKeys.revoked":        "Revocada",
  "settings.apiKeys.revoke":         "Revocar",
  "settings.apiKeys.usingTitle":     "Usar tus claves",
  "settings.apiKeys.usingBodyBefore":"Envia la clave como token Bearer o como cabecera",
  "settings.apiKeys.usingBodyAfter": ". Las claves son de solo lectura y cada llamada se comprueba contra los ambitos de la clave.",
  "settings.apiKeys.usingNote":      "Los ambitos de escritura estan reservados. Los endpoints de escritura aceptan solo tu sesion iniciada, asi que una clave filtrada no puede cambiar un proyecto.",
  "settings.apiKeys.toast.createdTitle": "Clave de API creada",
  "settings.apiKeys.toast.createdBody":  "Copiala ahora, no se volvera a mostrar.",
  "settings.apiKeys.toast.errorTitle":   "Error",
  "settings.apiKeys.toast.copiedTitle":  "Copiada",
  "settings.apiKeys.toast.copiedBody":   "Copiada al portapapeles.",

  // Settings - Privacy panel
  "settings.privacy.title": "Privacidad y cumplimiento",

  // Settings - System Status panel
  "settings.systemStatus.title":       "Estado del sistema",
  "settings.systemStatus.checking":    "Comprobando servicios",
  "settings.systemStatus.loadError":   "No se pudo cargar el estado del sistema.",
  "settings.systemStatus.allOperational": "Todos los sistemas operativos",
  "settings.systemStatus.noChecks":    "Ninguna comprobacion de servicio esta informando ahora mismo.",
  "settings.systemStatus.incidents":   "Incidentes",
  "settings.systemStatus.noIncidents": "Sin incidentes reportados.",
  "settings.systemStatus.resolved":    "Resuelto",
  "settings.systemStatus.startedAt":   "Iniciado {time}",
  "settings.systemStatus.resolvedAt":  "Resuelto {time}",
  "settings.systemStatus.state.operational": "Operativo",
  "settings.systemStatus.state.degraded":    "Degradado",
  "settings.systemStatus.state.outage":      "Interrupcion",

  // Settings - Memory panel
  "settings.memory.title":     "Gestion de memoria",
  "settings.memory.clearAll":  "Borrar todo",
  "settings.memory.body":      "Lo que Nuvra recuerda entre tus sesiones, por agente. El contenido nunca se muestra aqui, solo lo que se almacena y su tamano. Eliminar lo borra de forma permanente.",
  "settings.memory.loading":   "Cargando memoria",
  "settings.memory.loadError": "No se pudo cargar la memoria.",
  "settings.memory.emptyTitle":"Aun no hay memoria almacenada.",
  "settings.memory.emptyBody": "A medida que trabajes con los agentes de Nuvra, lo que aprendan aparecera aqui.",
  "settings.memory.itemOne":   "{n} elemento",
  "settings.memory.itemOther": "{n} elementos",
  "settings.memory.updated":   "actualizado {date}",
  "settings.memory.toast.deletedTitle":   "Memoria eliminada",
  "settings.memory.toast.deleteFailTitle":"Error al eliminar",
  "settings.memory.toast.clearedTitle":   "Memoria borrada",
  "settings.memory.toast.clearedOne":     "{n} elemento eliminado.",
  "settings.memory.toast.clearedOther":   "{n} elementos eliminados.",
  "settings.memory.toast.clearFailTitle": "Error al borrar",

  // Settings - AI Preferences panel
  "settings.aiPreferences.title":        "Preferencias de IA",
  "settings.aiPreferences.loading":      "Cargando",
  "settings.aiPreferences.intro":        "Como trabajan para ti los agentes de Nuvra. Se guardan en tu cuenta y se aplican en todas las sesiones.",
  "settings.aiPreferences.preferredModel":"Modelo preferido",
  "settings.aiPreferences.automatic":    "Automatico (Nuvra elige el mejor modelo por tarea)",
  "settings.aiPreferences.reasoningDepth":"Profundidad de razonamiento",
  "settings.aiPreferences.creativity":   "Creatividad",
  "settings.aiPreferences.responseStyle":"Estilo de respuesta",
  "settings.aiPreferences.longTermMemory":"Usar memoria a largo plazo",
  "settings.aiPreferences.longTermMemoryNote":"Permite que los agentes recuerden el contexto entre sesiones. Gestiona la memoria almacenada en Memoria.",
  "settings.aiPreferences.saving":       "Guardando",
  "settings.aiPreferences.save":         "Guardar preferencias",
  "settings.aiPreferences.depth.concise":  "Conciso",
  "settings.aiPreferences.depth.balanced": "Equilibrado",
  "settings.aiPreferences.depth.deep":     "Profundo",
  "settings.aiPreferences.creativity.precise":  "Preciso",
  "settings.aiPreferences.creativity.balanced": "Equilibrado",
  "settings.aiPreferences.creativity.creative": "Creativo",
  "settings.aiPreferences.style.concise":  "Conciso",
  "settings.aiPreferences.style.balanced": "Equilibrado",
  "settings.aiPreferences.style.detailed": "Detallado",
  "settings.aiPreferences.toast.savedTitle":    "Preferencias guardadas",
  "settings.aiPreferences.toast.saveFailTitle": "Error al guardar",


  // Settings - AI Behavior / Memory & Context enterprise surfaces
  "settings.aiBehavior.loadError":"No se pudieron cargar los ajustes de comportamiento de IA.","settings.aiBehavior.summaryTitle":"Nuvra AI","settings.aiBehavior.accountBadge":"Ajustes de cuenta","settings.aiBehavior.summaryBody":"Estos ajustes guian la IA interactiva de Nuvra; el enrutamiento de tareas y la gobernanza siguen teniendo prioridad.",
  "settings.aiBehavior.summary.routing":"Enrutamiento","settings.aiBehavior.summary.reasoning":"Razonamiento","settings.aiBehavior.summary.style":"Estilo","settings.aiBehavior.summary.memory":"Memoria","settings.aiBehavior.enabled":"Activada","settings.aiBehavior.disabled":"Desactivada",
  "settings.aiBehavior.routingTitle":"Enrutamiento de modelos","settings.aiBehavior.routingDescription":"Elige el modelo para la IA interactiva. El enrutamiento automatico usa las reglas de disponibilidad del servidor.","settings.aiBehavior.modelLabel":"Modelo preferido","settings.aiBehavior.modelHelp":"Una preferencia, no un mecanismo para omitir facturacion o gobernanza.","settings.aiBehavior.automatic":"Automatico","settings.aiBehavior.modelPinnedNote":"Nuvra usara este modelo para solicitudes interactivas elegibles mientras siga disponible.","settings.aiBehavior.modelAutomaticNote":"Nuvra elige el modelo disponible adecuado para cada solicitud interactiva.",
  "settings.aiBehavior.reasoningTitle":"Razonamiento","settings.aiBehavior.reasoningDescription":"Define el nivel de razonamiento preferido.","settings.aiBehavior.reasoning.concise.label":"Conciso","settings.aiBehavior.reasoning.concise.description":"Razonamiento enfocado para respuestas directas.","settings.aiBehavior.reasoning.balanced.label":"Equilibrado","settings.aiBehavior.reasoning.balanced.description":"Equilibrio entre profundidad, rapidez y recursos.","settings.aiBehavior.reasoning.deep.label":"Profundo","settings.aiBehavior.reasoning.deep.description":"Razonamiento mas amplio para preguntas complejas.",
  "settings.aiBehavior.creativityTitle":"Creatividad","settings.aiBehavior.creativityDescription":"Controla la variacion de las respuestas interactivas.","settings.aiBehavior.creativity.precise.label":"Preciso","settings.aiBehavior.creativity.precise.description":"Resultados previsibles guiados por especificaciones.","settings.aiBehavior.creativity.balanced.label":"Equilibrado","settings.aiBehavior.creativity.balanced.description":"Equilibra consistencia y exploracion util.","settings.aiBehavior.creativity.creative.label":"Creativo","settings.aiBehavior.creativity.creative.description":"Permite mas variacion al explorar ideas.",
  "settings.aiBehavior.responseTitle":"Estilo de respuesta","settings.aiBehavior.responseDescription":"Elige cuanta explicacion y contexto debe ofrecer Nuvra.","settings.aiBehavior.style.concise.label":"Conciso","settings.aiBehavior.style.concise.description":"Respuestas directas con poca explicacion adicional.","settings.aiBehavior.style.balanced.label":"Equilibrado","settings.aiBehavior.style.balanced.description":"Explicaciones claras con el contexto necesario.","settings.aiBehavior.style.detailed.label":"Detallado","settings.aiBehavior.style.detailed.description":"Explicaciones completas y contexto accionable.","settings.aiBehavior.recommended":"Recomendado",
  "settings.aiBehavior.memoryTitle":"Memoria a largo plazo","settings.aiBehavior.memoryDescription":"Permite que los agentes conserven el contexto permitido entre sesiones.","settings.aiBehavior.memoryToggle":"Activar memoria a largo plazo","settings.aiBehavior.memoryOnTitle":"Memoria activada","settings.aiBehavior.memoryOnBody":"Nuvra puede usar memoria permitida para mantener la continuidad.","settings.aiBehavior.memoryOffTitle":"Memoria desactivada","settings.aiBehavior.memoryOffBody":"Nuvra no usara operaciones de memoria a largo plazo.","settings.aiBehavior.manageMemory":"Gestionar memoria almacenada",
  "settings.aiBehavior.saved":"Guardado","settings.aiBehavior.unsaved":"Cambios sin guardar","settings.aiBehavior.saveNote":"Los ajustes de cuenta se aplican a solicitudes de IA interactivas elegibles.","settings.aiBehavior.reset":"Restablecer","settings.aiBehavior.discard":"Descartar","settings.aiBehavior.saving":"Guardando","settings.aiBehavior.save":"Guardar cambios","settings.aiBehavior.toast.savedTitle":"Comportamiento de IA guardado","settings.aiBehavior.toast.saveFailTitle":"No se pudo guardar el comportamiento de IA",
  "settings.memory.managementTitle":"Memoria y contexto","settings.memory.managementDescription":"Revisa los metadatos de memoria de cuenta. El contenido almacenado nunca se expone aqui.","settings.memory.active":"Activa","settings.memory.paused":"Pausada","settings.memory.manageBehavior":"Comportamiento de IA","settings.memory.stats.items":"Elementos de memoria","settings.memory.stats.storage":"Almacenamiento usado","settings.memory.stats.updated":"Ultima actualizacion","settings.memory.stats.expiring":"Con expiracion","settings.memory.privacyTitle":"Privacidad y control","settings.memory.longTermTitle":"Memoria a largo plazo","settings.memory.longTermOn":"Activada para operaciones elegibles.","settings.memory.longTermOff":"Desactivada por la preferencia de cuenta.","settings.memory.toggleManagedElsewhere":"Este control se gestiona desde Comportamiento de IA para mantener una sola fuente de verdad.",
  "settings.memory.recordsTitle":"Memoria almacenada","settings.memory.recordsDescription":"Solo metadatos: clave, agente, tamano, fechas y expiracion.","settings.memory.searchPlaceholder":"Buscar claves o agentes","settings.memory.clearSearch":"Borrar busqueda","settings.memory.agentFilter":"Filtrar por agente","settings.memory.allAgents":"Todos los agentes","settings.memory.expires":"Expira","settings.memory.delete":"Eliminar memoria","settings.memory.noMatches":"No hay memorias que coincidan","settings.memory.noMatchesBody":"Prueba con otra clave o agente.","settings.memory.reviewBehavior":"Revisar comportamiento de IA","settings.memory.securityNote":"Nuvra mantiene el contenido de memoria detras de la frontera del servidor. Esta pagina expone solo metadatos y cada operacion esta limitada al propietario autenticado.",
  "settings.memory.detailDescription":"Metadatos de esta memoria. El contenido subyacente no se muestra aqui.","settings.memory.detail.key":"Clave de memoria","settings.memory.detail.agent":"Agente","settings.memory.detail.size":"Tamano","settings.memory.detail.created":"Creada","settings.memory.detail.updated":"Actualizada","settings.memory.detail.expiration":"Expiracion","settings.memory.noExpiration":"Sin expiracion","settings.memory.contentHidden":"El contenido almacenado no se devuelve al cliente.","settings.memory.deleteConfirmTitle":"Eliminar esta memoria?","settings.memory.deleteConfirmDescription":"Esto elimina permanentemente la memoria seleccionada.","settings.memory.clearConfirmTitle":"Borrar toda la memoria de la cuenta?","settings.memory.clearConfirmDescription":"Esto elimina permanentemente los registros gestionados por esta superficie. El contexto de proyecto y organizacion se gestiona por separado.","settings.memory.clearing":"Borrando","settings.memory.cancel":"Cancelar",

  "settings.aiBehavior.pageDescription": "Controla como Nuvra razona, responde, enruta el trabajo interactivo y usa la memoria a largo plazo.",
  "settings.memory.pageDescription": "Revisa y controla la memoria de cuenta que Nuvra almacena para mantener continuidad.",
  "settings.memory.scopeNote": "Esta superficie gestiona los metadatos de memoria de la cuenta. El contexto de proyecto y organizacion se gestiona mediante las superficies de memoria existentes de Nuvra.",
  // Settings - Billing panel
  "settings.billing.planTitle":      "Plan",
  "settings.billing.loading":        "Cargando",
  "settings.billing.loadError":      "No se pudo cargar la facturacion.",
  "settings.billing.free":           "Gratis",
  "settings.billing.noSubscription": "Sin suscripcion de pago activa.",
  "settings.billing.renews":         "se renueva {date}",
  "settings.billing.cancelNote":     "Tu plan esta configurado para cancelarse al final del periodo actual.",
  "settings.billing.managePlan":     "Gestionar plan",
  "settings.billing.upgrade":        "Mejorar",
  "settings.billing.creditsTitle":   "Creditos",
  "settings.billing.actionsTitle":   "Lo que cuestan las acciones",
  "settings.billing.actionsNote":    "Los costes se muestran antes de ejecutar una accion. Las construcciones complejas pueden costar mas; las acciones fallidas se reembolsan.",
  "settings.billing.usageTitle":     "Uso reciente",
  "settings.billing.usage.refund":   "Reembolso: {reason}",
  "settings.billing.usage.grant":    "Creditos otorgados",
  "settings.billing.usage.topup":    "Creditos comprados",
  "settings.billing.usage.refunded": "reembolsado",
  "settings.billing.noUsage":        "Aun no hay uso registrado.",

  // Settings - Audit panel
  "settings.audit.title":        "Registros de auditoria",
  "settings.audit.intro":        "Un registro inmutable de las acciones realizadas en tu cuenta y proyectos. Filtra por tipo de evento o busca por ambito.",
  "settings.audit.allTypes":     "Todos los tipos de evento",
  "settings.audit.searchPlaceholder": "Buscar ambito o tabla",
  "settings.audit.loading":      "Cargando registro de auditoria",
  "settings.audit.emptyTitle":   "Aun no hay acciones auditadas.",
  "settings.audit.noMatch":      "Ningun evento coincide con tus filtros.",
  "settings.audit.account":      "Cuenta",
  "settings.audit.project":      "Proyecto {id}",
  "settings.audit.scope.account":"cuenta",
  "settings.audit.scope.project":"proyecto",

  // Settings - Export center panel
  "settings.export.privateBadge": "Privado para tu cuenta",
  "settings.export.sourceGroups": "Grupos de datos",
  "settings.export.format": "Formato de descarga",
  "settings.export.rateLimit": "Descargas máximas",
  "settings.export.privacyTitle": "Límite de privacidad",
  "settings.export.privacyBody": "La exportación se limita a los datos que Nuvra registra como tuyos. Se incluyen mensajes públicos de soporte; se excluyen las notas internas.",
  "settings.export.successTitle": "Exportación lista",
  "settings.export.successBody": "Se descargaron {records} registros de {sources} grupos de datos.",
  "settings.export.source.support_messages": "Conversaciones de soporte",
  "settings.audit.refresh": "Actualizar",
  "settings.audit.export": "Exportar CSV filtrado",
  "settings.audit.filters": "Filtros",
  "settings.audit.clear": "Borrar filtros",
  "settings.audit.allScopes": "Todos los ámbitos",
  "settings.audit.allTime": "Todo el tiempo",
  "settings.audit.last24h": "Últimas 24 horas",
  "settings.audit.last7d": "Últimos 7 días",
  "settings.audit.last30d": "Últimos 30 días",
  "settings.audit.stat.events": "Eventos registrados",
  "settings.audit.stat.account": "Eventos de cuenta",
  "settings.audit.stat.projects": "Eventos de proyectos",
  "settings.audit.col.event": "Evento",
  "settings.audit.col.scope": "Ámbito",
  "settings.audit.col.time": "Hora",
  "settings.audit.detail.action": "Acción",
  "settings.audit.detail.time": "Registrado",
  "settings.audit.detail.payload": "Detalle registrado",
  "settings.audit.retentionNote": "Esta vista muestra los registros de auditoría más recientes que Nuvra devuelve para tu cuenta y tus proyectos. Los filtros y el CSV usan los registros cargados.",
  "settings.export.title":       "Exporta tus datos",
  "settings.export.body":        "Descarga una copia JSON de los datos vinculados a tu cuenta, para tus propios registros o para cumplimiento. La exportacion incluye solo tus propios datos y esta limitada a 3 descargas por hora.",
  "settings.export.included":    "Incluido en tu exportacion",
  "settings.export.preparing":   "Preparando exportacion",
  "settings.export.exportNow":   "Exportar mis datos",
  "settings.export.generated":   "Generado {time}",
  "settings.export.toast.doneTitle": "Exportacion descargada",
  "settings.export.toast.doneBody":  "Tus datos se guardaron como nuvra-data-export.json.",
  "settings.export.toast.failTitle": "Error de exportacion",
  "settings.export.toast.failBody":  "Error de exportacion. Por favor intentalo de nuevo.",
  "settings.export.source.projects":            "Proyectos",
  "settings.export.source.builds":              "Historial de construcciones",
  "settings.export.source.support_tickets":     "Tickets de soporte",
  "settings.export.source.creator_standing":    "Estado del creador",
  "settings.export.source.creator_violations":  "Infracciones del creador",
  "settings.export.source.creator_appeals":     "Apelaciones del creador",
  "settings.export.source.enforcement_actions": "Acciones de cumplimiento",
  "settings.export.source.published_templates": "Plantillas publicadas",
  "settings.export.source.template_installs":   "Instalaciones de plantillas",
  "settings.export.source.purchases":           "Compras",
  "settings.export.source.payments":            "Historial de pagos",
  "settings.export.source.data_audit":          "Registro de auditoria de la cuenta",

  /* Admin operator console - shell (tab bar, access gate, header) */
  "admin.shell.nav.overview":       "Resumen",
  "admin.shell.nav.status":         "Estado",
  "admin.shell.nav.trust":          "Confianza y seguridad",
  "admin.shell.nav.users":          "Usuarios",
  "admin.shell.nav.projects":       "Proyectos",
  "admin.shell.nav.builds":         "Construcciones de IA",
  "admin.shell.nav.agent":             "Agente",
  "admin.shell.nav.workers":           "Workers",
  "admin.agent.loading":               "Cargando actividad del agente…",
  "admin.agent.summary":               "{sessions} sesiones, {failed} fallidas, {acu} ACU consumidos",
  "admin.agent.filterStatus":          "Filtrar por estado",
  "admin.agent.allStatuses":           "Todos los estados",
  "admin.agent.refresh":               "Actualizar",
  "admin.agent.none":                  "Aun no hay sesiones de agente registradas.",
  "admin.agent.colWorkOrder":          "Orden de trabajo",
  "admin.agent.colGoal":               "Objetivo",
  "admin.agent.colUser":               "Usuario",
  "admin.agent.colStatus":             "Estado",
  "admin.agent.colTasks":              "Tareas",
  "admin.agent.colAcu":                "ACU",
  "admin.agent.colStarted":            "Inicio",
  "admin.agent.prev":                  "Anterior",
  "admin.agent.next":                  "Siguiente",
  "admin.agent.page":                  "Pagina {page}",
  "admin.shell.nav.billing":        "Facturacion y creditos",
  "admin.shell.nav.executive":     "Ejecutivo",
  "admin.shell.nav.financials":     "Salud financiera",
  "admin.shell.nav.alerts":         "Alertas",
  "admin.shell.nav.communications": "Comunicaciones",
  "admin.shell.nav.security":       "Seguridad",
  "admin.shell.nav.audit":          "Registro de auditoria",
  "admin.shell.nav.insights":       "Analisis",
  "admin.shell.nav.flags":          "Indicadores de funcion",
  "admin.shell.nav.logs":           "Registros",
  "admin.shell.nav.simulate":       "Simular",
  "admin.shell.nav.database":       "Base de datos",
  "admin.comms.intro":               "Las comunicaciones de plataforma llegan a todas las cuentas a la vez y no se pueden retirar. Cada una se deduplica por persona, asi que reenviar la misma version no avisa dos veces a nadie.",
  "admin.comms.release.announce":    "Anunciar la version a todos los usuarios",
  "admin.comms.policy.announce":     "Anunciar el cambio del documento a todos los usuarios",
  "admin.comms.sent":                "Entregado a {created} de {recipients} cuentas. {deduplicated} ya lo tenian.",
  "admin.comms.error.forbidden":     "Tu cuenta no es administradora de la plataforma.",
  "admin.comms.error.unknownRelease":"Esa version no esta en las notas de version publicadas, asi que no se puede anunciar.",
  "admin.comms.error.invalidRequest":"Falta algo obligatorio. Revisa los campos e intentalo de nuevo.",
  "admin.comms.error.unavailable":   "No se pudo enviar la comunicacion. No se entrego nada.",
  "admin.comms.release.title":       "Novedades",
  "admin.comms.release.body":        "Anuncia una version ya publicada en las notas de version. El texto viene de la propia version, asi que la notificacion y la pagina de novedades siempre coinciden.",
  "admin.comms.release.version":     "Version",
  "admin.comms.policy.title":        "Privacidad y Terminos",
  "admin.comms.policy.body":         "Informa a todas las cuentas de que un documento legal cambio. La version es obligatoria, porque un aviso que nadie puede vincular a un documento no permite actuar.",
  "admin.comms.policy.document":     "Documento",
  "admin.comms.policy.privacy":      "Politica de privacidad",
  "admin.comms.policy.terms":        "Terminos del servicio",
  "admin.comms.policy.version":      "Version",
  "admin.comms.policy.effective":    "Fecha de entrada en vigor (opcional)",
  "admin.comms.security.title":      "Aviso de seguridad",
  "admin.comms.security.body":       "El mensaje mas grave que puede enviar la plataforma, y el que mas se degrada con el uso excesivo. Envialo solo ante un evento de seguridad real.",
  "admin.comms.security.reference":  "Referencia",
  "admin.comms.security.referenceHint": "Un identificador estable para este aviso. Reutilizarlo evita avisar dos veces de lo mismo.",
  "admin.comms.security.subject":    "Asunto",
  "admin.comms.security.message":    "Mensaje",
  "admin.comms.security.send":       "Enviar aviso de seguridad",
  "admin.shell.verifyingAccess":    "Verificando acceso...",
  "admin.shell.deniedMissingTitle": "Herramientas de admin no desplegadas",
  "admin.shell.deniedNotAdminTitle":"Se requiere acceso de admin",
  "admin.shell.deniedMissingBody":  "Las funciones de base de datos de admin aun no estan presentes en este entorno. Aplica las migraciones del panel de admin y luego recarga.",
  "admin.shell.deniedNotAdminBody": "Tu cuenta no tiene el rol de admin. Un admin existente debe otorgarlo (user_roles.role = 'admin') antes de que esta consola este disponible.",
  "admin.shell.deniedCacheTitle":   "Herramientas de admin inaccesibles",
  "admin.shell.deniedCacheBody":    "La API no puede resolver las funciones de admin. O no se han aplicado a esta base de datos, o se aplicaron y la cache de esquema no se ha recargado. Comprueba que existen y luego recarga la cache.",
  "admin.shell.deniedSchemaTitle":  "Esquema de admin incompleto",
  "admin.shell.deniedSchemaBody":   "Las funciones de admin estan desplegadas, pero falta una tabla o columna que leen en esta base de datos. Aplica las migraciones pendientes y recarga.",
  "admin.shell.deniedGrantTitle":   "Falta permiso de ejecucion",
  "admin.shell.deniedGrantBody":    "Las funciones de admin existen pero tu rol no puede ejecutarlas. Otorga EXECUTE sobre las funciones de admin a authenticated y recarga.",
  "admin.shell.deniedSignedOutTitle":"Sesion expirada",
  "admin.shell.deniedSignedOutBody":"Tu sesion ya no es valida. Inicia sesion de nuevo para reabrir la consola.",
  "admin.shell.deniedUnknownTitle": "No se pudo abrir la consola",
  "admin.shell.deniedUnknownBody":  "La verificacion de acceso fallo por un motivo que Nuvra no puede clasificar. La respuesta de la base de datos se muestra abajo.",
  "admin.shell.backToDashboard":    "Volver al panel",
  "admin.shell.title":              "Centro de control de Nuvra",
  "admin.shell.subtitle":           "Panel de operaciones internas. Solo acceso de admin.",

  // Admin Overview tab
  "admin.overview.loading":               "Cargando estadisticas de la plataforma...",
  "admin.overview.autoRefresh":           "Se actualiza cada 30s · Ultima:",
  "admin.overview.refresh":               "Actualizar",
  "admin.overview.usersHeading":          "Usuarios",
  "admin.overview.totalUsers":            "Usuarios totales",
  "admin.overview.active30d":             "Activos (30d)",
  "admin.overview.newThisWeek":           "Nuevos esta semana",
  "admin.overview.suspended":             "Suspendidos",
  "admin.overview.platformActivity":      "Actividad de la plataforma",
  "admin.overview.projects":              "Proyectos",
  "admin.overview.activeBuilds":          "Builds activos",
  "admin.overview.failedBuilds7d":        "Builds fallidos (7d)",
  "admin.overview.unreadAlerts":          "Alertas sin leer",
  "admin.overview.successRatesTitle":     "Tasas de exito (7 dias)",
  "admin.overview.buildSuccessRate":      "Tasa de exito de builds",
  "admin.overview.deploymentSuccessRate": "Tasa de exito de despliegues",
  "admin.overview.revenueTitle":          "Ingresos",
  "admin.overview.thisMonth":             "Este mes",
  "admin.overview.allTime":               "Historico",
  "admin.overview.acuCredits":            "Creditos",
  "admin.overview.totalGranted":          "Total otorgado",
  "admin.overview.totalConsumed":         "Total consumido",
  "admin.overview.inProfilesNow":         "En perfiles ahora",
  "admin.overview.allTimeSub":            "historico",
  "admin.overview.liveTotalSub":          "total en vivo de todos los usuarios",
  "admin.overview.sevenDayTrends":        "Tendencias de 7 dias",
  "admin.overview.buildActivityTitle":    "Actividad de builds (7 dias)",
  "admin.overview.noBuildData":           "No hay datos de builds en los ultimos 7 dias.",
  "admin.overview.newUsersTitle":         "Nuevos usuarios (7 dias)",
  "admin.overview.noSignupData":          "No hay datos de registros en los ultimos 7 dias.",
  "admin.overview.seriesSuccess":         "Exito",
  "admin.overview.seriesFailed":          "Fallidos",
  "admin.overview.seriesNewUsers":        "Nuevos usuarios",

  // Admin Trust & Safety tab
  "admin.trust.loading":             "Cargando la consola del fundador",
  "admin.trust.intro":               "Confianza y seguridad: preparacion, la cola de revision, el feed de seguridad y la aplicacion para creadores.",
  "admin.trust.refresh":             "Actualizar",
  "admin.trust.appealDecideError":   "No se pudo decidir la apelacion.",
  "admin.trust.appealApproved":      "Apelacion aprobada. La retencion fue liberada.",
  "admin.trust.appealDenied":        "Apelacion denegada.",
  "admin.trust.replyPrompt":         "Responder al creador (esto resuelve el ticket):",
  "admin.trust.replyError":          "No se pudo enviar la respuesta.",
  "admin.trust.replySent":           "Respuesta enviada y ticket resuelto.",
  "admin.trust.lookupError":         "No se pudo buscar al creador.",
  "admin.trust.violationError":      "No se pudo registrar la infraccion.",
  "admin.trust.violationRecorded":   "Infraccion registrada. Estado: {standing}{terminated}.",
  "admin.trust.terminatedSuffix":    "(terminado)",
  "admin.trust.resolveError":        "No se pudo resolver el caso.",
  "admin.trust.resolveSuccess":      "Caso resuelto y retencion de ingresos liberada.",
  "admin.trust.verifyError":         "No se pudo solicitar la verificacion.",
  "admin.trust.verifyRequested":     "Verificacion de {kind} solicitada. Los pagos estan congelados hasta que el creador la complete.",
  "admin.trust.chargebackError":     "No se pudieron evaluar los contracargos.",
  "admin.trust.chargebackEnforced":  "Infraccion de la regla 9 registrada; retencion de pago aplicada.",
  "admin.trust.chargebackNoThreshold": "Evaluado. No se alcanzo el umbral de retencion.",
  "admin.trust.readinessTitle":      "Preparacion para el lanzamiento",
  "admin.trust.ready":               "Listo",
  "admin.trust.notReady":            "No listo",
  "admin.trust.readinessLoadError":  "No se pudo cargar la preparacion (se requiere acceso de admin).",
  "admin.trust.blockers":            "Bloqueadores:",
  "admin.trust.recommended":         "Recomendado:",
  "admin.trust.schemaMissing":       "Esquema faltante:",
  "admin.trust.optionalOff":         "Opcional desactivado:",
  "admin.trust.appealsQueue":        "Cola de apelaciones",
  "admin.trust.noAppeals":           "No hay apelaciones abiertas.",
  "admin.trust.creatorPrefix":       "creador",
  "admin.trust.approve":             "Aprobar",
  "admin.trust.deny":                "Denegar",
  "admin.trust.supportQueue":        "Cola de soporte",
  "admin.trust.noTickets":           "No hay tickets abiertos o escalados.",
  "admin.trust.replyResolve":        "Responder y resolver",
  "admin.trust.creatorEnforcement":  "Aplicacion para creadores",
  "admin.trust.creatorIdPlaceholder":"id de usuario del creador",
  "admin.trust.lookUp":              "Buscar",
  "admin.trust.riskLabel":           "riesgo",
  "admin.trust.standingLabel":       "estado:",
  "admin.trust.strikesLabel":        "faltas:",
  "admin.trust.terminated":          "terminado",
  "admin.trust.ruleOption":          "Regla {number}: {title}",
  "admin.trust.recordViolation":     "Registrar infraccion",
  "admin.trust.resolveCase":         "Resolver caso",
  "admin.trust.verificationLabel":   "Verificacion (Regla 11):",
  "admin.trust.requestFreezes":      "Solicitar (congela pagos)",
  "admin.trust.chargebackTitle":     "Monitoreo de contracargos",
  "admin.trust.sellerIdPlaceholder": "id de cuenta del vendedor",
  "admin.trust.evaluate":            "Evaluar",
  "admin.trust.enforce":             "Aplicar",
  "admin.trust.chargebackRate":      "tasa de contracargos",
  "admin.trust.chargebackStats":     "{sales} ventas, {chargebacks} contracargos, {disputes} disputas",
  "admin.trust.securityFeedTitle":   "Feed de seguridad y abuso",
  "admin.trust.noSecurityEvents":    "No hay eventos de seguridad recientes.",

  // Admin Users tab
  "admin.users.loading":                 "Cargando usuarios...",
  "admin.users.amountMustBePositive":    "El monto debe ser positivo.",
  "admin.users.grantFailed":             "La concesion fallo.",
  "admin.users.grantSuccess":            "Se concedieron {amount} creditos correctamente.",
  "admin.users.amountZeroOrPositive":    "El monto debe ser cero o positivo.",
  "admin.users.setFailed":               "El ajuste fallo.",
  "admin.users.setSuccess":              "Saldo ajustado a {amount} creditos (era {old}).",
  "admin.users.failed":                  "Fallo.",
  "admin.users.userSuspended":           "Usuario suspendido.",
  "admin.users.userReactivated":         "Usuario reactivado.",
  "admin.users.profileLoadFailed":       "No se pudo cargar el perfil.",
  "admin.users.linkGenFailed":           "No se pudo generar el enlace.",
  "admin.users.totalUsers":              "Usuarios totales",
  "admin.users.totalProfileAcu":         "Creditos totales",
  "admin.users.grantAcu":                "Conceder creditos",
  "admin.users.emailOrUserId":           "Correo o ID de usuario",
  "admin.users.targetPlaceholder":       "user@example.com o uuid",
  "admin.users.amountAcu":               "Monto (creditos)",
  "admin.users.reason":                  "Motivo",
  "admin.users.granting":                "Concediendo...",
  "admin.users.setAcuBalance":           "Ajustar saldo de creditos (exacto)",
  "admin.users.setToAcu":                "Ajustar a (creditos)",
  "admin.users.setting":                 "Ajustando...",
  "admin.users.setBalance":              "Ajustar saldo",
  "admin.users.setBalanceHelp":          "Sobrescribe el saldo exacto (no aditivo). Solo admin; cada cambio se registra en el libro mayor.",
  "admin.users.usersTitle":              "Usuarios ({count})",
  "admin.users.planAll":                 "todos",
  "admin.users.searchPlaceholder":       "Buscar...",
  "admin.users.colEmail":                "Correo",
  "admin.users.colPlan":                 "Plan",
  "admin.users.colStatus":               "Estado",
  "admin.users.colProfileAcu":           "Creditos",
  "admin.users.colRole":                 "Rol",
  "admin.users.grantRole":             "Otorgar {role}",
  "admin.users.revokeRole":            "Revocar {role}",
  "admin.users.roleGranted":           "Rol {role} otorgado.",
  "admin.users.roleRevoked":           "Rol {role} revocado.",
  "admin.users.roleUnchanged":         "Sin cambios: el usuario ya tenia ese estado de {role}.",
  "admin.users.subscriptionHeading":     "Suscripcion",
  "admin.users.subPlan":                 "Plan",
  "admin.users.subStatus":               "Estado",
  "admin.users.subRenews":               "Se renueva",
  "admin.users.subCancelling":           "Se cancela al final del periodo",
  "admin.users.subNone":                 "Sin registro de suscripcion.",
  "admin.users.deploymentsHeading":      "Despliegues ({count})",
  "admin.users.noDeployments":           "Sin despliegues.",
  "admin.users.colJoined":               "Registrado",
  "admin.users.colActions":              "Acciones",
  "admin.users.noUsersFound":            "No se encontraron usuarios.",
  "admin.users.statusSuspended":         "Suspendido",
  "admin.users.statusActive":            "Activo",
  "admin.users.grant":                   "Conceder",
  "admin.users.view":                    "Ver",
  "admin.users.reactivate":              "Reactivar",
  "admin.users.suspend":                 "Suspender",
  "admin.users.suspendConfirm":          "Suspender a {email}?\nMotivo: {reason}",
  "admin.users.generateLoginLink":       "Generar enlace de acceso",
  "admin.users.magicLinkHelp":           "Abre un enlace magico de 1 hora para acceder como este usuario (incognito)",
  "admin.users.copyLink":                "Copiar enlace",
  "admin.users.loadingProfile":          "Cargando perfil...",
  "admin.users.riskProfile":             "Perfil de riesgo",
  "admin.users.riskLevel":               "Nivel de riesgo:",
  "admin.users.blockedPrompts":          "Prompts bloqueados:",
  "admin.users.suspendedLabel":          "Suspendido:",
  "admin.users.yes":                     "Si",
  "admin.users.no":                      "No",
  "admin.users.reasonLabel":             "Motivo:",
  "admin.users.projectsHeading":         "Proyectos ({count})",
  "admin.users.noProjects":              "Sin proyectos.",
  "admin.users.recentBuildsHeading":     "Builds recientes ({count})",
  "admin.users.noBuilds":                "Sin builds.",
  "admin.users.acuTransactionsHeading":  "Transacciones de creditos ({count})",
  "admin.users.noTransactions":          "Sin transacciones.",
  "admin.users.paymentsHeading":         "Pagos ({count})",
  "admin.users.noPayments":              "Sin historial de pagos.",
  "admin.users.sessionsHeading":         "Sesiones ({count})",
  "admin.users.noSessions":              "Sin sesiones.",
  "admin.users.sessionRevoked":          "Revocada",
  "admin.users.sessionActive":           "Activa",

  // Admin Projects tab
  "admin.projects.loading":          "Cargando proyectos...",
  "admin.projects.flagConfirm":      "Marcar \"{name}\" para revision de admin?\nEsto creara una alerta en la pestana Alertas.",
  "admin.projects.flagFailed":       "La marca fallo.",
  "admin.projects.flagSuccess":      "\"{name}\" marcado para revision.",
  "admin.projects.title":            "Proyectos",
  "admin.projects.searchPlaceholder":"Buscar proyecto o correo...",
  "admin.projects.search":           "Buscar",
  "admin.projects.colProject":       "Proyecto",
  "admin.projects.colOwner":         "Propietario",
  "admin.projects.colType":          "Tipo",
  "admin.projects.colVisibility":    "Visibilidad",
  "admin.projects.colLatestBuild":   "Ultimo build",
  "admin.projects.colLatestDeploy":  "Ultimo despliegue",
  "admin.projects.colCreated":       "Creado",
  "admin.projects.colActions":       "Acciones",
  "admin.projects.noProjects":       "No se encontraron proyectos.",
  "admin.projects.openWorkspace":    "Abrir espacio de trabajo",
  "admin.projects.open":             "Abrir",
  "admin.projects.flag":             "Marcar",
  "admin.projects.pageInfo":         "Pagina {page} - mostrando {count} proyectos",
  "admin.projects.prev":             "Anterior",
  "admin.projects.next":             "Siguiente",

  // Admin Builds tab
  "admin.builds.cancelConfirm":      "Cancelar este build? El despliegue de Railway en curso se detendra.",
  "admin.builds.cancelFailed":       "La cancelacion fallo",
  "admin.builds.cancelled":          "Build cancelado.",
  "admin.builds.retryConfirm":       "Reintentar este build? Railway volvera a desplegar el ultimo despliegue.",
  "admin.builds.retryFailed":        "El reintento fallo",
  "admin.builds.redeployTriggered":  "Redespliegue iniciado.",
  "admin.builds.loading":            "Cargando builds...",
  "admin.builds.title":              "Builds de IA",
  "admin.builds.queuedCount":        "{count} en cola",
  "admin.builds.runningCount":       "{count} en curso",
  "admin.builds.failedCount":        "{count} fallidos",
  "admin.builds.filter.all":         "todos",
  "admin.builds.filter.running":     "en curso",
  "admin.builds.filter.done":        "completados",
  "admin.builds.filter.failed":      "fallidos",
  "admin.builds.colBuildId":         "ID de build",
  "admin.builds.colProject":         "Proyecto",
  "admin.builds.colUser":            "Usuario",
  "admin.builds.colStatus":          "Estado",
  "admin.builds.colDuration":        "Duracion",
  "admin.builds.colCreated":         "Creado",
  "admin.builds.colActions":         "Acciones",
  "admin.builds.noBuilds":           "No se encontraron builds.",
  "admin.builds.cancel":             "Cancelar",
  "admin.builds.retry":              "Reintentar",
  "admin.builds.errorLabel":         "Error",
  "admin.builds.pageInfo":           "Pagina {page} - {count} builds",
  "admin.builds.prev":               "Anterior",
  "admin.builds.next":               "Siguiente",

  // Admin Billing tab
  "admin.billing.refundConfirm":   "Reembolsar esta transaccion de creditos?",
  "admin.billing.refundFailed":    "El reembolso fallo.",
  "admin.billing.refundSuccess":   "Reembolsado correctamente.",
  "admin.billing.loading":         "Cargando transacciones...",
  "admin.billing.title":           "Facturacion y creditos",
  "admin.billing.subAcu":          "Transacciones de creditos",
  "admin.billing.subPayments":     "Pagos",
  "admin.billing.colUser":         "Usuario",
  "admin.billing.colType":         "Tipo",
  "admin.billing.colAcu":          "Creditos",
  "admin.billing.colDescription":  "Descripcion",
  "admin.billing.colBuildId":      "ID de build",
  "admin.billing.colDate":         "Fecha",
  "admin.billing.colActions":      "Acciones",
  "admin.billing.noTransactions":  "Sin transacciones.",
  "admin.billing.highChip":        "alto",
  "admin.billing.refund":          "Reembolsar",
  "admin.billing.colGateway":      "Pasarela",
  "admin.billing.colAmount":       "Monto",
  "admin.billing.colStatus":       "Estado",
  "admin.billing.noPayments":      "Sin pagos.",
  "admin.billing.tariffTitle":     "Precios por accion (interno)",
  "admin.billing.tariffNote":      "El precio en creditos de cada accion medida, del mismo registro que usa la ruta de build. Los usuarios ven los costos en la vista previa antes de ejecutar y en su historial de uso, no en esta tabla.",
  "admin.billing.pageInfo":        "Pagina {page} - {count} registros",
  "admin.billing.prev":            "Anterior",
  "admin.billing.next":            "Siguiente",

  // Admin Alerts tab
  "admin.alerts.loading":              "Cargando alertas...",
  "admin.alerts.acknowledgeFailed":      "No se pudo confirmar esa alerta.",
  "admin.alerts.resolveFailed":          "No se pudo resolver esa alerta.",
  "admin.alerts.unacknowledgedCount":  "{count} sin reconocer",
  "admin.alerts.unresolvedCount":      "{count} sin resolver",
  "admin.alerts.criticalBadge":        "CRITICO",
  "admin.alerts.sev.all":              "todas",
  "admin.alerts.sev.critical":         "critica",
  "admin.alerts.sev.high":             "alta",
  "admin.alerts.sev.medium":           "media",
  "admin.alerts.sev.low":              "baja",
  "admin.alerts.hideResolved":         "Ocultar resueltas",
  "admin.alerts.showAll":              "Mostrar todas",
  "admin.alerts.allClear":             "Sin alertas. Todo en orden.",
  "admin.alerts.resolvedChip":         "Resuelta",
  "admin.alerts.acknowledgedChip":     "Reconocida",
  "admin.alerts.acknowledge":          "Reconocer",
  "admin.alerts.resolve":              "Resolver",

  // Admin Security tab
  "admin.security.forceLogoutConfirm": "Forzar cierre de sesion de {email}?\nTodas sus sesiones activas se terminaran de inmediato.",
  "admin.security.forceLogoutFailed":  "El cierre de sesion forzado fallo.",
  "admin.security.forceLogoutSuccess": "{email} ha cerrado sesion en todas las sesiones.",
  "admin.security.suspendConfirm":     "Suspender a este usuario?",
  "admin.security.failed":             "Fallo.",
  "admin.security.userSuspended":      "Usuario suspendido.",
  "admin.security.userReactivated":    "Usuario reactivado.",
  "admin.security.loading":            "Cargando datos de seguridad...",
  "admin.security.title":              "Seguridad",
  "admin.security.subSafetyEvents":    "Eventos de seguridad",
  "admin.security.subSessions":        "Sesiones",
  "admin.security.subAbuseTracking":   "Seguimiento de abuso",
  "admin.security.sev.all":            "todas",
  "admin.security.sev.critical":       "critica",
  "admin.security.sev.high":           "alta",
  "admin.security.sev.medium":         "media",
  "admin.security.sev.low":            "baja",
  "admin.security.colUser":            "Usuario",
  "admin.security.colProvider":        "Proveedor",
  "admin.security.colStatus":          "Estado",
  "admin.security.colSuspicious":      "Sospechosa",
  "admin.security.colLastSeen":        "Ultima vez",
  "admin.security.colExpires":         "Expira",
  "admin.security.colActions":         "Acciones",
  "admin.security.noSessions":         "No se encontraron sesiones.",
  "admin.security.revoked":            "Revocada",
  "admin.security.active":             "Activa",
  "admin.security.forceLogout":        "Forzar cierre",
  "admin.security.colType":            "Tipo",
  "admin.security.colSeverity":        "Severidad",
  "admin.security.colDescription":     "Descripcion",
  "admin.security.colDate":            "Fecha",
  "admin.security.noEvents":           "No hay eventos de seguridad.",
  "admin.security.colRisk":            "Riesgo",
  "admin.security.colBlocked":         "Bloqueados",
  "admin.security.colFlagged":         "Marcados",
  "admin.security.colViolations":      "Infracciones",
  "admin.security.colLastViolation":   "Ultima infraccion",
  "admin.security.noAbuse":            "No hay registros de abuso.",
  "admin.security.suspended":          "Suspendido",
  "admin.security.reactivate":         "Reactivar",
  "admin.security.suspend":            "Suspender",
  "admin.security.pageInfo":           "Pagina {page}",
  "admin.security.prev":               "Anterior",
  "admin.security.next":               "Siguiente",

  // Admin Audit tab
  "admin.audit.loading":               "Cargando registro de auditoria...",
  "admin.audit.targetType":            "Tipo de objetivo",
  "admin.audit.targetTypePlaceholder": "p. ej. user, acu_transaction...",
  "admin.audit.action":                "Accion",
  "admin.audit.actionPlaceholder":     "p. ej. suspend, grant...",
  "admin.audit.filter":                "Filtrar",
  "admin.audit.clear":                 "Limpiar",
  "admin.audit.title":                 "Registro de auditoria",
  "admin.audit.immutable":             "(inmutable)",
  "admin.audit.colActor":              "Actor",
  "admin.audit.colAction":             "Accion",
  "admin.audit.colTargetType":         "Tipo de objetivo",
  "admin.audit.colTargetId":           "ID de objetivo",
  "admin.audit.colIp":                 "IP",
  "admin.audit.colDate":               "Fecha",
  "admin.audit.noEntries":             "No hay entradas en el registro de auditoria.",
  "admin.audit.before":                "Antes",
  "admin.audit.after":                 "Despues",
  "admin.audit.pageInfo":              "Pagina {page} - {count} entradas",
  "admin.audit.prev":                  "Anterior",
  "admin.audit.next":                  "Siguiente",

  // Admin Insights tab
  "admin.insights.loading":             "Cargando analisis...",
  "admin.insights.subtitle":            "Ultimos 30 dias. Actualiza para los datos mas recientes.",
  "admin.insights.refresh":             "Actualizar",
  "admin.insights.funnelTitle":         "Embudo de activacion de usuarios",
  "admin.insights.funnelRegistered":    "Registrados",
  "admin.insights.funnelCreatedProject":"Proyecto creado",
  "admin.insights.funnelRanFirstBuild": "Primer build ejecutado",
  "admin.insights.funnelBuildSucceeded":"Build con exito",
  "admin.insights.funnelDeployed":      "Desplegado",
  "admin.insights.fromPrev":            "({pct} desde el anterior)",
  "admin.insights.signupToBuild":       "Registro a build",
  "admin.insights.buildSuccessRate":    "Tasa de exito de builds",
  "admin.insights.signupToDeploy":      "Registro a despliegue",
  "admin.insights.usersNoProject":      "Usuarios sin proyecto",
  "admin.insights.failurePatternsTitle":"Patrones de fallos de build",
  "admin.insights.last30d":             "(30d)",
  "admin.insights.noBuildFailures":     "Sin fallos de build en los ultimos 30 dias.",
  "admin.insights.safetyEventsTitle":   "Eventos de seguridad",
  "admin.insights.noSafetyEvents":      "Sin eventos de seguridad en los ultimos 30 dias.",
  "admin.insights.colEventType":        "Tipo de evento",
  "admin.insights.colSeverity":         "Severidad",
  "admin.insights.colCount":            "Cantidad",
  "admin.insights.topFailingProjects":  "Proyectos con mas fallos",
  "admin.insights.noProjectFailures":   "No se registraron fallos de proyectos.",
  "admin.insights.colProject":          "Proyecto",
  "admin.insights.colOwner":            "Propietario",
  "admin.insights.colFailures":         "Fallos",
  "admin.insights.topFailingUsers":     "Usuarios con mas fallos",
  "admin.insights.noUserFailures":      "No se registraron fallos de usuarios.",
  "admin.insights.colUser":             "Usuario",
  "admin.insights.colFailedBuilds":     "Builds fallidos",

  // Admin Feature Flags tab
  "admin.featureFlags.toggleFailed":          "El cambio fallo",
  "admin.featureFlags.deleteConfirm":         "Eliminar el indicador \"{key}\"?\nEsto no se puede deshacer.",
  "admin.featureFlags.deleteFailed":          "La eliminacion fallo",
  "admin.featureFlags.keyNameRequired":       "La clave y el nombre son obligatorios.",
  "admin.featureFlags.rolloutRange":          "El despliegue debe ser 0-100.",
  "admin.featureFlags.saveFailed":            "El guardado fallo.",
  "admin.featureFlags.flagCreated":           "Indicador creado.",
  "admin.featureFlags.loading":               "Cargando indicadores de funcion...",
  "admin.featureFlags.intro":                 "Activa funciones de forma global o despliegalas a un porcentaje de usuarios. Todos los cambios se registran en el registro de auditoria.",
  "admin.featureFlags.cancel":                "Cancelar",
  "admin.featureFlags.newFlag":               "Nuevo indicador",
  "admin.featureFlags.newFlagTitle":          "Nuevo indicador de funcion",
  "admin.featureFlags.keyLabel":              "Clave",
  "admin.featureFlags.keyPlaceholder":        "p. ej. new_dashboard",
  "admin.featureFlags.keyHelp":               "Solo minusculas y guiones bajos. Se usa en el codigo.",
  "admin.featureFlags.displayNameLabel":      "Nombre visible",
  "admin.featureFlags.displayNamePlaceholder":"Nuevo panel",
  "admin.featureFlags.descriptionLabel":      "Descripcion",
  "admin.featureFlags.descriptionPlaceholder":"Que controla este indicador",
  "admin.featureFlags.rolloutLabel":          "Despliegue % (0 = nadie, 100 = todos)",
  "admin.featureFlags.creating":              "Creando...",
  "admin.featureFlags.createFlag":            "Crear indicador",
  "admin.featureFlags.tableTitle":            "Indicadores de funcion",
  "admin.featureFlags.noFlags":               "Aun no hay indicadores de funcion. Crea uno arriba.",
  "admin.featureFlags.colKey":                "Clave",
  "admin.featureFlags.colName":               "Nombre",
  "admin.featureFlags.colDescription":        "Descripcion",
  "admin.featureFlags.colEnabled":            "Activado",
  "admin.featureFlags.colRollout":            "Despliegue %",
  "admin.featureFlags.colUpdated":            "Actualizado",
  "admin.featureFlags.colActions":            "Acciones",
  "admin.featureFlags.disable":               "Desactivar",
  "admin.featureFlags.enable":                "Activar",
  "admin.featureFlags.setRollout":            "Establecer porcentaje de despliegue",

  // Admin Logs tab
  "admin.logs.loading":            "Cargando registros...",
  "admin.logs.loadingShort":       "Cargando...",
  "admin.logs.filterAll":          "Todos",
  "admin.logs.filterBuildErrors":  "Errores de build",
  "admin.logs.filterSafety":       "Seguridad",
  "admin.logs.filterAudit":        "Auditoria",
  "admin.logs.filterTransactions": "Transacciones",
  "admin.logs.typeBuildError":     "error de build",
  "admin.logs.typeSafety":         "seguridad",
  "admin.logs.typeAudit":          "auditoria",
  "admin.logs.typeTransaction":    "transaccion",
  "admin.logs.filterByEmail":      "Filtrar por correo...",
  "admin.logs.search":             "Buscar",
  "admin.logs.clear":              "Limpiar",
  "admin.logs.title":              "Registro de eventos unificado",
  "admin.logs.colDate":            "Fecha",
  "admin.logs.colType":            "Tipo",
  "admin.logs.colUser":            "Usuario",
  "admin.logs.colSeverity":        "Severidad",
  "admin.logs.colSummary":         "Resumen",
  "admin.logs.noEvents":           "No se encontraron eventos de registro.",
  "admin.logs.pageInfo":           "Pagina {page} - {count} eventos",
  "admin.logs.filteredBy":         "· filtrado por \"{email}\"",
  "admin.logs.prev":               "Anterior",
  "admin.logs.next":               "Siguiente",

  // Admin Simulate tab
  "admin.simulate.notRun":              "Sin ejecutar",
  "admin.simulate.running":             "Ejecutando...",
  "admin.simulate.linkFailed":          "No se pudo generar el enlace.",
  "admin.simulate.grantStepFailed":     "El paso de concesion fallo: {error}",
  "admin.simulate.unknownError":        "Error desconocido",
  "admin.simulate.acuRoundTripPassed":  "Ciclo completo de creditos superado: la concesion de +1 credito a {email} tuvo exito, reembolso aplicado.",
  "admin.simulate.acuNoTxId":            "La concesion a {email} no devolvio un id de transaccion, asi que no se reembolso nada. Revisa esa cuenta antes de repetir.",
  "admin.simulate.acuRefundFailed":      "Se concedio 1 ACU a {email} pero el reembolso fallo: {error}. Ese 1 ACU sigue en la cuenta.",
  "admin.simulate.title":               "Simulacion y verificacion de salud",
  "admin.simulate.subtitle":            "Verifica la conectividad y ejecuta diagnosticos. La prueba de ACU concede y luego reembolsa 1 ACU real en la cuenta que indiques.",
  "admin.simulate.healthCheckTitle":    "Verificacion de salud del sistema",
  "admin.simulate.passing":             "{pass}/{total} correctos",
  "admin.simulate.runChecks":           "Ejecutar verificaciones",
  "admin.simulate.runChecksHint":       "Haz clic en \"Ejecutar verificaciones\" para probar todas las RPC criticas de admin y la Edge Function.",
  "admin.simulate.journeyTitle":        "Simulacion de recorrido de usuario",
  "admin.simulate.journeyHelp":         "Genera un enlace de acceso de 1 hora para cualquier usuario y simula su experiencia en una ventana privada. Esto NO cambia su cuenta.",
  "admin.simulate.userEmail":           "Correo del usuario",
  "admin.simulate.generate":            "Generar",
  "admin.simulate.copyLink":            "Copiar enlace",
  "admin.simulate.linkNote":            "Abre en incognito. Expira en 1 hora. El usuario NO recibe notificacion.",
  "admin.simulate.acuTestTitle":        "Prueba de concesion/reembolso de creditos",
  "admin.simulate.acuTestHelp":         "Concede 1 credito de prueba a un usuario y luego lo reembolsa de inmediato. Verifica todo el flujo de facturacion de extremo a extremo sin cambios permanentes de saldo.",
  "admin.simulate.targetUserEmail":     "Correo del usuario objetivo",
  "admin.simulate.test":                "Probar",
  "admin.simulate.acuTestNote":         "Todas las acciones de prueba se registran en el registro de auditoria con el motivo \"system_test_grant\" / \"system_test_cleanup\".",

  // Admin Database tab
  "admin.database.loadError":      "No se pudo cargar el estado del esquema.",
  "admin.database.applyError":     "No se pudo aplicar el bootstrap.",
  "admin.database.title":          "Esquema de base de datos",
  "admin.database.allPresent":     "todo presente",
  "admin.database.missingCount":   "{count} faltantes",
  "admin.database.refresh":        "Actualizar",
  "admin.database.applyBootstrap": "Aplicar bootstrap",
  "admin.database.noRows":         "Aun no hay filas. Apareceran aqui una vez que esta tabla tenga datos.",
  "admin.database.present":        "presente",
  "admin.database.missing":        "faltante",
  "admin.database.helpText":       "Aplicar es idempotente y aditivo (solo crea si no existe). Nunca elimina una tabla, columna o fila. Requiere DATABASE_URL en el servidor; de lo contrario, aplica el SQL manualmente en el editor SQL de Supabase.",

  /* Top bar (brand link, command palette) */
  "topbar.home":        "Inicio de Nuvra",
  "topbar.searchAria":  "Buscar comandos",
  "topbar.searchTitle": "Buscar comandos (Ctrl/Cmd + K)",

  /* Sidebar projects group (live project navigation) */
  "sidebarProjects.nBuilding":        "{n} en construccion",
  "sidebarProjects.newProject":       "Nuevo proyecto",
  "sidebarProjects.loading":          "Cargando",
  "sidebarProjects.empty":            "Aun no hay proyectos. Inicia una construccion →",
  "sidebarProjects.status.building":  "Construyendo",
  "sidebarProjects.status.live":      "En vivo",
  "sidebarProjects.status.idle":      "Inactivo",
  "sidebarProjects.moreOnDashboard":  "+{n} mas en el panel",

  /* Share project dialog (owner-facing share-link management) */
  "share.title":                 "Compartir \"{name}\"",
  "share.description":           "Cualquier persona con sesion iniciada que abra un enlace se convierte en miembro con ese rol. Revoca un enlace cuando quieras.",
  "share.roleViewer":           "Lector",
  "share.roleEditor":           "Editor",
  "share.createLink":           "Crear enlace para compartir",
  "share.copyNow":              "Copia este enlace ahora. Por seguridad no se volvera a mostrar.",
  "share.activeLinks":          "Enlaces activos",
  "share.noActiveLinks":        "No hay enlaces de uso compartido activos.",
  "share.usedN":                "{n} usos",
  "share.unused":               "sin usar",
  "share.expiresSuffix":        " · expira {date}",
  "share.revokeLink":           "Revocar enlace",
  "share.toast.createFailTitle":"No se pudo crear el enlace",
  "share.toast.createFailBody": "Solo el propietario del proyecto puede compartirlo.",
  "share.toast.copyFailTitle":  "Error al copiar",
  "share.toast.copyFailBody":   "Copia el enlace manualmente.",
  "share.toast.revokeFailTitle":"No se pudo revocar",
  "share.toast.revokeFailBody": "Intentalo de nuevo.",

  /* Command palette (global command bar, search/navigate/build) */
  "cmdk.section.navigate":      "Navegar",
  "cmdk.section.actions":       "Acciones",
  "cmdk.section.favorites":     "Favoritos",
  "cmdk.section.suggested":     "Sugeridos",
  "cmdk.section.recent":        "Recientes",
  "cmdk.searchPlaceholder":     "Busca comandos, navega o describe lo que quieres construir...",
  "cmdk.voiceMode":             "Modo de voz",
  "cmdk.voiceInput":            "Entrada de voz",
  "cmdk.undoLabel":             "Deshacer: {label}",
  "cmdk.confirmLabel":          "Confirmar: {label}?",
  "cmdk.confirm":               "Confirmar",
  "cmdk.cancel":                "Cancelar",
  "cmdk.aiBuild":               "Construir con IA",
  "cmdk.buildWithAI":           "Construir con IA",
  "cmdk.macro":                 "Macro",
  "cmdk.stepsCount":            "{n} pasos",
  "cmdk.noMatch":               "Ningun comando coincide con \"{query}\"",
  "cmdk.noMatchHint":           "Describe lo que quieres construir o usa \"/\" para activar una macro",
  "cmdk.emptyHint":             "Busca comandos, navega o describe que construir",
  "cmdk.hint.nav":              "Navegar",
  "cmdk.hint.run":              "Ejecutar",
  "cmdk.hint.undo":             "Deshacer",
  "cmdk.hint.macro":            "Macro",
  "cmdk.voiceBadge":            "🎙 Voz",
  "cmdk.toast.build":           "→ Construir",
  "cmdk.toast.executing":       "Ejecutando...",
  "cmdk.toast.undone":          "↩ Deshecho",
  "cmdk.toast.nothingToUndo":   "Nada que deshacer",
  "cmdk.toast.addedFavorite":   "⭐ Anadido a favoritos",
  "cmdk.toast.removedFavorite": "Eliminado de favoritos",
  "cmdk.toast.listening":       "🎙 Escuchando...",
  "cmdk.toast.sayCommand":      "Di un comando o describe que construir",
  "cmdk.toast.voiceCaptured":   "🎙 Voz capturada",
  "cmdk.toast.processing":      "Procesando comando...",
  "cmdk.toast.macro":           "⚡ Macro: {name}",
  "cmdk.toast.executedSteps":   "{n} pasos ejecutados",

  /* Site footer (marketing footer, navigation + legal) */
  "footer.section.platform":    "Plataforma",
  "footer.section.product":     "Producto",
  "footer.section.legal":       "Legal",
  "footer.link.features":       "Funciones",
  "footer.link.howItWorks":     "Como funciona",
  "footer.link.useCases":       "Casos de uso",
  "footer.link.liveDemo":       "Planificador de Arquitectura",
  "footer.link.dashboard":      "Panel",
  "footer.link.deploy":         "Desplegar",
  "footer.link.billing":        "Facturacion",
  "footer.link.privacyPolicy":  "Politica de privacidad",
  "footer.link.termsOfService": "Terminos del servicio",
  "footer.link.acceptableUse":  "Uso aceptable",
  "footer.link.aiContentPolicy":"Politica de contenido de IA",
  "footer.link.securityPolicy": "Politica de seguridad",
  "footer.social.community":    "Comunidad",
  "footer.rightsReserved":      "Todos los derechos reservados.",
  "footer.bottom.privacy":      "Privacidad",
  "footer.bottom.terms":        "Terminos",
  "footer.bottom.cookies":      "Cookies",

  /* Project backend menu (generated database viewer/editor popover) */
  "backendMenu.triggerTitle":   "Base de datos y backend generados",
  "backendMenu.triggerAria":    "Base de datos",
  "backendMenu.triggerLabel":   "Base de datos",
  "backendMenu.heading":        "Backend generado",
  "backendMenu.tab.deploys":    "Despliegues",
  "backendMenu.tab.backups":    "Copias",
  "backendMenu.tab.audit":      "Auditoria",
  "backendMenu.tab.diagram":    "Diagrama",
  "backendMenu.status.liveDatabase": "Base de datos activa",
  "backendMenu.status.needsReview":  "Necesita revision",
  "backendMenu.status.schemaReady":  "Esquema listo",
  "backendMenu.deployHistory":  "Historial de despliegues",
  "backendMenu.loading":        "Cargando",
  "backendMenu.noDeploys":      "Aun no hay despliegues.",
  "backendMenu.open":           "abrir",
  "backendMenu.backupNow":      "Copiar ahora",
  "backendMenu.noBackups":      "Aun no hay copias.",
  "backendMenu.confirm":        "confirmar",
  "backendMenu.cancel":         "cancelar",
  "backendMenu.restore":        "restaurar",
  "backendMenu.restoreTitle":   "Restaurar sobrescribe las filas actuales con esta captura; las filas anadidas despues se conservan",
  "backendMenu.download":       "descargar",
  "backendMenu.auditLog":       "Registro de auditoria de datos",
  "backendMenu.noChanges":      "Aun no se han registrado cambios.",
  "backendMenu.deployed":       "Desplegado",
  "backendMenu.deploying":      "Desplegando",
  "backendMenu.openLiveApp":    "Abrir app en vivo",
  "backendMenu.loadingBackend": "Cargando backend",
  "backendMenu.noTables":       "Aun no hay tablas. Anade una abajo, o Nuvra las crea cuando construye una app full-stack.",
  "backendMenu.pending":        "pendiente",
  "backendMenu.column":         "columna",
  "backendMenu.colTo":          "a",
  "backendMenu.colUnique":      "unica",
  "backendMenu.restoreColTitle":"Restaurar (tus datos se conservaron)",
  "backendMenu.removeColTitle": "Quitar de la app (los datos se conservan, reversible)",
  "backendMenu.view":           "ver",
  "backendMenu.viewTitle":      "Explorar filas (requiere una base de datos materializada)",
  "backendMenu.import":         "importar",
  "backendMenu.importTitle":    "Importar filas desde un archivo CSV",
  "backendMenu.seed":           "sembrar",
  "backendMenu.seedTitle":      "Insertar algunas filas de muestra (requiere una base de datos materializada)",
  "backendMenu.reference":      "referencia",
  "backendMenu.required":       "obligatorio",
  "backendMenu.unique":         "unico",
  "backendMenu.indexed":        "indexado",
  "backendMenu.noReference":    "sin referencia",
  "backendMenu.referenceTitle": "Referenciar otra tabla",
  "backendMenu.add":            "Anadir",
  "backendMenu.isolatedSchema": "Esquema aislado",
  "backendMenu.newTable":       "Nueva tabla",
  "backendMenu.field":          "campo",
  "backendMenu.createTable":    "Crear tabla",
  "backendMenu.rebuildDeploy":  "Reconstruir y desplegar",
  "backendMenu.rebuildTitle":   "Reconstruye la app con tus cambios de esquema y despliegala en vivo (usa creditos)",
  "backendMenu.ph.columnName":  "nombre de columna",
  "backendMenu.ph.enumValues":  "valores permitidos, separados por comas",
  "backendMenu.ph.min":         "min",
  "backendMenu.ph.minLength":   "longitud min",
  "backendMenu.ph.max":         "max",
  "backendMenu.ph.maxLength":   "longitud max",
  "backendMenu.ph.pattern":     "patron (regex, opcional)",
  "backendMenu.ph.default":     "valor por defecto (opcional)",
  "backendMenu.ph.tableName":   "Nombre de tabla (p. ej. Invoice)",
  "backendMenu.ph.fieldName":   "nombre de campo",
  "backendMenu.unit.row":       "fila",
  "backendMenu.unit.rows":      "filas",
  "backendMenu.unit.table":     "tabla",
  "backendMenu.unit.tables":    "tablas",
  "backendMenu.notice.tableCreated":    "Creada en tu base de datos.",
  "backendMenu.notice.savedNextBuild":  "Guardado. Se aplica en la proxima construccion.",
  "backendMenu.notice.columnAdded":     "Anadida a tu base de datos.",
  "backendMenu.notice.columnRemoved":   "Quitada de la app. Tus datos se conservan y el cambio es reversible.",
  "backendMenu.notice.columnRestored":  "Columna restaurada.",
  "backendMenu.notice.relationshipAdded": "Relacion anadida.",
  "backendMenu.notice.seededOne":       "Se sembro {n} fila de muestra en {table}.",
  "backendMenu.notice.seededN":         "Se sembraron {n} filas de muestra en {table}.",
  "backendMenu.notice.backedUp":        "Se copiaron {rows} {rowLabel} en {tables} {tableLabel}.",
  "backendMenu.notice.restored":        "Se restauraron {rows} {rowLabel} en {tables} {tableLabel}.",
  "backendMenu.notice.imported":        "Se importaron {imported} de {total} {rowLabel} en {table}.",
  "backendMenu.notice.importedSkipped": "{failed} omitidas.",
  "backendMenu.notice.rebuilding":      "Reconstruyendo y desplegando. Tu app en vivo se actualiza cuando termine.",
  "backendMenu.error.load":             "No se pudo cargar el backend.",
  "backendMenu.error.addTable":         "No se pudo anadir la tabla.",
  "backendMenu.error.addColumn":        "No se pudo anadir la columna.",
  "backendMenu.error.removeColumn":     "No se pudo quitar la columna.",
  "backendMenu.error.restoreColumn":    "No se pudo restaurar la columna.",
  "backendMenu.error.seed":             "No se pudo sembrar la tabla.",
  "backendMenu.error.backup":           "No se pudo crear la copia.",
  "backendMenu.error.restoreBackup":    "No se pudo restaurar la copia.",
  "backendMenu.error.import":           "No se pudo importar el CSV.",
  "backendMenu.error.insufficientCredits": "Creditos insuficientes para desplegar.",
  "backendMenu.error.startDeploy":      "No se pudo iniciar el despliegue.",
  "backendMenu.audit.seed":             "sembro",
  "backendMenu.audit.import":           "importo",
  "backendMenu.audit.cellUpdate":       "edito una celda",
  "backendMenu.audit.restore":          "restauro una copia",
  "backendMenu.audit.backup":           "creo una copia",
  "backendMenu.audit.addTable":         "anadio tabla",
  "backendMenu.audit.addColumn":        "anadio columna",
  "backendMenu.audit.removeColumn":     "quito columna",
  "backendMenu.audit.restoreColumn":    "restauro columna",
  "backendMenu.audit.addRelationship":  "anadio relacion",
  "backendMenu.audit.build":            "construyo la app",
  "backendMenu.audit.buildFailed":      "fallo la construccion",
  "backendMenu.audit.lockSection":      "bloqueo una pagina",
  "backendMenu.audit.unlockSection":    "desbloqueo una pagina",
  "backendMenu.audit.hidePage":         "oculto una pagina",
  "backendMenu.audit.showPage":         "restauro una pagina",
  "backendMenu.audit.renamePage":       "renombro una pagina",
  "backendMenu.audit.hideComponent":    "oculto un componente",
  "backendMenu.audit.showComponent":    "restauro un componente",
  "backendMenu.audit.codeEdit":         "edito codigo",
  "backendMenu.audit.codePush":         "envio a GitHub",
  "backendMenu.audit.hostDeploy":       "desplego",
  "backendMenu.audit.supabaseConnect":  "conecto Supabase",

  /* Strategic memory panel (project reasoning artifacts) */
  "strategicMemory.loading":      "Cargando estrategia",
  "strategicMemory.error":        "No se pudo cargar la estrategia de este proyecto ahora. Aparecera cuando sea accesible.",
  "strategicMemory.empty.title":  "Aun no hay estrategia capturada",
  "strategicMemory.empty.body":   "A medida que Nuvra razona sobre tu producto, su plano, descubrimiento y recomendaciones aparecen aqui y te siguen entre dispositivos.",
  "strategicMemory.title":        "Estrategia",
  "strategicMemory.subtitle":     "Lo que Nuvra entiende de este producto, del concepto a las recomendaciones.",
  "strategicMemory.confidence":   "{pct} de confianza",
  "strategicMemory.section.concept":         "El concepto",
  "strategicMemory.section.discovery":       "Descubrimiento",
  "strategicMemory.section.blueprint":       "Plano",
  "strategicMemory.section.implications":    "Implicaciones",
  "strategicMemory.section.recommendations": "Recomendaciones",
  "strategicMemory.label.value":     "Valor:",
  "strategicMemory.label.for":       "Para:",
  "strategicMemory.label.distinct":  "Lo que lo hace distinto",
  "strategicMemory.label.problems":  "Problemas que resuelve",
  "strategicMemory.label.domain":    "Dominio:",
  "strategicMemory.label.form":      "Forma:",
  "strategicMemory.label.readiness": "Preparacion:",
  "strategicMemory.label.nextSteps": "Proximos pasos",
  "strategicMemory.stat.modules":       "Modulos",
  "strategicMemory.stat.pages":         "Paginas",
  "strategicMemory.stat.models":        "Modelos",
  "strategicMemory.stat.roles":         "Roles",
  "strategicMemory.stat.workflows":     "Flujos",
  "strategicMemory.stat.integrations":  "Integraciones",
  "strategicMemory.stat.architectural": "Arquitectonicas",
  "strategicMemory.stat.requiredCaps":  "Capacidades necesarias",
  "strategicMemory.stat.risks":         "Riesgos",
  "strategicMemory.stat.dependencies":  "Dependencias",

  /* Monetization menu (define plans end-users can buy) */
  "monetizationMenu.title":        "Monetizacion",
  "monetizationMenu.monetize":     "Monetizar",
  "monetizationMenu.notOwner":     "Solo el propietario del proyecto puede configurar la monetizacion.",
  "monetizationMenu.intro":        "Define lo que tus usuarios finales pueden comprar. Limita funciones por claves de permisos.",
  "monetizationMenu.enabled":      "Activado",
  "monetizationMenu.featured":     "Destacado",
  "monetizationMenu.addPlan":      "Anadir plan",
  "monetizationMenu.save":         "Guardar",
  "monetizationMenu.saved":        "Guardado.",
  "monetizationMenu.billingActive":"Facturacion activa para {n} plan(es).",
  "monetizationMenu.addPriceId":   "Anade un id de precio del proveedor a un plan de pago para habilitar el pago.",
  "monetizationMenu.error.save":   "No se pudo guardar.",
  "monetizationMenu.interval.month": "/ mes",
  "monetizationMenu.interval.year":  "/ ano",
  "monetizationMenu.interval.once":  "una vez",
  "monetizationMenu.ph.planName":     "Nombre del plan",
  "monetizationMenu.ph.features":     "Funciones (separadas por comas)",
  "monetizationMenu.ph.entitlements": "Claves de permisos (separadas por comas)",
  "monetizationMenu.ph.priceId":      "Id de precio del proveedor (para el pago)",

  /* Members menu (invite collaborators) */
  "membersMenu.title":        "Miembros",
  "membersMenu.notOwner":     "Solo el propietario del proyecto puede gestionar los miembros.",
  "membersMenu.invite":       "Invitar",
  "membersMenu.invited":      "invitado",
  "membersMenu.empty":        "Aun no hay colaboradores. Invita a alguien por correo.",
  "membersMenu.role.editor":  "Editor",
  "membersMenu.role.viewer":  "Visor",
  "membersMenu.ph.email":     "colaborador@email.com",
  "membersMenu.error.invite": "No se pudo invitar",

  /* Marketplace menu (publish and clone templates) */
  "marketplaceMenu.title":         "Marketplace",
  "marketplaceMenu.tab.browse":    "Explorar",
  "marketplaceMenu.tab.publish":   "Publicar este proyecto",
  "marketplaceMenu.search":        "Buscar",
  "marketplaceMenu.empty":         "Aun no hay plantillas. Publica una desde cualquier proyecto.",
  "marketplaceMenu.clones":        "{n} clones",
  "marketplaceMenu.clone":         "Clonar",
  "marketplaceMenu.publishIntro":  "Publica la estructura de este proyecto como plantilla reutilizable. Nunca se incluyen secretos, datos ni dominios.",
  "marketplaceMenu.publish":       "Publicar",
  "marketplaceMenu.published":     "Publicado en el marketplace.",
  "marketplaceMenu.titleTooShort": "Anade un titulo de al menos 3 caracteres.",
  "marketplaceMenu.ph.search":      "Buscar plantillas",
  "marketplaceMenu.ph.title":       "Titulo de la plantilla",
  "marketplaceMenu.ph.description":  "Que construye?",
  "marketplaceMenu.error.clone":   "No se pudo clonar.",
  "marketplaceMenu.error.publish": "No se pudo publicar.",

  /* Observability menu (deployed app usage, errors, self-healing) */
  "observabilityMenu.triggerTitle": "Observabilidad de la app",
  "observabilityMenu.insights":     "Metricas",
  "observabilityMenu.appInsights":  "Metricas de la app",
  "observabilityMenu.autoFix":      "Correccion automatica",
  "observabilityMenu.mode.fullAuto": "Totalmente auto",
  "observabilityMenu.mode.safe":     "Seguro",
  "observabilityMenu.mode.manual":   "Manual",
  "observabilityMenu.modeDesc.fullAuto": "Las correcciones de riesgo bajo y medio se aplican automaticamente. Las de riesgo alto siempre te preguntan primero.",
  "observabilityMenu.modeDesc.safe":     "Las correcciones de riesgo bajo se aplican automaticamente. Todo lo demas se propone para tu aprobacion.",
  "observabilityMenu.modeDesc.manual":   "Nuvra detecta problemas y propone correcciones. Tu apruebas cada correccion.",
  "observabilityMenu.noIssues":     "No se detectaron problemas recurrentes. El feed se actualiza a medida que se observan errores reales en ejecucion.",
  "observabilityMenu.risk":         "riesgo {risk}",
  "observabilityMenu.confidence":   "{n}% de confianza",
  "observabilityMenu.autoFixing":   "Corrigiendo auto",
  "observabilityMenu.autoFixWhenKeyed":      "Correccion auto cuando la IA este activa",
  "observabilityMenu.autoFixWhenKeyedTitle": "Esta correccion califica para aplicacion automatica; se activa cuando se configura una clave de proveedor de IA.",
  "observabilityMenu.approveFix":   "Aprobar correccion",
  "observabilityMenu.dismiss":      "Descartar",
  "observabilityMenu.noEvents":     "Aun no hay eventos. Configura {ingestVar} y {projectVar} en tu app desplegada para reportar uso y errores aqui.",
  "observabilityMenu.stat.views":    "Vistas",
  "observabilityMenu.stat.sessions": "Sesiones",
  "observabilityMenu.stat.errors":   "Errores",
  "observabilityMenu.stat.logs":     "Registros",
  "observabilityMenu.topPages":     "Paginas principales",
  "observabilityMenu.errors":       "Errores",
  "observabilityMenu.fixThis":      "Corregir esto",
  "observabilityMenu.recentLogs":   "Registros recientes",

  /* Build chat thread (work-order engineering log) */
  "bct.turn.you":              "Tu",
  "bct.turn.nuvra":            "Nuvra",
  "bct.turn.buildRequested":   "Compilacion solicitada",
  "bct.session.expand":             "Inspeccionar sesion",
  "bct.wo.status.queued":           "En cola",
  "bct.wo.status.understanding":    "Analizando",
  "bct.wo.status.planning":         "Planificando",
  "bct.wo.status.implementing":     "Implementando",
  "bct.wo.status.verifying":        "Verificando",
  "bct.wo.status.completed":        "Completado",
  "bct.wo.status.blocked":          "Bloqueado",
  "bct.wo.status.failed":           "Fallido",
  "bct.summary.files": "Archivos",
  "bct.summary.validation": "Validacion",
  "bct.summary.tests": "Pruebas",
  "bct.summary.preview": "Vista previa",
  "bct.summary.passed": "aprobada",
  "bct.summary.review": "requiere revision",
  "bct.summary.available": "disponible",
  "bct.summary.none": "ninguna",
  "bct.stage.artifactsGenerated":   "Archivos del proyecto",
  "bct.stage.finalStatus":          "Resultado",
  "bct.stage.engineeringNote":      "Nota",
  "bct.status.queued":              "En cola",
  "bct.status.inProgress":          "En progreso",
  "bct.status.failed":              "Fallido",
  "bct.status.cancelled":           "Cancelado",
  "bct.status.working":             "Nuvra esta trabajando",
  "bct.status.planned":             "Planificado",
  "bct.turn.planRequested":         "Plan solicitado",
  "bct.plan.pages":                 "Paginas",
  "bct.plan.dataModels":            "Modelos de datos",
  "bct.plan.features":              "Funciones",
  "bct.stream.jumpToLatest":        "Ir a lo mas reciente",
  "bct.status.completed":           "Completado",
  "bct.status.note":                "Nota",
  "bct.blueprint.pages":            "Paginas",
  "bct.blueprint.components":       "Componentes",
  "bct.blueprint.models":           "Modelos",
  "bct.blueprint.routes":           "Rutas",
  "bct.blueprint.auth":             "Autenticacion",
  "bct.blueprint.deploy":           "Despliegue",
  "bct.blueprint.fallbackTitle":    "Plano de la aplicacion",
  "bct.blueprint.suggestedNext":    "Siguiente sugerido",
  // Build credits used: the customer-facing record of what one build's credits went on.
  "bcu.title":                      "Creditos usados",
  "bcu.credits":                    "{count} creditos",
  "bcu.note":                       "Repartidos segun el trabajo que llevo cada parte de esta construccion.",
  "bcu.noBreakdown":                "No hay un desglose detallado para esta construccion.",
  "bcu.zero":                       "Esta construccion no uso creditos.",
  "bcu.stage.understanding":        "Entendiendo tu peticion",
  "bcu.stage.planning":             "Planificando la construccion",
  "bcu.stage.writing":              "Escribiendo tu codigo",
  "bcu.stage.checking":             "Revisando tu aplicacion",
  "bcu.stage.fixing":               "Corrigiendo problemas",
  "bcu.stage.other":                "Otro trabajo",
  "bct.report.completed":           "Ingenieria completada",
  "bct.report.completedWithBlockers": "Ingenieria completada con bloqueos",
  "bct.report.blockersHint":        "La compilacion termino, pero el despliegue esta bloqueado hasta resolver esto.",
  "bct.report.scaffoldDraft":       "Borrador generado (scaffold)",
  "bct.report.scaffoldDraftHint":   "Esto es un borrador deterministico, no software generado por IA. Activa la generacion con IA para construir el producto real.",
  "bct.mode.aiGenerated":           "Generado por IA",
  "bct.mode.scaffoldDraft":         "Borrador scaffold",
  "bct.report.openRuntime":         "Abrir runtime",
  "bct.report.capabilities":        "Capacidades",
  "bct.receipt.withBalance":        "Esta compilacion uso {cost} creditos. Te quedan {balance} creditos.",
  "bct.receipt.costOnly":           "Esta compilacion uso {cost} creditos.",
  "bct.security.passed":            "Seguridad revisada: sin problemas criticos ({score}/100).",
  "bct.security.issues":            "Seguridad revisada: {critical} criticos, {high} altos detectados.",
  "bct.report.decisions":           "Decisiones que tomo tu equipo",
  "bct.report.pages":               "Paginas",
  "bct.report.blockers":            "Bloqueos",
  "bct.report.heading":             "Detalles del build",
  "bct.report.findings":          "Hallazgos y reparaciones",
  "bct.report.verified":          "Verificado",
  "bct.report.artifact":          "Artefacto",
  "bct.report.artifactVerified":  "Artefacto verificado",
  "bct.report.remaining":         "Pendiente",
  "bct.report.next":              "Siguiente",
  "bct.report.implemented":         "Implementado",
  "bct.report.requested":      "Solicitado",
  "bct.report.changedFiles":   "Archivos modificados",
  "bct.report.created":        "creado",
  "bct.report.modified":       "modificado",
  "bct.report.removed":        "eliminado",
  "bct.report.andMoreFiles":   "y {n} mas",
  "bct.report.filesWritten":        "{n} archivos escritos",
  "bct.report.details":             "Detalles",
  "bct.report.listen":              "Escuchar",
  "bct.report.stopListening":       "Detener",
  "bct.report.nextInvite":          "Quieres algo mas sobre esto? Describe el cambio y Nuvra construye sobre esta aplicacion exacta.",
  "bct.report.hideDetails":         "Ocultar detalles",
  "bct.report.preview":             "Vista previa",
  "bct.error.buildFailed":          "Compilacion fallida",
  "bct.error.retry":                "Reintentar",
  "bct.error.rerun":                "Reejecutar orden de trabajo",
  "bct.latestWorkOrder":            "Ultima orden de trabajo",
  "bct.reuseRequestTitle":          "Reutilizar esta solicitud en el compositor",
  "bct.msg.edit":                   "Editar",
  "bct.msg.copy":                   "Copiar",
  "bct.msg.copied":                 "Copiado",
  "bct.msg.share":                  "Compartir",
  "bct.msg.like":                   "Buena respuesta",
  "bct.msg.dislike":                "Mala respuesta",
  "bct.history.loadEarlier":     "Cargar historial anterior",
  "bct.history.loadingEarlier":  "Cargando historial anterior...",
  "bct.msg.showMore":               "Ver mas",
  "bct.msg.showLess":               "Ver menos",
  "bct.msg.rerun":                  "Reutilizar esta solicitud",
  "bct.buildRequestFallback":       "Solicitud de compilacion",
  "bct.cap.auth":                   "Autenticacion",
  "bct.cap.search":                 "Busqueda",
  "bct.cap.payments":               "Pagos",
  "bct.cap.realtime":               "Tiempo real",
  "bct.cap.notifications":          "Notificaciones",
  "bct.cap.upload":                 "Subida de archivos",
  "bct.cap.export":                 "Exportacion de datos",
  "bct.cap.analytics":              "Analiticas",
  "bct.cap.email":                  "Correo",

  /* Extensions page */
  "ext.title":                      "Extensiones",
  "ext.subtitle":                   "Agrega capacidades a tu proyecto. Tu otorgas cada permiso de forma explicita.",
  "ext.noProject":                  "Abre esta pagina desde un proyecto para instalar y gestionar sus extensiones. Puedes explorar el registro a continuacion.",
  "ext.loading":                    "Cargando extensiones...",
  "ext.errorLoad":                  "No se pudieron cargar las extensiones.",
  "ext.installed":                  "Instaladas",
  "ext.installedNone":              "No hay extensiones instaladas en este proyecto todavia.",
  "ext.permissionsNone":            "No se han otorgado permisos.",
  "ext.suspend":                    "Suspender",
  "ext.activate":                   "Activar",
  "ext.uninstall":                  "Desinstalar",
  "ext.registry":                   "Registro",
  "ext.searchPlaceholder":          "Buscar extensiones",
  "ext.typeAll":                    "Todos los tipos",
  "ext.search":                     "Buscar",
  "ext.registryNone":               "No hay extensiones publicadas todavia.",
  "ext.installOpenProject":         "Abre un proyecto para instalar",
  "ext.installIncompat":            "Incompatible con esta plataforma",
  "ext.install":                    "Instalar",
  "ext.permRequested.one":          "{n} permiso solicitado",
  "ext.permRequested.other":        "{n} permisos solicitados",
  "ext.installCount.one":           "{n} instalacion",
  "ext.installCount.other":         "{n} instalaciones",
  "ext.modalTitle":                 "Otorgar permisos",
  "ext.modalDesc":                  "{title} solicita acceso. Otorga solo lo que consideres seguro.",
  "ext.paidNotice":                 "Extension de pago. Comprala en el marketplace primero. Una vez que la poseas, la instalacion aqui se completa.",
  "ext.deprecatedNotice":           "Esta version esta obsoleta pero sigue funcionando. Considera una version mas nueva cuando este disponible.",
  "ext.permissionsRequestedNone":   "Esta extension no solicita permisos.",
  "ext.optional":                   "opcional",
  "ext.cancel":                     "Cancelar",
  "ext.installWithPerm.one":        "Instalar con {n} permiso",
  "ext.installWithPerm.other":      "Instalar con {n} permisos",
  "ext.badgeVerified":              "Verificado",
  "ext.badgeCommunity":             "Comunidad",
  "ext.badgeExperimental":          "Experimental",
  "ext.badgeIncompat":              "Incompatible",
  "ext.badgeDeprecated":            "Obsoleto",
  "ext.badgePaid":                  "De pago",
  "ext.toastInstalled":             "Extension instalada",
  "ext.toastPurchaseRequired":      "Compra requerida",
  "ext.toastPurchaseDesc":          "Esta extension es de pago. Comprala en el marketplace y luego instalala.",
  "ext.toastInstallFailed":         "Instalacion fallida",
  "ext.toastTryAgain":              "Intentalo de nuevo.",
  "ext.toastActionFailed":          "Accion fallida",
  "ext.toastPermFailed":            "No se pudo actualizar el permiso",

  /* Capabilities page */
  "cap.page.label":   "Inteligencia de Plataforma",
  "cap.page.title":   "Que puede hacer Nuvra",
  "cap.page.desc":    "Cada capacidad aqui es real, funcionando en produccion y disponible ahora mismo.",
  "cap.page.allLive": "Todas las capacidades en vivo",
  "cap.filter.all":   "Todas las Capacidades",
  "cap.history.label": "Cronologia de Infraestructura",
  "cap.history.title": "Historial de Construccion de la Plataforma",
  "cap.history.desc":  "Cada fase a continuacion es un lanzamiento de infraestructura real, completado y desplegado.",
  "cap.footer.live":   "Todas las capacidades en produccion",
  "cap.footer.status": "Estado de la plataforma en vivo",

  /* Capabilities - AI Generation */
  "cap.aiGen.label":    "Generacion IA",
  "cap.aiGen.headline": "Cada capa de tu aplicacion, generada en paralelo por agentes IA especializados",
  "cap.aiGen.multiAgent.name":   "Generacion de Codigo Multi-Agente",
  "cap.aiGen.multiAgent.desc":   "Agentes especializados se ejecutan simultaneamente. Uno construye el frontend, otro la API, otro esquematiza la base de datos, otro configura la infraestructura. Nada es secuencial.",
  "cap.aiGen.multiAgent.badge":  "Agentes paralelos",
  "cap.aiGen.aiRouting.name":    "Enrutamiento IA Redundante",
  "cap.aiGen.aiRouting.desc":    "Si una fuente de IA no esta disponible, el sistema enruta automaticamente a la siguiente. Tu generacion nunca se detiene por una interrupcion del proveedor.",
  "cap.aiGen.aiRouting.badge":   "Tolerante a fallos",
  "cap.aiGen.consensus.name":    "Sistema de Consenso de Agentes",
  "cap.aiGen.consensus.desc":    "Multiples agentes independientes generan salidas competidoras. Una capa de consenso selecciona el resultado de mayor calidad mediante acuerdo ponderado, no seleccion aleatoria.",
  "cap.aiGen.consensus.badge":   "Consenso",
  "cap.aiGen.adaptive.name":     "Enrutamiento Adaptativo",
  "cap.aiGen.adaptive.desc":     "Las decisiones de enrutamiento mejoran automaticamente con el tiempo basandose en datos de resultados. El sistema aprende que agentes rinden mejor para cada tipo de solicitud.",
  "cap.aiGen.adaptive.badge":    "Adaptativo",
  "cap.aiGen.arbitration.name":  "Arbitraje de Agentes",
  "cap.aiGen.arbitration.desc":  "Cuando los agentes discrepan, sus salidas compiten en arbitraje estructurado en lugar de seleccion aleatoria. Las discrepancias producen mejor resultado.",
  "cap.aiGen.arbitration.badge": "Multi-agente",
  "cap.aiGen.copilot.name":      "Copiloto IA Consciente del Contexto",
  "cap.aiGen.copilot.desc":      "El asistente integrado lee los datos reales de tu proyecto, el historial de construccion y el estado de despliegue antes de responder. Conoce tu sistema, no solo tu pregunta.",
  "cap.aiGen.copilot.badge":     "Consciente del contexto",
  "cap.aiGen.template.name":     "Motor de Plantillas IA",
  "cap.aiGen.template.desc":     "Genera plantillas de aplicacion listas para produccion con seguimiento de versiones y validacion de compatibilidad. No codigo base. Puntos de partida completamente conectados.",
  "cap.aiGen.template.badge":    "IA de plantillas",
  "cap.aiGen.marketplace.name":  "Inteligencia de Mercado",
  "cap.aiGen.marketplace.desc":  "Recomendaciones impulsadas por IA para listados, senales de precios y analisis de compatibilidad en el ecosistema del mercado de Nuvra.",
  "cap.aiGen.marketplace.badge": "IA de mercado",

  /* Capabilities - Deployment Pipeline */
  "cap.deploy.label":    "Pipeline de Despliegue",
  "cap.deploy.headline": "Desde codigo generado hasta URL de produccion en vivo, un pipeline completamente automatizado y auto-reparable",
  "cap.deploy.fullStack.name":    "Motor de Despliegue Full-Stack",
  "cap.deploy.fullStack.desc":    "Orquesta el empaquetado, la distribucion y la activacion de contenedores en una sola operacion atomica. Un boton. Un sistema en vivo completo.",
  "cap.deploy.fullStack.badge":   "Pipeline completo",
  "cap.deploy.stateMachine.name": "Maquina de Estados de Despliegue",
  "cap.deploy.stateMachine.desc": "Cada despliegue sigue una transicion ordenada estricta: en cola, construyendo, desplegando, en vivo. Los saltos de estado invalidos estan bloqueados. La reversion es automatica ante cualquier violacion.",
  "cap.deploy.stateMachine.badge": "Aplicacion de estado",
  "cap.deploy.healing.name":      "Reconciliador Auto-Reparable",
  "cap.deploy.healing.desc":      "Se ejecuta continuamente en segundo plano. Encuentra y repara construcciones bloqueadas y activaciones obsoletas sin intervencion humana.",
  "cap.deploy.healing.badge":     "Auto-reparable",
  "cap.deploy.repair.name":       "Motor de Reparacion de Despliegue",
  "cap.deploy.repair.desc":       "Cuando un despliegue falla, se aplican automaticamente estrategias de recuperacion estructuradas. La mayoria de los fallos se resuelven sin intervencion del usuario.",
  "cap.deploy.repair.badge":      "Auto-reparacion",
  "cap.deploy.atomic.name":       "Transacciones de Despliegue Atomicas",
  "cap.deploy.atomic.desc":       "Las operaciones de despliegue de multiples pasos se tratan como una sola transaccion. Si algun paso falla, todos los pasos anteriores se revierten. Sin despliegues parciales.",
  "cap.deploy.atomic.badge":      "Atomico",
  "cap.deploy.cdn.name":          "Capa de Activacion CDN",
  "cap.deploy.cdn.desc":          "Gestiona el momento en que una construccion entra en vivo: cambio de DNS, propagacion global y verificacion de salud antes de que se enrute el trafico.",
  "cap.deploy.cdn.badge":         "Activacion CDN",
  "cap.deploy.governance.name":   "Gobernanza de Despliegue",
  "cap.deploy.governance.desc":   "La aplicacion de politicas valida cada despliegue contra los limites del plan, las reglas del proyecto y las restricciones de recursos antes de que comience la ejecucion.",
  "cap.deploy.governance.badge":  "Politica",
  "cap.deploy.retryLedger.name":  "Registro de Reintentos Duradero",
  "cap.deploy.retryLedger.desc":  "Cada intento de reintento se registra de forma duradera. Previene bucles infinitos, rastrea patrones de fallo entre reinicios y permite analisis post-incidente.",
  "cap.deploy.retryLedger.badge": "Reintentos duraderos",
  "cap.deploy.garbage.name":      "Recoleccion de Basura de Artefactos",
  "cap.deploy.garbage.desc":      "Los artefactos de construccion obsoletos se limpian automaticamente en un horario. Los costos de almacenamiento no aumentan a medida que iteras.",
  "cap.deploy.garbage.badge":     "Control de costos",

  /* Capabilities - Mobile Builds */
  "cap.mobile.label":    "Construcciones Moviles",
  "cap.mobile.headline": "Construcciones nativas de Android e iOS, toolchain real, firma real, artefactos reales",
  "cap.mobile.aab.name":        "Android App Bundle",
  "cap.mobile.aab.desc":        "Pipeline de construccion Android completo que produce un bundle firmado y optimizado. Verificado antes de la entrega. Listo para subir a Play Store.",
  "cap.mobile.aab.badge":       "Android",
  "cap.mobile.apk.name":        "Constructor de APK",
  "cap.mobile.apk.desc":        "APK firmado con optimizacion de alineacion y verificacion de firma. Artefactos listos para instalar directamente.",
  "cap.mobile.apk.badge":       "Android",
  "cap.mobile.ios.name":        "Construccion iOS",
  "cap.mobile.ios.desc":        "Cadena de construccion de archivo completo que produce paquetes iOS distribuibles. Se ejecuta en hosts de construccion macOS con deteccion precisa del entorno.",
  "cap.mobile.ios.badge":       "iOS",
  "cap.mobile.pwa.name":        "Progressive Web App a Nativo",
  "cap.mobile.pwa.desc":        "Envuelve Progressive Web Apps en paquetes distribuibles nativos de Play Store. Puente completo PWA a nativo.",
  "cap.mobile.pwa.badge":       "PWA a Nativo",
  "cap.mobile.concurrent.name": "Pool de Trabajadores de Construccion Concurrente",
  "cap.mobile.concurrent.desc": "Multiples construcciones moviles se ejecutan en paralelo con aplicacion de tiempo de espera por trabajo y gestion del ciclo de vida de artefactos. Sin cuellos de botella en la cola.",
  "cap.mobile.concurrent.badge": "Construcciones concurrentes",
  "cap.mobile.sdk.name":        "Aprovisionador Automatico de SDK",
  "cap.mobile.sdk.desc":        "Detecta componentes SDK faltantes y los aprovisiona automaticamente. Informa el componente exacto que falta en lugar de un fallo generico.",
  "cap.mobile.sdk.badge":       "Toolchain",
  "cap.mobile.governor.name":   "Gobernador de Tiempo de Ejecucion de Construccion",
  "cap.mobile.governor.desc":   "Gestiona el ciclo de vida del daemon de construccion, la configuracion de memoria y el cache de construccion incremental para un rendimiento de construccion consistente.",
  "cap.mobile.governor.badge":  "Tiempo de ejecucion",
  "cap.mobile.signing.name":    "Gestion de Claves de Firma",
  "cap.mobile.signing.desc":    "Registro completo de keystore con huella digital, seguimiento de vencimiento y verificacion. Las claves de firma se gobiernan, no solo se almacenan.",
  "cap.mobile.signing.badge":   "Firma de codigo",
  "cap.mobile.icons.name":      "Generador de Iconos y Recursos",
  "cap.mobile.icons.desc":      "Genera recursos de iconos de aplicacion en cada densidad y formato requeridos para Android e iOS en una sola operacion.",
  "cap.mobile.icons.badge":     "Generacion de recursos",
  "cap.mobile.manifest.name":   "Generador de Manifiestos",
  "cap.mobile.manifest.desc":   "Genera manifiestos de configuracion de Android e iOS a partir de la configuracion del proyecto. No se requiere edicion manual.",
  "cap.mobile.manifest.badge":  "Auto-manifiesto",
  "cap.mobile.telemetry.name":  "Telemetria de Construcciones Moviles",
  "cap.mobile.telemetry.desc":  "Cada evento del ciclo de vida de construccion se registra: inicio, finalizacion, fallo, detalles del artefacto. Historial completo de construccion visible en tu panel.",
  "cap.mobile.telemetry.badge": "Telemetria",

  /* Capabilities - Store Publishing */
  "cap.store.label":    "Publicacion en Tiendas",
  "cap.store.headline": "Envio a tiendas autenticado y gobernado con firma real y rastros de auditoria",
  "cap.store.google.name":       "Integracion con Google Play Console",
  "cap.store.google.desc":       "Pipeline completo de API de Play Store: crear sesion, subir bundle, actualizar track, confirmar. Autenticado contra tu cuenta de servicio.",
  "cap.store.google.badge":      "Play Store",
  "cap.store.appstore.name":     "Integracion con App Store Connect",
  "cap.store.appstore.desc":     "Pipeline completo de API de App Store Connect con cadena de subida autenticada para distribucion en produccion.",
  "cap.store.appstore.badge":    "App Store",
  "cap.store.staged.name":       "Gestor de Lanzamiento por Fases",
  "cap.store.staged.desc":       "Aplicacion de lanzamiento por fases con puertas de tiempo configurables entre cada etapa antes de la promocion. Sin lanzamientos completos accidentales.",
  "cap.store.staged.badge":      "Lanzamiento controlado",
  "cap.store.vault.name":        "Boveda de Credenciales Cifradas",
  "cap.store.vault.desc":        "Las credenciales de la tienda se cifran en reposo. El descifrado ocurre solo del lado del servidor. Las credenciales en texto plano nunca tocan la base de datos.",
  "cap.store.vault.badge":       "Cifrado",
  "cap.store.release.name":      "Orquestador de Lanzamiento",
  "cap.store.release.desc":      "Cada lanzamiento pasa por etapas validadas con tiempo por etapa: verificacion de artefactos, registro de procedencia, validacion de credenciales, envio a tienda y registro de auditoria.",
  "cap.store.release.badge":     "Pipeline gobernado",
  "cap.store.rollback.name":     "Reversion Verificada",
  "cap.store.rollback.desc":     "Antes de iniciar una reversion, el sistema recalcula y verifica la integridad del artefacto. Los artefactos corruptos estan bloqueados. No puedes revertir accidentalmente a una mala construccion.",
  "cap.store.rollback.badge":    "Reversion verificada",
  "cap.store.keyGov.name":       "Gobernanza de Claves de Firma",
  "cap.store.keyGov.desc":       "Las claves de firma son validadas, monitoreadas y gobernadas. Advierte con suficiente anticipacion sobre el vencimiento. Bloquea ante desajuste de certificados.",
  "cap.store.keyGov.badge":      "Gobernanza de claves",
  "cap.store.provenance.name":   "Procedencia de Artefactos",
  "cap.store.provenance.desc":   "Se calcula un hash a prueba de manipulaciones en el momento de la construccion y se almacena con el artefacto. En el momento del lanzamiento, el hash se verifica. La deteccion de manipulaciones es automatica.",
  "cap.store.provenance.badge":  "A prueba de manipulaciones",
  "cap.store.manifestZip.name":  "Exportador ZIP con Manifiesto",
  "cap.store.manifestZip.desc":  "Crea archivos ZIP con registros de integridad por entrada. Cada archivo del archivo se verifica de forma independiente.",
  "cap.store.manifestZip.badge": "ZIP con manifiesto",

  /* Capabilities - Execution Infrastructure */
  "cap.exec.label":    "Infraestructura de Ejecucion",
  "cap.exec.headline": "Ejecucion de trabajos asincrona, duradera y limitada en memoria con degradacion controlada",
  "cap.exec.renderer.name":        "Renderizador de Medios Aislado",
  "cap.exec.renderer.desc":        "Renderizado de navegador aislado para capturas de pantalla y generacion de medios. Multiples intentos de reintento con retroceso exponencial. Memoria limitada por contexto.",
  "cap.exec.renderer.badge":       "Sandboxed",
  "cap.exec.asyncQueue.name":      "Cola de Procesamiento Asincrono",
  "cap.exec.asyncQueue.desc":      "Generacion de audio y medios asincrona a traves de un sistema de cola durable. Degrada graciosamente a ejecucion sincrona cuando el backend de la cola no esta disponible.",
  "cap.exec.asyncQueue.badge":     "Cola asincrona",
  "cap.exec.aiWorker.name":        "Trabajador de Ejecucion de Construccion IA",
  "cap.exec.aiWorker.desc":        "Ejecucion de generacion de codigo IA asincrona con concurrencia limitada en memoria. Escala con los recursos del contenedor disponibles.",
  "cap.exec.aiWorker.badge":       "Cola de ejecucion",
  "cap.exec.faultIsolation.name":  "Aislamiento de Fallos de Conexion",
  "cap.exec.faultIsolation.desc":  "Despues de fallos repetidos, el sistema pausa y reintenta en un temporizador. Previene fallos en cascada por una interrupcion temporal del backend.",
  "cap.exec.faultIsolation.badge": "Aislamiento de fallos",
  "cap.exec.heartbeat.name":       "Latido del Trabajador",
  "cap.exec.heartbeat.desc":       "Los trabajadores registran el estado de salud periodicamente. Los trabajadores muertos se detectan automaticamente, permitiendo el rebalanceo de trabajos sin intervencion manual.",
  "cap.exec.heartbeat.badge":      "Seguimiento de salud",
  "cap.exec.retryScheduler.name":  "Planificador de Reintentos Duradero",
  "cap.exec.retryScheduler.desc":  "Las operaciones fallidas se vuelven a poner en cola en un horario en lugar de descartarse. El estado de reintento sobrevive a los reinicios del servidor.",
  "cap.exec.retryScheduler.badge": "Durable",
  "cap.exec.retryConsumer.name":   "Consumidor de Reintentos",
  "cap.exec.retryConsumer.desc":   "Procesa trabajos de reintento con retroceso configurable, registra resultados y escala despues del maximo de intentos.",
  "cap.exec.retryConsumer.badge":  "Retroceso",
  "cap.exec.eventBridge.name":     "Puente de Eventos entre Servicios",
  "cap.exec.eventBridge.desc":     "Enruta eventos entre conexiones en tiempo real, suscripciones de bases de datos y colas de procesamiento. Eventos de construccion, cambios de estado de despliegue y alertas fluyen a traves de un solo hub.",
  "cap.exec.eventBridge.badge":    "Enrutamiento de eventos",

  /* Capabilities - Observability */
  "cap.observ.label":    "Observabilidad",
  "cap.observ.headline": "Visibilidad en tiempo real en cada capa de la plataforma, cero metricas fabricadas",
  "cap.observ.bootValidator.name":    "Validador de Infraestructura en el Arranque",
  "cap.observ.bootValidator.desc":    "Valida que todos los servicios backend requeridos son accesibles antes de aceptar trafico. Falla de forma segura si faltan servicios criticos.",
  "cap.observ.bootValidator.badge":   "Seguridad de arranque",
  "cap.observ.releaseTelemetry.name": "Telemetria de Eventos de Lanzamiento",
  "cap.observ.releaseTelemetry.desc": "Cada evento del pipeline de lanzamiento se escribe como un registro de solo adicion. La capa de telemetria nunca lanza excepciones ni interrumpe el pipeline que observa.",
  "cap.observ.releaseTelemetry.badge": "Solo adicion",
  "cap.observ.auditLog.name":         "Registro de Auditoria Inmutable",
  "cap.observ.auditLog.desc":         "Cada decision de lanzamiento, acceso a credenciales y envio a tienda se escribe en un registro de cumplimiento. Los registros nunca se modifican ni se eliminan.",
  "cap.observ.auditLog.badge":        "Cumplimiento",
  "cap.observ.platformStatus.name":   "Servicio de Estado de la Plataforma",
  "cap.observ.platformStatus.desc":   "Sondeo de salud en tiempo real en todos los servicios con deteccion de incidentes, callbacks de suscriptores para cambios de estado y estado persistente.",
  "cap.observ.platformStatus.badge":  "Estado en vivo",
  "cap.observ.buildLog.name":         "Registro de Eventos de Construccion Movil",
  "cap.observ.buildLog.desc":         "Registro completo de eventos del ciclo de vida para cada trabajo de construccion movil: tiempo de inicio, duracion, detalles del artefacto, razones de fallo e historial de reintentos.",
  "cap.observ.buildLog.badge":        "Telemetria de construccion",
  "cap.observ.runtimeValidator.name": "Validador de Ejecucion en Tiempo de Ejecucion",
  "cap.observ.runtimeValidator.desc": "Valida la salud del nodo de ejecucion y la integridad de la salida durante las ejecuciones activas del pipeline de construccion. Detecta salidas corruptas antes de que se propaguen.",
  "cap.observ.runtimeValidator.badge": "En pipeline",
  "cap.observ.integrity.name":        "Verificador de Integridad de Artefactos",
  "cap.observ.integrity.desc":        "Verifica independientemente las firmas y sumas de comprobacion de artefactos de construccion movil antes de cualquier envio de lanzamiento. Una verificacion separada del proceso de construccion en si.",
  "cap.observ.integrity.badge":       "Integridad",
  "cap.observ.metrics.name":          "Endpoint de Metricas del Sistema",
  "cap.observ.metrics.desc":          "Expone profundidades de cola, conteos de trabajos activos, uso de memoria y estado de conexion. Visibilidad de infraestructura en tiempo real para operadores de la plataforma.",
  "cap.observ.metrics.badge":         "Metricas",

  /* Capabilities - Security */
  "cap.security.label":    "Seguridad",
  "cap.security.headline": "Defensa en profundidad en cada capa: cifrado, aislamiento, control de acceso, rastros de auditoria",
  "cap.security.sandbox.name":     "Sandbox de Renderizado Aislado",
  "cap.security.sandbox.desc":     "Cada renderizado del navegador se ejecuta en un contexto aislado dedicado con proteccion de red, aplicacion de seguridad de contenido y memoria limitada por renderizado.",
  "cap.security.sandbox.badge":    "Sandboxed",
  "cap.security.vault.name":       "Boveda de Credenciales Cifradas",
  "cap.security.vault.desc":       "Las credenciales de la tienda se cifran en reposo con cifrado fuerte. Solo se descifran dentro del proceso del servidor. La clave nunca aparece en registros, respuestas o codigo del cliente.",
  "cap.security.vault.badge":      "Cifrado de campo",
  "cap.security.adminGate.name":   "Puerta de Administrador Segura al Tiempo",
  "cap.security.adminGate.desc":   "La comparacion de claves de API de administrador es inmune a ataques de tiempo. Falla de forma cerrada si la variable de entorno de la clave de administrador no esta configurada.",
  "cap.security.adminGate.badge":  "Seguro al tiempo",
  "cap.security.authConn.name":    "Conexiones en Tiempo Real Autenticadas",
  "cap.security.authConn.desc":    "Cada conexion en tiempo real autentica un token de sesion firmado antes de que se conceda acceso a la sala. Cualquier error de autenticacion cierra la conexion. Sin autenticacion parcial.",
  "cap.security.authConn.badge":   "Restringido por token",
  "cap.security.rateLimit.name":   "Limitacion de Tasa de API",
  "cap.security.rateLimit.desc":   "Las solicitudes entrantes se limitan por direccion IP. Se aplica antes de que comience el procesamiento de la solicitud.",
  "cap.security.rateLimit.badge":  "Limitado por tasa",
  "cap.security.auditSvc.name":    "Servicio de Auditoria de Seguridad",
  "cap.security.auditSvc.desc":    "Los eventos significativos de seguridad se escriben en un registro de auditoria dedicado para forense post-incidente e informes de cumplimiento.",
  "cap.security.auditSvc.badge":   "Forense",
  "cap.security.fieldEnc.name":    "Cifrado a Nivel de Campo",
  "cap.security.fieldEnc.desc":    "Utilidades de cifrado a nivel de campo de proposito general para cualquier dato sensible almacenado en reposo, mas alla de las credenciales de la tienda.",
  "cap.security.fieldEnc.badge":   "En reposo",
  "cap.security.roomAuth.name":    "Autorizacion de Sala de Proyecto",
  "cap.security.roomAuth.desc":    "Las uniones de sala de proyecto en tiempo real requieren verificacion de propiedad a traves de busqueda en la base de datos. El formato de entrada se valida antes de que se ejecute cualquier consulta.",
  "cap.security.roomAuth.badge":   "Verificacion de propiedad",

  /* Capabilities - Real-time Infrastructure */
  "cap.realtime.label":    "Infraestructura en Tiempo Real",
  "cap.realtime.headline": "Entrega de eventos en vivo en cada superficie: construcciones, despliegues, salud del sistema",
  "cap.realtime.server.name":    "Servidor en Tiempo Real Autenticado",
  "cap.realtime.server.desc":    "Entrega de eventos en vivo con salas por usuario y por proyecto autenticadas por token. Los intervalos de keep-alive mantienen la estabilidad de la conexion.",
  "cap.realtime.server.badge":   "Tiempo real",
  "cap.realtime.logStream.name": "Streaming en Vivo de Registros de Construccion",
  "cap.realtime.logStream.desc": "El canal WebSocket raw transmite registros de construccion en tiempo real a medida que se generan. Baja latencia, separado de la conexion de eventos principal.",
  "cap.realtime.logStream.badge": "Streaming de registros",
  "cap.realtime.dbSubs.name":    "Suscripciones a Cambios de Base de Datos",
  "cap.realtime.dbSubs.desc":    "Las suscripciones a cambios de base de datos en el historial de construccion y perfiles de usuario impulsan actualizaciones del panel en vivo sin sondeo.",
  "cap.realtime.dbSubs.badge":   "DB tiempo real",
  "cap.realtime.hook.name":      "Hook de Observabilidad",
  "cap.realtime.hook.desc":      "Hook del lado del cliente que se suscribe a eventos de salud de infraestructura en vivo y los muestra a los componentes del panel en tiempo real.",
  "cap.realtime.hook.badge":     "Hook de cliente",

  /* Capabilities - Game Platform */
  "cap.game.label":    "Plataforma de Juegos",
  "cap.game.headline": "Generacion de SDK, configuracion de emparejamiento y andamiaje de economia para backends de juegos",
  "cap.game.gameSdk.name":     "Exportacion de SDK para Motor de Juegos",
  "cap.game.gameSdk.desc":     "Genera codigo de integracion SDK listo para produccion para Unity, Unreal, Godot y motores web apuntando a tu backend desplegado.",
  "cap.game.gameSdk.badge":    "Exportacion SDK",
  "cap.game.multiplayer.name": "Plantillas de Infraestructura Multijugador",
  "cap.game.multiplayer.desc": "Algoritmos de emparejamiento, gestion de salas, soporte de espectadores y manejo de reconexion generados como configuracion backend conectable, no codificado.",
  "cap.game.multiplayer.badge": "Multijugador",
  "cap.game.economy.name":     "Sistema de Economia del Juego",
  "cap.game.economy.desc":     "Moneda virtual, catalogo de articulos, tablas de drops y andamiaje de inventario del jugador. Las definiciones de economia se generan y pueden desplegarse en tu backend de juego.",
  "cap.game.economy.badge":    "Economia",
  "cap.game.analytics.name":   "Motor de Analitica de Juegos",
  "cap.game.analytics.desc":   "Seguimiento del comportamiento del jugador, analitica de sesion, metricas de embudo y cohortes de retencion generadas como backend de analitica conectable para tu juego.",
  "cap.game.analytics.badge":  "Analitica",
  "cap.game.aiBalance.name":   "Motor de Equilibrio IA",
  "cap.game.aiBalance.desc":   "Recomendaciones de equilibrio del juego basadas en analisis de tasa de victorias, flujo de economia y datos de retencion. Alimenta la configuracion de vuelta a los sistemas de economia y emparejamiento.",
  "cap.game.aiBalance.badge":  "IA de equilibrio",
  "cap.game.scale.name":       "Panel de Configuracion de Escalabilidad",
  "cap.game.scale.desc":       "Configuracion de escalado de servidor para despliegues de juegos: objetivos de capacidad de jugadores, distribucion de regiones, umbrales de auto-escalado y politicas de distribucion de carga.",
  "cap.game.scale.badge":      "Config de escala",

  /* Capabilities - Releases */
  "cap.release.storeRelease.title":  "Infraestructura de Lanzamiento y Gobernanza de Tiendas",
  "cap.release.storeRelease.desc":   "Publicacion de extremo a extremo en tiendas con boveda de credenciales cifradas, adaptadores de tienda autenticados, pipeline de lanzamiento gobernado y registro de auditoria inmutable.",
  "cap.release.storeRelease.tag":    "Lanzamiento",
  "cap.release.mobileRelease.title": "Sistema de Construccion Movil",
  "cap.release.mobileRelease.desc":  "Trabajadores de construccion nativos de Android e iOS con deteccion real de toolchain, gobernador de tiempo de ejecucion de construccion, infraestructura de firma, pool de trabajadores concurrente y telemetria de construccion completa.",
  "cap.release.mobileRelease.tag":   "Movil",
  "cap.release.asyncRelease.title":  "Infraestructura de Ejecucion Asincrona",
  "cap.release.asyncRelease.desc":   "Cola de ejecucion IA asincrona, planificador de reintentos duradero, monitoreo de latido de trabajadores, aislamiento de fallos de conexion y puente de eventos entre servicios.",
  "cap.release.asyncRelease.tag":    "Ejecucion",
  "cap.release.mediaRelease.title":  "Sandbox de Medios y Renderizado",
  "cap.release.mediaRelease.desc":   "Sandbox de renderizado aislado con proteccion de red, aplicacion de seguridad de contenido y memoria limitada por renderizado.",
  "cap.release.mediaRelease.tag":    "Medios",
  "cap.release.deployRelease.title": "Auto-Reparacion de Despliegue",
  "cap.release.deployRelease.desc":  "Maquina de estados de despliegue con aplicacion de transicion ordenada, reconciliador en segundo plano, motor de reparacion, transacciones atomicas e infraestructura de reintentos duradera.",
  "cap.release.deployRelease.tag":   "Despliegue",
  "cap.release.coreRelease.title":   "Generacion IA Central y Autenticacion",
  "cap.release.coreRelease.desc":    "Generacion de codigo multi-agente, enrutamiento IA redundante, sistemas de consenso y arbitraje de agentes, autenticacion e infraestructura en tiempo real.",
  "cap.release.coreRelease.tag":     "Fundacion",

  /* LandingPage - Navbar */
  "landing.nav.features":         "Funciones",
  "landing.nav.howItWorks":       "Como Funciona",
  "landing.nav.useCases":         "Casos de Uso",
  "landing.nav.tryIt":            "Planificador",
  "landing.nav.architecture":     "Arquitectura",
  "landing.nav.pricing":     "Precios",
  "landing.nav.signIn":           "Iniciar sesion",

  /* LandingPage - Hero */
  "landing.hero.badge":           "Plataforma Autonoma de Ingenieria de Software",
  "landing.hero.h1a":             "Describelo.",
  "landing.hero.h1b":             "Nuvra lo construye y lo despliega.",
   "landing.hero.desc":            "Nuvra es una plataforma autonoma de ingenieria de software: disena la arquitectura, construye el codigo, valida el sistema y lo lleva por un despliegue gobernado desde una sola conversacion.",
  "landing.hero.ctaPrimary":      "Empezar a Construir",
  "landing.hero.ctaSecondary":    "Verlo en Vivo",

  "landing.hero.pipelineLabel":  "Cómo ejecuta Nuvra",
  "landing.hero.pipelineDesc":   "Una vista concisa del pipeline de software autónomo.",
  "landing.hero.pipelineStatus": "Pipeline activo",
  /* LandingPage - Product Explanation */
  "landing.explain.label":        "Que hace Nuvra",
  "landing.explain.h2":           "No asistencia. Ejecucion.",
  "landing.explain.desc":         "Describe lo que quieres construir. Nuvra genera el sistema completo y lo despliega, sin pasos manuales.",

  "landing.hero.proof.architecture": "Arquitectura primero",
  "landing.hero.proof.code":         "Codigo real",
  "landing.hero.proof.validation":   "Validacion automatizada",
  "landing.hero.proof.control":      "Control de produccion",

  "landing.capabilities.label": "Mas alla del primer build",
  "landing.capabilities.h2": "La fabrica sigue trabajando despues del prompt.",
  "landing.capabilities.desc": "Nuvra es mas que generacion. La plataforma lleva el producto a traves de builds moviles, publicacion, despliegue, memoria y trabajo de ingenieria conectado.",
  "landing.capability.mobile.title": "Builds moviles",
  "landing.capability.mobile.desc": "Genera la salida de la aplicacion movil junto al producto web, con artefactos reales y rutas de build.",
  "landing.capability.publishing.title": "Preparacion para tiendas",
  "landing.capability.publishing.desc": "Prepara el producto para distribucion con metadatos, documentos de politicas, capturas y comprobaciones de preparacion.",
  "landing.capability.externalDeploy.title": "Opciones de despliegue",
  "landing.capability.externalDeploy.desc": "Pasa del preview a destinos de despliegue compatibles sin reconstruir el producto alrededor de otro stack.",
  "landing.capability.workspace.title": "Workspace de ingenieria",
  "landing.capability.workspace.desc": "Requisitos, arquitectura, work orders, builds, entornos, despliegues e integraciones permanecen en un solo lugar.",
  "landing.capability.memory.title": "Memoria y procedencia",
  "landing.capability.memory.desc": "El contexto del proyecto, el linaje, la procedencia y el historial de ingenieria mantienen conectado el trabajo futuro con lo anterior.",
  "landing.capability.integrations.title": "Servicios conectados",
  "landing.capability.integrations.desc": "Usa integraciones gobernadas y credenciales almacenadas para conectar la fabrica de software con los servicios que necesita tu producto.",

  /* LandingPage - Stats */
  "landing.live.builders":             "Creadores",
  "landing.live.projects":             "Proyectos creados",
  "landing.live.builds":               "Apps entregadas",
  "landing.live.deployments":          "Despliegues",
  "landing.proof.label":                "Actividad de la plataforma",
  "landing.proof.desc":                 "Medida a partir de la telemetria publica de Nuvra.",
  "landing.lifecycle.label":            "Ciclo de vida de Nuvra",
  "landing.lifecycle.summary":          "Arquitectura → validacion → despliegue",
  "landing.lifecycle.note":             "Flujo del producto mostrado como ilustracion. El planificador interactivo es la experiencia real respaldada por el servidor.",
  "landing.lifecycle.intent":            "Intencion",
  "landing.lifecycle.architecture":      "Arquitectura",
  "landing.lifecycle.database":          "Base de datos",
  "landing.lifecycle.backend":           "Backend",
  "landing.lifecycle.frontend":          "Frontend",
  "landing.lifecycle.validation":        "Validacion",
  "landing.lifecycle.deployment":        "Despliegue",
  "landing.lifecycle.production":        "Produccion",
  "landing.stat.architecture.value": "Autonoma",
  "landing.stat.architecture.label": "Arquitectura",
  "landing.stat.code.value":         "Generado",
  "landing.stat.code.label":         "Codigo",
  "landing.stat.deployment.value":   "Automatizado",
  "landing.stat.deployment.label":   "Despliegue",
  "landing.stat.delivery.value":     "Produccion",
  "landing.stat.delivery.label":     "Entrega",

  /* LandingPage - Chips */
  "landing.chip.saas":            "SaaS",
  "landing.chip.fintech":         "Fintech",
  "landing.chip.market":          "Mercado",
  "landing.chip.mobileApp":       "App Movil",
  "landing.chip.enterprise":      "Empresarial",
  "landing.chip.aiTool":          "Herramienta IA",
  "landing.chip.gamePlatform":    "Plataforma de Juegos",
  "landing.chip.dashboard":       "Panel de Control",

  /* LandingPage - How It Works */
  "landing.howItWorks.label":     "Pipeline de Construccion Autonomo",
  "landing.howItWorks.h2":        "Una descripcion. Sistema completo.",
  "landing.howItWorks.desc":      "Cada etapa se ejecuta sin ti. Arquitectura, codigo, validacion y despliegue son completamente automatizados.",
  "landing.pipeline.sequential.label": "Ejecucion secuencial por etapas",
  "landing.pipeline.sequential.desc":  "Cada etapa valida antes de que comience la siguiente, para que una compilacion nunca avance sobre trabajo no verificado.",
  "landing.pipeline.repair.label":     "Reparacion autonoma",
  "landing.pipeline.repair.desc":      "Los fallos de validacion desencadenan autocuracion especifica.",
  "landing.pipeline.zerotouch.label":  "Despliegue sin intervencion",
  "landing.pipeline.zerotouch.desc":   "De vista previa a produccion en una sola operacion orquestada.",

  /* LandingPage - Try It */
  "landing.tryIt.label":          "Planificador de Arquitectura",
  "lpp.panelLabel":               "Planificador de arquitectura de Nuvra",
  "landing.tryIt.h2":             "Mira como Nuvra Planifica tu App",
  "landing.tryIt.desc":           "Escribe un mensaje y Nuvra mapea la arquitectura real: entidades, esquema de base de datos, APIs y control de acceso. Registrate para generar la construccion completa y funcional.",

  /* LandingPage - System Scale */
  "landing.scale.label":          "Generacion Full-Stack",
  "landing.scale.h2":             "Cada Capa. Generada de Forma Autonoma.",
  "landing.scale.desc":           "Nuvra genera y despliega tu aplicacion en una plataforma completa: UI, diseno de esquema, pruebas y despliegue, todo automatizado.",
  "landing.layer.frontend":       "Apps Frontend",
  "landing.layer.backend":        "Servicios Backend",
  "landing.layer.database":       "Bases de Datos",
  "landing.layer.infra":          "Infraestructura",
  "landing.layer.analytics":      "Analitica",
  "landing.layer.automation":     "Automatizacion",

  /* LandingPage - Use Cases */
  "landing.useCases.label":       "Casos de Uso",
  "landing.useCases.h2":          "Construye Cualquier Cosa",
  "landing.useCases.exploreTemplate": "Ver lo que construye Nuvra",
  "landing.useCase.saas.title":   "Plataformas SaaS",
  "landing.useCase.saas.desc":    "Apps multi-tenant con autenticacion, facturacion y paneles",
  "landing.useCase.market.title": "Mercados",
  "landing.useCase.market.desc":  "Plataformas comprador-vendedor con pagos y busqueda",
  "landing.useCase.fintech.title": "Apps Fintech",
  "landing.useCase.fintech.desc": "Herramientas financieras con transacciones seguras",
  "landing.useCase.health.title": "Salud",
  "landing.useCase.health.desc":  "Portales de pacientes, citas y apps de gestion de salud",
  "landing.useCase.gov.title":    "Portales Gubernamentales",
  "landing.useCase.gov.desc":     "Plataformas de servicio ciudadano a escala",
  "landing.useCase.mobile.title": "Apps Moviles",
  "landing.useCase.mobile.desc":  "Backends moviles multiplataforma y APIs",
  "landing.useCase.game.title":   "Juegos 3D",
  "landing.useCase.game.desc":    "Mundos 3D jugables con fisica, escenas y progreso guardado",
  "landing.useCase.ai.title":     "Paneles de IA",
  "landing.useCase.ai.desc":      "Visualizacion de analitica e inteligencia",

  /* LandingPage - Features */
  "landing.features.label":       "Capacidades de la Fabrica",
  "landing.features.h2":          "Como Opera la Fabrica",
  "landing.features.learnMore":   "Saber mas",
  "landing.feature.intent.title":  "Motor de Intencion",
  "landing.feature.intent.desc":   "Describelo en lenguaje natural. Nuvra resuelve tu intencion en un plan de sistema completo antes de que nada se ejecute.",
  "landing.feature.pipeline.title": "Pipeline de Construccion Autonomo",
  "landing.feature.pipeline.desc":  "Cada capa de tu app se construye de forma autonoma a traves de un pipeline secuencial: analisis de intencion, generacion de codigo, escaneo de seguridad, pruebas y despliegue.",
  "landing.feature.preview.title":  "Vista Previa en Vivo",
  "landing.feature.preview.desc":   "Cada construccion obtiene un entorno de vista previa aislado. Velo ejecutarse e interactua con el antes de desplegarlo.",
  "landing.feature.deploy.title":   "Despliegue con Un Clic",
  "landing.feature.deploy.desc":    "De construccion a URL de produccion en vivo en una sola operacion. Sin configuracion, sin configuracion de CI.",
  "landing.feature.healing.title":  "Reparacion Automatica",
  "landing.feature.healing.desc":   "Los fallos recurrentes se detectan automaticamente y se convierten en propuestas de correccion que apruebas con un clic. Nada se parchea sin tu conocimiento.",
  "landing.feature.monitor.title":  "Monitoreo de Construcciones",
  "landing.feature.monitor.desc":   "Las tasas de exito, la latencia y el estado de despliegue se rastrean en tiempo real para que siempre sepas que se desplegado.",

  /* LandingPage - Enterprise */
  "landing.enterprise.label":     "Listo para Empresas",
  "landing.enterprise.h2":        "Construido para Produccion",
  "landing.enterprise.hint":      "Haz clic para explorar cada capacidad",

  /* LandingPage - System Architecture */
  "landing.arch.label":           "Profundidad de la Plataforma",
  "landing.arch.h2":              "Grado produccion. Cada capa.",
  "landing.arch.desc":            "Las capacidades principales estan listas para produccion y activas.",
  "landing.arch.ctaAll":          "Explorar Todas las Capacidades",
  "landing.arch.ai.label":        "IA y Generacion",
  "landing.arch.ai.desc":         "Resolucion de intencion con IA, generacion de codigo por etapas y autoreparacion autonoma.",
  "landing.arch.deploy.label":    "Despliegue",
  "landing.arch.deploy.desc":     "Pipeline de construccion automatizado desde codigo hasta URL de produccion en vivo con verificacion de sondas de salud.",
  "landing.arch.mobile.label":    "Construcciones Moviles",
  "landing.arch.mobile.desc":     "Empaquetado nativo para Android e iOS con soporte de firma, listo para las tiendas.",
  "landing.arch.store.label":     "Publicacion en Tiendas",
  "landing.arch.store.desc":      "Listados listos para tiendas: politica de privacidad generada, terminos, descripciones, capturas de pantalla y verificaciones de preparacion.",
  "landing.arch.exec.label":      "Motor de Ejecucion",
  "landing.arch.exec.desc":       "Ejecucion de trabajos de alto rendimiento con programacion prioritaria, colas y metricas en vivo.",
  "landing.arch.observ.label":    "Observabilidad",
  "landing.arch.observ.desc":     "Telemetria de construccion, registros de auditoria, salud del sistema y seguimiento de incidentes integrados.",
  "landing.arch.security.label":  "Seguridad",
  "landing.arch.security.desc":   "Boveda de credenciales cifrada, proteccion de red, autenticacion segura y politica de contenido.",
  "landing.arch.realtime.label":  "Tiempo Real",
  "landing.arch.realtime.desc":   "Transmision de construcciones en vivo, actualizaciones de estado basadas en eventos y monitoreo del sistema en tiempo real.",

  /* LandingPage - Final CTA */
  "landing.cta.h2a":              "Describelo una vez.",
  "landing.cta.h2b":              "Despliega hoy.",
  "landing.cta.desc":             "Sin plantillas. Sin configuracion. Sin conexiones manuales. Nuvra genera, orquesta y despliega tu sistema de extremo a extremo.",
  "landing.cta.buildWithNuvra":   "Construir con Nuvra",

  /* LandingPage - Product-led proof, control plane, pricing and FAQ */
  "landing.nav.menu": "Abrir menu",
  "landing.nav.close": "Cerrar menu",
  "landing.difference.label": "La diferencia Nuvra",
  "landing.howItWorks.liveH2": "Mira el sistema en accion",
  "landing.principles.label": "Principios de ingenieria",
  "landing.principles.h2": "Construido como software, no como una demo.",
  "landing.principles.desc": "Nuvra convierte la intencion en trabajo de ingenieria estructurado y mantiene la ejecucion acotada, observable y verificable.",
  "landing.principle.intent.title": "Intencion antes del codigo",
  "landing.principle.intent.desc": "Los requisitos se resuelven en un contrato del sistema antes de comenzar la generacion.",
  "landing.principle.schema.title": "Ejecucion basada en esquemas",
  "landing.principle.schema.desc": "La arquitectura, los datos, los servicios y las interfaces se planifican como salidas estructuradas, no como codigo libre.",
  "landing.principle.verify.title": "Verificacion en el proceso",
  "landing.principle.verify.desc": "Las etapas de construccion se apoyan en validacion, reparacion y evidencia, no en una sola pasada de generacion.",
  "landing.principle.govern.title": "Autonomia gobernada",
  "landing.principle.govern.desc": "La ejecucion, los permisos, las acciones de infraestructura y los cambios de produccion permanecen dentro de controles explicitos.",
  "landing.control.label": "El plano de control de Nuvra",
  "landing.control.h2": "Tu fabrica de software tiene una cabina.",
  "landing.control.desc": "Requisitos, arquitectura, ordenes de trabajo, compilaciones, entornos, despliegues, memoria e integraciones viven en un solo espacio de ingenieria.",
  "landing.control.cta": "Abrir Nuvra",
  "landing.control.window": "Espacio de trabajo de ingenieria Nuvra",
  "landing.control.status": "Superficie operativa",
  "landing.control.note": "Estas son superficies reales del espacio de trabajo de Nuvra, no un panel decorativo.",
  "landing.control.surface.requirements.title": "Requisitos",
  "landing.control.surface.requirements.desc": "Contrato del producto",
  "landing.control.surface.architecture.title": "Arquitectura",
  "landing.control.surface.architecture.desc": "Diseno del sistema",
  "landing.control.surface.workOrders.title": "Ordenes de trabajo",
  "landing.control.surface.workOrders.desc": "Solicitudes de ingenieria permanentes",
  "landing.control.surface.builds.title": "Compilaciones",
  "landing.control.surface.builds.desc": "Historial de ejecucion",
  "landing.control.surface.environments.title": "Entornos",
  "landing.control.surface.environments.desc": "Destinos de ejecucion",
  "landing.control.surface.deployments.title": "Despliegues",
  "landing.control.surface.deployments.desc": "Estado de lanzamiento",
  "landing.control.surface.memory.title": "Memoria",
  "landing.control.surface.memory.desc": "Contexto del proyecto",
  "landing.control.surface.integrations.title": "Integraciones",
  "landing.control.surface.integrations.desc": "Servicios conectados",
  "landing.pricing.label": "Planes",
  "landing.pricing.h2": "Empieza pequeno. Escala la fabrica.",
  "landing.pricing.desc": "Elige el nivel de control de ingenieria que se adapte al software que estas construyendo. Consulta la matriz completa de funciones y los limites actuales en precios.",
  "landing.pricing.popular": "Popular",
  "landing.pricing.custom": "Personalizado",
  "landing.pricing.month": "/ mes",
  "landing.pricing.noMonthly": "Unico / incluido",
  "landing.pricing.cta": "Ver precios completos",
  "landing.faq.label": "Preguntas",
  "landing.faq.h2": "Antes de construir",
  "landing.faq.desc": "Las respuestas importantes, sin niebla de marketing.",
  "landing.faq.what.q": "Es Nuvra solo un chatbot de programacion con IA?",
  "landing.faq.what.a": "No. Nuvra esta disenado como una plataforma autonoma de ingenieria de software: primero resuelve la intencion y los requisitos, planifica la arquitectura, ejecuta por etapas y lleva la verificacion y el despliegue al flujo.",
  "landing.faq.real.q": "El planificador de arquitectura esta realmente activo?",
  "landing.faq.real.a": "Si. El planificador de esta pagina llama al servicio publico de vista previa de arquitectura de Nuvra y devuelve una arquitectura real derivada de tu prompt. No requiere una cuenta para la vista previa.",
  "landing.faq.control.q": "Cuanto control conservo?",
  "landing.faq.control.a": "Nuvra expone requisitos, arquitectura, ordenes de trabajo, compilaciones, entornos, despliegues, memoria e integraciones para que puedas inspeccionar y gobernar el proceso de ingenieria en lugar de entregarlo a una caja negra.",
  "landing.faq.deploy.q": "Puede Nuvra desplegar software?",
  "landing.faq.deploy.a": "Nuvra tiene capacidades de despliegue de produccion e integraciones con proveedores. Lo que puede desplegar depende de la arquitectura del proyecto, la infraestructura conectada, los permisos y el plan.",
  "landing.faq.ownership.q": "Soy propietario de lo que construye Nuvra?",
  "landing.faq.ownership.a": "Los planes publicos de Nuvra incluyen capacidades de propiedad y exportacion de codigo. Las opciones exactas dependen del plan; la pagina de precios muestra la matriz de derechos actual.",

  /* OrgAdminShell */
  "orgAdmin.noOrg":               "No se especifico ninguna organizacion.",
  "orgAdmin.verifying":           "Verificando acceso...",
  "orgAdmin.accessDeniedTitle":   "Acceso restringido",
  "orgAdmin.accessDeniedBody":    "Necesitas acceso de administrador u propietario de la organizacion para ver esta pagina.",
  "orgAdmin.title":               "Administracion de organizacion",
  "orgAdmin.subtitle":            "Gestiona politica de IA, miembros, SSO, cumplimiento, cuentas de servicio y configuracion de despliegue.",
  "orgAdmin.tab.overview":        "Resumen",
  "orgAdmin.tab.members":         "Miembros",
  "orgAdmin.tab.aiPolicy":        "Politica de IA",
  "orgAdmin.tab.audit":           "Registro de auditoria",
  "orgAdmin.tab.whiteLabel":      "Marca blanca",
  "orgAdmin.tab.ssoCompliance":   "SSO y cumplimiento",
  "orgAdmin.tab.serviceAccounts": "Cuentas de servicio",
  "orgAdmin.tab.sharedCredentials": "Credenciales compartidas",
  "orgAdmin.tab.webhooks":        "Webhooks",
  "orgAdmin.tab.deployment":      "Despliegue",

  /* VoiceNoteButton */
  "voice.micUnavailable":           "El microfono no esta disponible en este navegador.",
  "voice.notConfigured":            "La transcripcion de voz no esta configurada aun.",
  "voice.noCredits":                "Creditos insuficientes para la transcripcion.",
  "voice.transcribeFailed":         "No se pudo transcribir la nota.",
  "voice.micDenied":                "Permiso de microfono denegado.",
  "voice.stopRecording":            "Detener grabacion",
  "voice.transcribing":             "Transcribiendo",
  "voice.record":                   "Grabar nota de voz",

  /* NotificationPanel */
  "notif.title":                    "Notificaciones",
  "notif.markAllRead":              "Marcar todo como leido",
  "notif.empty":                    "Sin notificaciones aun",
  "notif.enablePush":               "Activar en este dispositivo",
  "notif.pushOn":                   "Push activado para este dispositivo",
  "notif.pushDenied":               "Notificaciones bloqueadas en los ajustes del navegador",
  "notif.pushEnabling":             "Activando...",

  /* UpgradeModal */
  "upgrade.maybeLater":             "Quizas mas tarde",
  "upgrade.feature.unlimitedBuilds":"Builds ilimitados",
  "upgrade.feature.aiAgents":       "Agentes IA",
  "upgrade.feature.priorityQueue":  "Cola prioritaria",
  "upgrade.feature.customDomains":  "Dominios personalizados",

  /* CapabilityStrip */
  "capStrip.title":                 "Capacidades de la plataforma",
  "capStrip.viewAll":               "Capacidades de la plataforma",
  "capStrip.aiGen.label":           "Generacion IA",
  "capStrip.aiGen.desc":            "Pipeline de generacion IA",
  "capStrip.webDeploy.label":       "Despliegue web",
  "capStrip.webDeploy.desc":        "Vista previa en vivo y URL de produccion",
  "capStrip.mobile.label":          "Builds moviles",
  "capStrip.mobile.desc":           "AAB, APK, IPA, TWA",
  "capStrip.store.label":           "Publicacion en stores",
  "capStrip.store.desc":            "Google Play y App Store",
  "capStrip.rollout.label":         "Despliegue gradual",
  "capStrip.rollout.desc":          "Despliegue por porcentaje controlado",
  "capStrip.repair.label":          "Reparacion IA",
  "capStrip.repair.desc":           "Recuperacion autonoma de builds",
  "capStrip.multiAi.label":         "Multi-IA",
  "capStrip.multiAi.desc":          "Cascada de 3 proveedores de IA",
  "capStrip.integrity.label":       "Integridad de artefactos",
  "capStrip.integrity.desc":        "SHA-256 y deteccion de alteraciones",
  "capStrip.realtime.label":        "Eventos en tiempo real",
  "capStrip.realtime.desc":         "Bus de eventos y colas de trabajos",
  "capStrip.rollback.label":        "Reversion",
  "capStrip.rollback.desc":         "Reversion verificada al build anterior",
  "capStrip.whatsNew.label":        "Novedades",
  "capStrip.whatsNew.desc":         "Ultimas versiones de la plataforma",

  /* LiveSystemStatus */
  "sys.runtimeHealth":              "Estado del sistema",
  "sys.live":                       "En vivo",
  "sys.status.operational":         "Operativo",
  "sys.status.degraded":            "Degradado",
  "sys.status.outage":              "Interrupcion",

  /* ConnectorCard */
  "connector.badge.enabled":        "Activado",
  "connector.badge.new":            "Nuevo",
  "connector.badge.popular":        "Popular",
  "connector.badge.required":       "Requerido por tu app",
  "connector.configure":            "Configurar",
  "connector.connect":              "Conectar",

  /* FactoryProgressPanel */
  "factory.title":                  "Fabrica de software",
  "factory.stage.planning":         "Analizando el prompt",
  "factory.stage.architecture":     "Disenando la arquitectura",
  "factory.stage.models":           "Generando modelos de datos",
  "factory.stage.workflows":        "Creando flujos de trabajo",
  "factory.stage.api":              "Configurando rutas de API",
  "factory.stage.components":       "Generando componentes de UI",
  "factory.stage.design":           "Creando el sistema de diseno",
  "factory.stage.deployment":       "Preparando el despliegue",
  "factory.stage.mobile":           "Preparando builds moviles",
  "factory.stage.materializing":    "Materializando archivos",
  "factory.stage.complete":         "Completado",
  "factory.stage.failed":           "Fallido",

  /* InteractiveProjectGrid */
  "projects.grid.title":            "Tus proyectos",
  "projects.grid.loading":          "Cargando tu trabajo de ingenieria...",
  "projects.grid.startNew":         "Iniciar un nuevo proyecto",
  "projects.grid.emptyHint":        "Describe tu idea arriba y tu equipo de ingenieria comenzara a construirla.",
  "projects.grid.inProgress":       "Ingenieria en progreso...",
  "projects.grid.lastActivity":     "Ultima actividad {t}",
  "projects.grid.notBuilt":         "Aun no construido",
  "projects.grid.continueBuild":    "Continuar construyendo",
  "projects.grid.runtime":          "Runtime",
  "projects.grid.openRuntime":      "Abrir el runtime en vivo",
  "projects.grid.status.building":  "Ingenieria",
  "projects.grid.status.live":      "En vivo",
  "projects.grid.status.error":     "Requiere atencion",
  "projects.grid.status.draft":     "Borrador",

  /* ThreatTelemetryRail */
  "threat.title":                   "Telemetria de amenazas",
  "threat.noThreats":               "Sin amenazas detectadas",
  "threat.noThreatsHint":           "Los eventos de seguridad aparecen aqui en tiempo real",
  "threat.blocked":                 "bloqueado",
  "threat.kind.threat":             "Amenaza",
  "threat.kind.quarantine":         "Cuarentena",
  "threat.kind.rate_limit":         "Limite de tasa",
  "threat.kind.validation_fail":    "Op. invalida",
  "threat.kind.ai_threat":          "Amenaza IA",
  "threat.metric.assessed":         "Evaluados",
  "threat.metric.blocked":          "Bloqueados",
  "threat.metric.blockPct":         "% bloqueados",

  /* WorkflowCanvas */
  "workflow.builder":               "Constructor visual",
  "workflow.emptyHint":             "Agrega nodos para construir tu flujo de trabajo",
  "workflow.node.trigger":          "Disparador",
  "workflow.node.condition":        "Condicion",
  "workflow.node.action":           "Accion",
  "workflow.node.parallel":         "Paralelo",
  "workflow.node.loop":             "Bucle",
  "workflow.node.wait":             "Espera/Retraso",
  "workflow.node.approval":         "Aprobacion",

  /* AgentRunPanel */
  "agent.title":                    "Agente autonomo",
  "agent.locked.title":             "Agente autonomo",
  "agent.locked.body":              "El agente de ingenieria autonomo (lee, edita, ejecuta comandos, compila, prueba y repara tu proyecto) se incluye desde el plan Pro. Mejora tu plan para usarlo.",
  "agent.locked.cta":               "Ver planes",
  "agent.placeholder":              "Describe lo que debe hacer el agente (leer, editar, ejecutar, construir, probar, confirmar)...",
  "agent.run":                      "Ejecutar agente",
  "agent.image":                    "Imagen",
  "agent.imageTitle":               "Adjuntar una imagen (captura de pantalla, boceto)",
  "agent.stop":                     "Detener",
  "agent.working":                  "Trabajando",
  "agent.error.noKey":              "La clave del proveedor de IA aun no esta configurada.",
  "agent.error.notEnabled":         "El runtime del agente no esta habilitado.",
  "agent.reconnect":                   "Reconectar",
  "agent.resume":                      "Continuar",
  "agent.resume.note":                 "Continuar reanuda desde el ultimo punto guardado; los pasos completados no se ejecutan ni se cobran de nuevo.",
  "agent.acuUsed":                     "ACU usados",
  "agent.paused.approval":             "En pausa: este paso necesita aprobacion antes de ejecutarse.",
  "agent.paused.budget":               "En pausa: la asignacion de credito de esta sesion se agoto.",
  "agent.cancelled":                   "Cancelado.",
  "agent.disconnected":                "Conexion perdida. La ejecucion continua; reconecta para seguir viendola.",
  "agent.summary.pipeline":            "Flujo",
  "agent.summary.stages":              "Etapas",
  "agent.summary.capabilities":        "Capacidades completadas",

  // WorkspaceBusyState
  "workspace.busy.errorTitle":      "El build no se completo",
  "workspace.busy.errorBody":       "Este build se detuvo antes de completarse y no se cobraron creditos. Ejecutalo de nuevo para continuar.",
  "workspace.busy.runAgain":        "Intentar de nuevo",
  "workspace.busy.buildingTitle":   "Construyendo tu producto",
  "workspace.busy.generating":      "Generando en los servidores de Nuvra. El progreso en vivo aparece aqui.",

  // SyncConflictBanner
  "sync.conflict.ariaLabel":        "Este proyecto cambio en otro dispositivo",
  "sync.conflict.title":            "Este proyecto cambio en otro dispositivo",
  "sync.conflict.desc":             "Elige que version conservar. Nada se sobreescribe hasta que decidas.",
  "sync.conflict.keepMine":         "Conservar la mia",
  "sync.conflict.useTheirs":        "Usar la de ellos",
  "sync.conflict.diffCount":        "{n} diferencias",
  "sync.conflict.keepAllMine":      "Conservar todas las mias",
  "sync.conflict.useAllTheirs":     "Usar todas las de ellos",

  // DataExplorer
  "data.explorer.selectTable":      "Seleccionar tabla",
  "data.explorer.searchPlaceholder":"Buscar registros...",
  "data.explorer.insert":           "Insertar",
  "data.explorer.import":           "Importar",
  "data.explorer.export":           "Exportar",
  "data.explorer.actionsCol":       "Acciones",
  "data.explorer.noRecords":        "No se encontraron registros",
  "data.explorer.recordCount":      "{n} registros",
  "data.explorer.editHint":         "Doble clic en una celda para editar en linea",

  // ArchitectureConfidencePanel
  "arch.confidence.title":          "Confianza en la arquitectura",
  "arch.confidence.score":          "{score}% de confianza",
  "arch.confidence.components":     "Componentes",
  "arch.confidence.services":       "Servicios",
  "arch.confidence.tables":         "Tablas",
  "arch.confidence.endpoints":      "Endpoints",
  "arch.confidence.estCredits":     "Creditos est.",
  "arch.confidence.deployTarget":   "Destino de despliegue:",
  "arch.confidence.confirmBuild":   "Confirmar y compilar",

  // ExecutionLogs
  "exec.filter.all":                "Todos",
  "exec.filter.completed":          "Completados",
  "exec.filter.failed":             "Fallidos",
  "exec.filter.running":            "En ejecucion",
  "exec.logs.noExecutions":         "No se encontraron ejecuciones",

  // AccessControlPanel
  "access.rls.title":               "Seguridad a nivel de fila",
  "access.rls.desc":                "Define condiciones RLS usando lenguaje natural o expresiones SQL",
  "access.rls.placeholder":         "p.ej. \"Los usuarios solo pueden leer sus propios registros\"",
  "access.rls.addRule":             "Agregar regla",

  // BuildActivityStream
  "build.activity.onTeam":          "esta en el equipo",

  // PlanExplanationPanel
  "plan.explain.toggle":            "Por que Nuvra planifico esto",
  "plan.explain.summary":           "Resumen",
  "plan.explain.keyDecisions":      "Decisiones clave",
  "plan.explain.assumptions":       "Supuestos",
  "plan.explain.confidence":        "{pct}% de confianza",
  "plan.explain.pages":             "Paginas ({n})",
  "plan.explain.capabilities":      "Capacidades ({n})",
  "plan.explain.noData":            "No hay datos de explicacion disponibles para este plan.",

  // UseCasePreviewPanel
  "usecase.tab.architecture":       "Arquitectura",
  "usecase.tab.data":               "Modelos de datos",
  "usecase.tab.auth":               "Autenticacion",
  "usecase.tab.deploy":             "Despliegue",
  "usecase.startBuilding":          "Comenzar a construir",
  "usecase.close":                  "Cerrar",

  // FeatureInteractionPanel
  "feature.howItWorks":             "Como funciona",
  "feature.highlights":             "Destacados",
  "feature.try":                    "Probar {title}",
  "feature.close":                  "Cerrar",

  // SaveStatusIndicator
  "save.status.ready":              "Listo",
  "save.status.saving":             "Guardando...",
  "save.status.saved":              "Guardado",
  "save.status.failed":             "Error al guardar",

  // CommentsMenu
  "comments.title":                 "Comentarios",
  "comments.empty":                 "Sin comentarios aun. Inicia la conversacion.",
  "comments.placeholder":           "Agregar un comentario...",
  "comments.post":                  "Publicar",

  // OAuthButtons
  "oauth.google":                   "Continuar con Google",
  "oauth.github":                   "Continuar con GitHub",
  "oauth.gitlab":                   "Continuar con GitLab",
  "oauth.bitbucket":                "Continuar con Bitbucket",
  "oauth.apple":                    "Continuar con Apple",
  "oauth.microsoft":                "Continuar con Microsoft",
  "oauth.signInFailed":             "Error al iniciar sesion",

  // WelcomeBanner
  "welcome.goodMorning":            "Buenos dias",
  "welcome.goodAfternoon":          "Buenas tardes",
  "welcome.goodEvening":            "Buenas noches",
  "welcome.factory":                "Ingeniería de software autónoma",
  "welcome.credits":                "creditos",

  // IntentChips
  "intent.chip.saas":              "SaaS",
  "intent.chip.api":               "API / Backend",
  "intent.chip.music":             "Musica",
  "intent.chip.books":             "Libros y ebooks",
  "intent.chip.video":             "Video",
  "intent.chip.ecommerce":         "E-commerce",
  "intent.chip.ai":                "Producto con IA",
  "intent.chip.mobile":            "App movil",
  "intent.chip.game3d":            "Juego 3D",

  // ProjectSwitcher
  "proj.switcher.select":           "Seleccionar proyecto",
  "proj.switcher.noProjects":       "Sin proyectos aun",
  "proj.switcher.pinned":           "Fijados",
  "proj.switcher.recent":           "Recientes",
  "proj.switcher.create":           "Crear nuevo proyecto",
  "proj.switcher.justNow":          "ahora mismo",
  "proj.switcher.mAgo":             "hace {m}m",
  "proj.switcher.hAgo":             "hace {h}h",
  "proj.switcher.dAgo":             "hace {d}d",

  // ProjectsSheet
  "proj.sheet.title":               "Proyectos",
  "proj.sheet.newProject":          "Nuevo proyecto",
  "proj.sheet.noProjects":          "Sin proyectos aun. Crea uno para comenzar.",
  "proj.sheet.untitled":            "Proyecto sin titulo",

  // BuilderEmptyState
  "builder.headline":               "Construye tu proxima empresa de software.",
  "builder.subline":                "Describe tu producto y Nuvra lo ingenia de extremo a extremo.",
  "builder.step.intent":            "Intencion",
  "builder.step.planning":          "Planificacion",
  "builder.step.engineering":       "Ingenieria",
  "builder.step.operations":        "Operaciones",
  "builder.step.growth":            "Crecimiento",
  "builder.tpl.saas":               "Panel SaaS",
  "builder.tpl.crm":                "CRM",
  "builder.tpl.marketplace":        "Marketplace",
  "builder.tpl.aiAgent":            "Plataforma de agente IA",
  "builder.tpl.adminPortal":        "Portal de administracion",
  "builder.tpl.landingPage":        "Pagina de inicio",
  "builder.tpl.ecommerce":          "Tienda de comercio electronico",
  "builder.tpl.internalTool":       "Herramienta interna",

  // ACUUsageNudges (ACUBalanceWarning)
  "acu.nudge.exhaustedTitle":       "Creditos agotados",
  "acu.nudge.exhaustedBody":        "Tus creditos de computo se han agotado. Recarga para seguir construyendo.",
  "acu.nudge.topUp":                "Recargar",
  "acu.nudge.dismiss":              "Descartar",
  "acu.nudge.almostTitle":          "Casi sin creditos",
  "acu.nudge.remaining":            "{n} creditos restantes.",
  "acu.nudge.approachingTitle":     "Acercandose al limite de creditos",
  "acu.nudge.buildingTitle":        "Ganando impulso",
  "acu.nudge.buildingBody":         "Vas por buen camino. Sigue adelante.",

  // FirstRunOnboarding
  "onboarding.a11yTitle":           "Configura tu perfil de creador en Nuvra",
  "onboarding.stepOf":              "Paso {n} de {total}",
  "onboarding.maybeLater":          "Quizas mas tarde",
  "onboarding.back":                "Atras",
  "onboarding.continue":            "Continuar",
  "onboarding.saveFailTitle":       "Tus preferencias no se guardaron",
  "onboarding.saveFailBody":        "Nuvra no pudo conectar con el servidor. Puedes continuar; tus respuestas se guardaran con tu siguiente paso.",
  "onboarding.sessionExpiredTitle": "Tu sesion expiro",
  "onboarding.sessionExpiredBody":  "Inicia sesion de nuevo para guardar tus preferencias. Puedes seguir construyendo igualmente.",
  "onboarding.profile.title":       "Como usaras Nuvra?",
  "onboarding.profile.body":        "Cuentanos un poco sobre como construyes. Nuvra adapta tu experiencia a ello.",
  "onboarding.profile.founder":     "Fundador / startup",
  "onboarding.profile.founderDesc": "Convierte ideas en productos reales y lanza mas rapido.",
  "onboarding.profile.product_manager":     "Product manager",
  "onboarding.profile.product_managerDesc": "Convierte requisitos de producto en software funcional.",
  "onboarding.profile.developer":       "Desarrollador / ingeniero",
  "onboarding.profile.developerDesc":   "Construye, depura y entrega software de produccion.",
  "onboarding.profile.business_owner":      "Dueno de negocio",
  "onboarding.profile.business_ownerDesc":  "Crea herramientas y sistemas que hacen funcionar tu negocio.",
  "onboarding.profile.freelancer":      "Freelancer / agencia",
  "onboarding.profile.freelancerDesc":  "Construye productos y experiencias para clientes y equipos.",
  "onboarding.profile.marketer":        "Marketer / creador",
  "onboarding.profile.marketerDesc":    "Crea sitios web, experiencias digitales y productos.",
  "onboarding.profile.student":         "Estudiante / educador",
  "onboarding.profile.studentDesc":     "Aprende, experimenta y convierte ideas en proyectos reales.",
  "onboarding.profile.personal":        "Proyectos personales",
  "onboarding.profile.personalDesc":    "Construye algo util solo para ti.",
  "onboarding.intent.title":        "Que quieres crear primero?",
  "onboarding.intent.body":         "Elige un punto de partida. Puedes cambiar todo despues.",
  "onboarding.intent.production_app":  "Aplicacion de produccion",
  "onboarding.intent.saas":            "Producto SaaS",
  "onboarding.intent.website":         "Sitio web / experiencia web",
  "onboarding.intent.internal_tool":   "Herramienta interna de negocio",
  "onboarding.intent.mobile_app":      "Aplicacion movil",
  "onboarding.intent.api_backend":     "API / sistema backend",
  "onboarding.intent.mvp":             "MVP / prototipo",
  "onboarding.intent.ecommerce":       "Producto de e-commerce",
  "onboarding.intent.client_project":  "Proyecto para un cliente",
  "onboarding.intent.something_else":  "Otra cosa",
  "onboarding.intent.ownIdea":      "Tengo otra idea",
  "onboarding.priorities.title":    "Que es lo mas importante para ti?",
  "onboarding.priorities.body":     "Elige hasta 3 prioridades. Nuvra las usara para entender mejor como quieres construir.",
  "onboarding.priorities.count":    "{n} de {max} seleccionadas",
  "onboarding.priority.production_readiness": "Listo para produccion",
  "onboarding.priority.ui_ux":            "UI/UX excepcional",
  "onboarding.priority.speed":            "Velocidad hasta la primera version",
  "onboarding.priority.architecture":     "Arquitectura solida",
  "onboarding.priority.business_logic":   "Logica de negocio",
  "onboarding.priority.scalability":      "Escalabilidad",
  "onboarding.priority.integrations":     "Integraciones",
  "onboarding.priority.security":         "Seguridad",
  "onboarding.priority.mobile":           "Experiencia movil",
  "onboarding.priority.cost":             "Eficiencia de costos",
  "onboarding.ready.headline":      "Que vamos a construir?",
  "onboarding.ready.body":          "Describe lo que quieres construir. Nuvra convierte tu idea en un proyecto de ingenieria que puedes construir, probar y lanzar.",
  "onboarding.ready.placeholder":   "Describe tu producto: que hace, quien lo usa y que debe soportar.",
  "onboarding.ready.examplesLabel": "Ejemplos",
  "onboarding.ready.build":         "Construir con Nuvra",
  "onboarding.ready.workspace":     "Continuar al espacio de trabajo",
  "onboarding.example.store":            "Construye una tienda en linea con catalogo de productos, pago y seguimiento de pedidos",
  "onboarding.example.clientPortal":     "Construye un portal de clientes con autenticacion, facturas, documentos y mensajeria",
  "onboarding.example.fitnessPlatform":  "Construye una plataforma de fitness con membresias, planes de entrenamiento, pagos y seguimiento del progreso",

  // ACUTopUpGrid
  "topup.title":                    "Recargar creditos",
  "topup.body":                     "Casi sin saldo? Agrega creditos en cualquier momento sin cambiar tu plan.",
  "topup.info":                     "Comprar creditos no cambia tu suscripcion. Los creditos nunca vencen. Maximo pendiente: {n} creditos.",
  "topup.creditsUnit":              "creditos",
  "topup.bestValue":                "Mejor valor",
  "topup.processing":               "Procesando...",
  "topup.buy":                      "Comprar {label}",
  "topup.footer":                   "Los creditos de recarga nunca vencen y se acumulan cada ciclo de facturacion.",
  "topup.upgrade":                  "Necesitas mucho mas? Actualiza tu plan",
  "topup.viewPlans":                "Ver planes",

  // BillingUsagePanel
  "usage.loading":                  "Cargando detalles de uso...",
  "usage.noData":                   "Sin datos de uso.",
  "usage.month":                    "Este mes ({period})",
  "usage.spentSoFar":               "Gastado hasta ahora",
  "usage.projected":                "Proyectado al final del mes",
  "usage.dayOf":                    "dia {elapsed} de {total}",
  "usage.planLimits":               "Limites del plan ({tier})",
  "usage.unlimited":                "Ilimitado en este plan.",
  "usage.notInPlan":                "No incluido en tu plan",
  "usage.unlockUpgrade":            "Desbloquealos con una mejora",
  "usage.breakdown":                "Desglose de uso",
  "usage.byProvider":               "Por proveedor de IA",
  "usage.byActivity":               "Por actividad",
  "usage.noActivity":               "Sin uso registrado este mes.",
  "usage.recentLimits":             "Limites alcanzados recientemente",
  "usage.rateLimit":                "limite de tasa",
  "usage.planLimit":                "limite de plan",

  // SSOComplianceTab
  "sso.loading":                    "Cargando configuracion SSO y cumplimiento...",
  "sso.noConfig":                   "Sin configuracion cargada",
  "sso.title":                      "Inicio de sesion unico",
  "sso.saved":                      "Guardado",
  "sso.saving":                     "Guardando...",
  "sso.saveSso":                    "Guardar SSO",
  "sso.enabled":                    "SSO habilitado",
  "sso.type":                       "Tipo de SSO",
  "sso.issuerUrl":                  "URL del emisor",
  "sso.clientId":                   "ID de cliente",
  "sso.scope":                      "Alcance",
  "sso.scopeHint":                  "Alcances OIDC separados por espacio.",
  "sso.entityId":                   "ID de entidad",
  "sso.ssoUrl":                     "URL de SSO",
  "sso.certificate":                "Certificado X.509",
  "sso.defaultRole":                "Rol de miembro predeterminado",
  "sso.defaultRoleHint":            "Rol asignado a nuevos miembros aprovisionados via SSO.",
  "sso.scim":                       "Aprovisionamiento SCIM",
  "sso.scimDesc":                   "Habilita el endpoint /api/org/{orgId}/scim/v2/Users para aprovisionamiento IdP.",
  "sso.scimToken":                  "Token SCIM",
  "sso.scimTokenHint":              "Guardalo ahora. No se mostrara de nuevo al salir de esta pagina.",
  "sso.copied":                     "Copiado",
  "sso.complianceTitle":            "Residencia de datos y cumplimiento",
  "sso.saveRegion":                 "Guardar politica de region",
  "sso.dataClass":                  "Clasificacion de datos",
  "sso.piiRedaction":               "Redaccion de PII requerida",
  "sso.piiDesc":                    "Marca nombres de campos PII en el README de exportacion. No redacta contenido automaticamente.",
  "sso.legalHold":                  "Retencion legal",
  "sso.legalHoldActive":            "ACTIVO: todas las exportaciones de proyectos estan bloqueadas.",
  "sso.legalHoldDesc":              "Cuando esta activo: las exportaciones de proyectos son bloqueadas.",
  "sso.frameworks":                 "Marcos de cumplimiento",
  "sso.dataRegions":                "Regiones de datos permitidas",
  "sso.dataRegionsHint":            "Codigos de region separados por coma. Vacio = cualquier region permitida.",
  "sso.aiRegions":                  "Regiones del proveedor de IA",
  "sso.aiRegionsHint":              "Separados por coma. Vacio = cualquier region del proveedor permitida.",

  // AIGovernanceTab
  "ai.gov.loading":                 "Cargando politica de IA...",
  "ai.gov.noPolicy":                "Sin politica cargada",
  "ai.gov.title":                   "Politica de gobernanza de IA",
  "ai.gov.saved":                   "Guardado",
  "ai.gov.saving":                  "Guardando...",
  "ai.gov.save":                    "Guardar",
  "ai.gov.approvedModels":          "Modelos aprobados",
  "ai.gov.approvedModelsHint":      "Lista vacia = cualquier modelo permitido. Selecciona para restringir.",
  "ai.gov.restrictionActive":       "Restriccion activa: solo los modelos seleccionados estan permitidos para esta org.",
  "ai.gov.redactionPatterns":       "Patrones de redaccion",
  "ai.gov.redactionHint":           "Una expresion regular por linea. Se aplica a los prompts antes de llegar a cualquier proveedor.",
  "ai.gov.retention":               "Retencion de prompts (dias)",
  "ai.gov.retentionHint":           "0 = no retener",
  "ai.gov.costCap":                 "Limite de costo mensual (centavos)",
  "ai.gov.noCap":                   "Sin limite",
  "ai.gov.month":                   "mes",
  "ai.gov.logPrompts":              "Registrar prompts",
  "ai.gov.logPromptsHint":          "Cuando esta desactivado, el contenido del prompt nunca se almacena (solo metadatos).",
  "ai.gov.trainingOptOut":          "Exclusion de entrenamiento",
  "ai.gov.trainingOptOutHint":      "Indica a los proveedores de IA que los datos de la org no deben usarse para entrenamiento.",

  // WhiteLabelTab
  "wl.loading":                     "Cargando configuracion de marca blanca...",
  "wl.noConfig":                    "Sin configuracion cargada",
  "wl.title":                       "Configuracion de marca blanca",
  "wl.saved":                       "Guardado",
  "wl.saving":                      "Guardando...",
  "wl.save":                        "Guardar",
  "wl.domain":                      "Dominio personalizado",
  "wl.domainHint":                  "El dominio donde se sirve tu instancia de Nuvra con marca blanca.",
  "wl.platformName":                "Nombre de la plataforma",
  "wl.platformNameHint":            "Reemplaza 'Nuvra' en toda la interfaz.",
  "wl.primaryColor":                "Color primario",
  "wl.logoUrl":                     "URL del logo",
  "wl.faviconUrl":                  "URL del favicon",
  "wl.supportEmail":                "Correo de soporte",
  "wl.footerText":                  "Texto del pie de pagina",
  "wl.hidePoweredBy":               "Ocultar 'Desarrollado por Nuvra'",
  "wl.customCss":                   "CSS personalizado",
  "wl.customCssHint":               "Con alcance a tu tenant. Los patrones peligrosos se eliminan en el servidor (sin @import, javascript:, expresiones). Max 8 KB.",

  // MembersTab
  "members.loading":                "Cargando miembros...",
  "members.title":                  "Miembros",
  "members.total":                  "{n} en total",
  "members.empty":                  "No se encontraron miembros",
  "members.col.member":             "Miembro",
  "members.col.role":               "Rol",
  "members.col.joined":             "Ingreso",
  "members.col.lastActive":         "Ultimo acceso",
  "members.never":                  "Nunca",
  "members.remove":                 "Eliminar miembro",

  // OrgDashboardTab
  "org.loading":                    "Cargando panel...",
  "org.health":                     "Salud de la organizacion",
  "org.refresh":                    "Actualizar",
  "org.metric.totalInferences":     "Total de inferencias",
  "org.metric.denied":              "Denegadas",
  "org.metric.redacted":            "Redactadas",
  "org.metric.policyChanges":       "Cambios de politica (24h)",
  "org.alerts":                     "Alertas activas ({n})",
  "org.noAlerts":                   "Sin alertas activas",
  "org.members":                    "Miembros ({n})",
  "org.inactive":                   "inactivo",
  "org.noMembers":                  "No se encontraron miembros",

  // AuditLogTab
  "audit.tab.prompts":              "Registro de prompts",
  "audit.tab.policy":               "Cambios de politica",
  "audit.loading":                  "Cargando...",
  "audit.col.time":                 "Hora",
  "audit.col.model":                "Modelo",
  "audit.col.outcome":              "Resultado",
  "audit.col.redacted":             "Redactado",
  "audit.col.action":               "Accion",
  "audit.col.changedBy":            "Modificado por",
  "audit.unknown":                  "desconocido",
  "audit.field":                    "campo",
  "audit.fields":                   "campos",
  "audit.noRedaction":              "No",
  "audit.blockReason":              "Razon de bloqueo: {reason}",
  "audit.noPromptEntries":          "No hay entradas de auditoria de prompts",
  "audit.system":                   "sistema",
  "audit.noPolicyEntries":          "No hay entradas de cambios de politica",

  // WatermarkModal
  "wm.close":                       "Cerrar",
  "wm.logoAlt":                     "Logo de Nuvra",
  "wm.title":                       "Creado con Nuvra",
  "wm.tagline":                     "La forma mas rapida de lanzar apps con IA.",
  "wm.descBefore":                  "Esta app fue creada con",
  "wm.descAfter":                   "una plataforma nativa de IA para lanzar apps de produccion en minutos, no en meses.",
  "wm.buildMyApp":                  "Crear mi app",
  "wm.exploreTemplates":            "Explorar plantillas",
  "wm.viewPlatform":                "Ver la plataforma",
  "wm.joinLine":                    "Unete a miles que construyen con Nuvra",

  // ProjectCodeView
  "code.title":                     "Codigo del proyecto",
  "code.edited":                    "editado",
  "code.editedTooltip":             "Editado",
  "code.pushGithub":                "Subir a GitHub",
  "code.mobileApp":                 "App movil",
  "code.mobileAppTitle":            "Descargar la app movil nativa (Expo / React Native)",
  "code.downloadZip":               "Descargar ZIP",
  "code.buildCi":                   "Compilar app (CI)",
  "code.buildCiTitle":              "Compilar un AAB/IPA firmado en CI (runners con Android SDK / macOS + Xcode)",
  "code.loading":                   "Cargando",
  "code.empty":                     "Codigo aun no generado. Construye el proyecto primero.",
  "code.edit":                      "Editar",
  "code.reset":                     "Restablecer",
  "code.save":                      "Guardar",
  "code.cancel":                    "Cancelar",
  "code.flash.downloadStarted":     "Descarga iniciada.",
  "code.flash.noCredits":           "Creditos insuficientes para exportar (necesita {acu} creditos).",
  "code.flash.more":                "mas",
  "code.flash.exportFailed":        "Exportacion fallida (HTTP {status}).",
  "code.flash.needGithub":          "Conecta tu GitHub primero, luego compila en CI.",
  "code.flash.ciFailed":            "Error al enviar la compilacion (HTTP {status}).",
  "code.flash.pushed":              "Subido. Agrega los secretos de firma y luego ejecuta el flujo.",
  "code.flash.ciReady":             "Entrega de compilacion preparada.",
  "code.flash.saved":               "Guardado.",
  "code.flash.reset":               "Restablecido al original generado.",
  "code.flash.filesTo":             "Se subieron {n} archivos a {branch}.",

  // NuvraActionsMenu
  "actions.button":                 "Acciones",
  "actions.title":                  "Acciones de Nuvra",
  "actions.templates":              "Plantillas",
  "actions.connect":                "Conectar",
  "actions.manageConnectors":       "Gestionar conectores",
  "actions.cat.saas":               "SaaS / Startup",
  "actions.cat.marketplace":        "Marketplace / E-Commerce",
  "actions.cat.internal":           "Herramientas internas",
  "actions.cat.ai_agents":          "IA y Agentes",
  "actions.cat.marketing":          "Marketing",
  "actions.cat.other":              "Otros",
  "actions.tpl.analytics_dash":     "Panel de analiticas",
  "actions.tpl.sub_billing":        "App de facturacion por suscripcion",
  "actions.tpl.two_sided":          "Marketplace de dos lados",
  "actions.tpl.ecommerce":          "Tienda de e-commerce",
  "actions.tpl.admin_portal":       "Portal de administracion",
  "actions.tpl.ops_tool":           "Herramienta de operaciones",
  "actions.tpl.ai_platform":        "Plataforma de agentes IA",
  "actions.tpl.ai_chat":            "App de chat con IA",
  "actions.tpl.landing":            "Landing page SaaS",
  "actions.tpl.crm":                "CRM",
  "actions.tpl.community":          "Plataforma comunitaria",
  "actions.tpl.project_mgmt":       "Herramienta de gestion de proyectos",

  // BuildHistory
  "bh.title":                       "Historial de compilaciones",
  "bh.count":                       "{n} compilaciones",
  "bh.noBuilds":                    "Ninguna compilacion coincide con los filtros.",
  "bh.filter.all":                  "Todas",
  "bh.filter.success":              "Exitosas",
  "bh.filter.failed":               "Fallidas",
  "bh.filter.inProgress":           "En curso",
  "bh.filter.allCost":              "Todo costo",
  "bh.filter.highCost":             "Alto costo",
  "bh.used":                        "Usado: {n} creditos",
  "bh.reserved":                    "Reservado: {n} creditos",
  "bh.refunded":                    "Reembolsado: {n} creditos",
  "bh.logsTitle":                   "Registros - {id}",
  "bh.logsExported":                "Registros exportados",
  "bh.downloading":                 "Descargando {target} - {mb}MB",
  "bh.shareCopied":                 "Enlace de compartir copiado",
  "bh.idCopied":                    "ID de compilacion copiado",

  // MobileGovernanceDashboard
  "mgov.loading":                   "Cargando datos de gobernanza movil",
  "mgov.loadError":                 "No se pudieron cargar los datos de gobernanza movil.",
  "mgov.loadAllFailed":             "No pudimos cargar la salud de compilacion de este proyecto. Suele ser temporal.",
  "mgov.loadPartial":               "Parte de los datos de salud no se cargo. Reintenta para completar el panorama.",
  "mgov.retry":                     "Reintentar",
  "mgov.profileSaved":              "Perfil predeterminado guardado. Las nuevas compilaciones lo usaran salvo anulacion.",
  "mgov.profileError":              "No se pudo guardar el perfil predeterminado.",
  "mgov.observability":             "Observabilidad de compilaciones",
  "mgov.stat.builds":               "Compilaciones",
  "mgov.stat.successRate":          "Tasa de exito",
  "mgov.stat.p50":                  "Duracion p50",
  "mgov.stat.p95":                  "Duracion p95",
  "mgov.stat.completed":            "Completadas",
  "mgov.stat.failed":               "Fallidas",
  "mgov.stat.running":              "En ejecucion",
  "mgov.stat.queued":               "En cola",
  "mgov.failuresByCause":           "Fallos por causa",
  "mgov.of":                        "de",
  "mgov.noBuilds":                  "No hay compilaciones registradas para este proyecto aun.",
  "mgov.versionHistory":            "Historial de versiones",
  "mgov.storeReady":                "lista-para-tienda",
  "mgov.caps":                      "caps",
  "mgov.noVersions":                "No hay compilaciones versionadas aun.",
  "mgov.profiles":                  "Perfiles de gobernanza",
  "mgov.profilesExplainer":         "El perfil seleccionado ejecuta sus controles de seguridad, cifrado, auditoria y residencia de datos antes de cada compilacion movil. Un control fallido bloquea la compilacion.",
  "mgov.projectDefault":            "Predeterminado del proyecto",
  "mgov.selectDefault":             "Seleccionar predeterminado",
  "mgov.default":                   "predeterminado",
  "mgov.tag.encryption":            "cifrado",
  "mgov.tag.audit":                 "auditoria",
  "mgov.tag.residency":             "residencia: {r}",
  "mgov.tag.security":              "seguridad: {s}",
  "mgov.tag.blocks":                "bloquea {n}",

  // HostDeploySection
  "host.title":                     "Desplegar en tu host",
  "host.subtitle":                  "Enviar la compilacion actual a tu propia cuenta de Vercel o Netlify.",
  "host.connectBtn":                "Conectar host",
  "host.open":                      "Abrir",
  "host.deploy":                    "Desplegar",
  "host.disconnect":                "Desconectar",
  "host.noConnection":              "No hay host conectado. Conecta Vercel o Netlify para desplegar en tu cuenta.",
  "host.tokenRequired":             "Se requieren token y destino.",
  "host.connectError":              "No se pudo conectar.",
  "host.noCredits":                 "Creditos insuficientes para desplegar (necesita {acu} creditos).",
  "host.more":                      "mas",
  "host.deployFailed":              "Despliegue fallido (HTTP {status}).",
  "host.tokenPlaceholder":          "Token de acceso de {provider}",
  "host.vercelProjectId":           "ID de proyecto de Vercel",
  "host.netlifySiteId":             "ID de sitio de Netlify",
  "host.vercelTeamId":              "ID de equipo de Vercel (opcional)",
  "host.connectProvider":           "Conectar {provider}",

  // StoreSubmitSection
  "store.google":                   "Google Play Store",
  "store.apple":                    "Apple App Store",
  "store.ciRun":                    "Ejecucion CI",
  "store.checking":                 "Verificando compilacion",
  "store.needGithub":               "Conecta GitHub y ejecuta una compilacion {target} para enviar.",
  "store.noBuild":                  "Aun no hay compilacion {target}. Inicia una desde Compilaciones moviles.",
  "store.buildRunning":             "Compilacion {status}. El envio se desbloquea cuando termine.",
  "store.buildFailed":              "La ultima compilacion fallo. Corrige y recompila antes de enviar.",
  "store.noArtifact":               "La compilacion tuvo exito pero no produjo artefacto para enviar.",
  "store.packagePlaceholder":       "ID de aplicacion Android (ej. com.empresa.app)",
  "store.submit":                   "Enviar",
  "store.submitted":                "Enviado ({id})",
  "store.release":                  "lanzamiento",
  "store.submitFailed":             "Envio fallido",
  "store.statusError":              "No se pudo leer el estado de la compilacion",

  // batch 9 - ver.*
  "ver.title":          "Historial de versiones",
  "ver.titleBtn":       "Historial de versiones",
  "ver.btnLabel":       "Versiones",
  "ver.loading":        "Cargando versiones...",
  "ver.loadError":      "No se pudo cargar el historial de versiones.",
  "ver.empty":          "Aun no hay compilaciones completadas.",
  "ver.untitled":       "Compilacion sin titulo",
  "ver.name":           "Nombrar esta version",
  "ver.rename":         "Renombrar esta version",
  "ver.namePlaceholder": "Nombra esta version (ej. Antes de pagos)",
  "ver.labelError":     "No se pudo guardar el nombre. Intenta de nuevo.",
  "ver.current":        "Actual",
  "ver.confirm":        "Confirmar",
  "ver.cancel":         "Cancelar",
  "ver.revert":         "Revertir",
  "ver.revertFailed":   "No se pudo revertir a esta version.",

  // batch 9 - domain.*
  "domain.status.live":      "Activo",
  "domain.status.progress":  "En progreso",
  "domain.status.failed":    "Fallido",
  "domain.status.pending":   "Pendiente",
  "domain.title":            "Dominios personalizados",
  "domain.subtitle":         "Conecta tu propio dominio a este proyecto.",
  "domain.add":              "Agregar dominio",
  "domain.dnsInstructions":  "Agrega estos registros DNS en tu proveedor de dominio:",
  "domain.copy":             "Copiar",
  "domain.verify":           "Verificar",
  "domain.remove":           "Eliminar",
  "domain.registerError":    "No se pudo registrar el dominio.",
  "domain.dnsError":         "No se pudieron obtener las instrucciones DNS.",
  "domain.verifyFailed":     "La verificacion fallo.",
  "domain.record.host":      "Anfitrión",
  "domain.record.value":     "Valor",

  // batch 9 - sb.*
  "sb.title":                    "Conectar Supabase",
  "sb.subtitle":                 "Vincula tu proyecto de Supabase para persistencia en la nube y autenticacion real.",
  "sb.anonOnly":                 "Solo clave anonima",
  "sb.anonService":              "Clave anonima y de rol de servicio",
  "sb.connectBtn":               "Conectar",
  "sb.disconnect":               "Desconectar",
  "sb.urlRequired":              "La URL del proyecto es obligatoria.",
  "sb.connectError":             "No se pudo conectar. Verifica tus credenciales.",
  "sb.connected":                "Supabase conectado.",
  "sb.anonPlaceholder":          "Clave publica de la API (anon)",
  "sb.serviceRolePlaceholder":   "Clave de rol de servicio (opcional)",

  // batch 9 - hdm.*
  "hdm.title":             "Desplegar",
  "hdm.connectTitle":      "Conectar proveedor de hosting",
  "hdm.deploy":            "Desplegar",
  "hdm.open":              "Abrir sitio",
  "hdm.disconnect":        "Desconectar",
  "hdm.tokenRequired":     "Se requiere un token de API.",
  "hdm.connectError":      "No se pudo conectar con el proveedor.",
  "hdm.deployFailed":      "El despliegue fallo (HTTP {status}).",
  "hdm.deployFailed2":     "El despliegue fallo.",
  "hdm.noCredits":         "Se necesitan {acu} creditos para desplegar.",
  "hdm.more":              "mas",
  "hdm.connected":         "Conectado a {provider}.",
  "hdm.liveon":            "En vivo en {provider}.",
  "hdm.tokenPlaceholder":  "Token de acceso de {provider}",
  "hdm.vercelProjectId":   "ID del proyecto Vercel",
  "hdm.netlifySiteId":     "ID del sitio Netlify",
  "hdm.vercelTeamId":      "ID del equipo Vercel (opcional)",
  "hdm.connect":           "Conectar {provider}",

  // batch 9 - imp.*
  "imp.title":            "Importar repositorio",
  "imp.subtitle":         "Importa un proyecto de GitHub existente a Nuvra.",
  "imp.btnLabel":         "Importar repo",
  "imp.preview":          "Vista previa",
  "imp.import":           "Importar",
  "imp.tokenPlaceholder": "Token de acceso personal de GitHub",
  "imp.previewError":     "No se pudo obtener la vista previa del repositorio.",
  "imp.importError":      "La importacion fallo.",
  "imp.models":           "modelos",
  "imp.routes":           "rutas",
  "imp.done":             "Importacion completada: {files} archivos, {models} modelos, {pages} paginas.",

  // batch 9 - sr.*
  "sr.docsTitle":       "Preparacion para la tienda",
  "sr.dataDisclosure":  "Datos recopilados: {data}.",
  "sr.minimalData":     "ninguno",
  "sr.privacy":         "Politica de privacidad",
  "sr.terms":           "Terminos de servicio",
  "sr.listing":         "Descripcion de la tienda",
  "sr.screenshots":     "Capturas de pantalla",
  "sr.genError":        "No se pudo generar el contenido de la tienda.",
  "sr.shotsError":      "No se pudieron generar las capturas de pantalla.",
  "sr.shotsError2":     "No se pudo descargar la captura de pantalla.",

  // batch 9 - erd.*
  "erd.title":       "Diagrama de la base de datos",
  "erd.fromTable":   "Desde tabla",
  "erd.references":  "referencia",
  "erd.colName":     "nombre de columna",
  "erd.addRel":      "Relacion",
  "erd.noTables":    "Aun no hay tablas para mostrar.",
  "erd.pending":     "pendiente",
  "erd.relError":    "No se pudo agregar la relacion.",

  // batch 9 - tdv.*
  "tdv.search":        "Buscar filas...",
  "tdv.loading":       "Cargando...",
  "tdv.noRows":        "No hay filas en esta tabla.",
  "tdv.noRowsSearch":  "Ninguna fila coincide con la busqueda.",
  "tdv.clickEdit":     "Clic para editar",
  "tdv.loadError":     "No se pudieron cargar los datos.",
  "tdv.saveError":     "No se pudo guardar el cambio.",

  // batch 9 - wft.*
  "wft.testTitle":    "Probar {name}",
  "wft.dryRun":       "Simulacro",
  "wft.payloadLabel": "Carga de prueba (JSON)",
  "wft.invalidJson":  "JSON invalido. Corrija antes de continuar.",
  "wft.executing":    "Ejecutando...",
  "wft.runTest":      "Ejecutar prueba",
  "wft.liveBadge":    "En vivo",
  "wft.steps":        "{n} pasos",

  // batch 9 - wfv.*
  "wfv.saveTitle":      "Guardar version",
  "wfv.currentBadge":   "v{n}",
  "wfv.placeholder":    "Mensaje de version",
  "wfv.save":           "Guardar",
  "wfv.historyTitle":   "Versiones guardadas",
  "wfv.empty":          "Aun no hay versiones guardadas.",
  "wfv.restore":        "Restaurar",

  // batch 10 - env.*
  "env.title":          "Entornos",
  "env.notReleased":    "no publicado",
  "env.build":          "compilacion {id}",
  "env.promoteStaging": "Promover staging",
  "env.rollBack":       "Revertir",
  "env.rollbackTitle":  "Revertir a la compilacion {id}",
  "env.config":         "Config",
  "env.add":            "Agregar",
  "env.actionFailed":   "La accion fallo",
  "env.description":    "Las nuevas compilaciones se publican en staging. Promueve a produccion cuando estes listo. La configuracion es para ajustes no secretos; guarda los secretos en tu plataforma de despliegue.",

  "assets.open":        "Recursos",
  "assets.title":       "Recursos",
  "assets.subtitle":    "Sube archivos y colocalos en tu app",
  "assets.empty":       "Aun no hay recursos. Sube una imagen, audio o video.",
  "assets.drop":        "Arrastra archivos aqui, o haz clic para subir",
  "assets.uploading":   "Subiendo {pct}%",
  "assets.retry":       "Reintentar",
  "assets.delete":      "Eliminar",
  "assets.replace":     "Reemplazar",
  "assets.selected":    "{count} seleccionados",
  "assets.placeHint":   "Di donde va, por ejemplo: pon esto en el hero",
  "assets.place":       "Colocar en la app",
  "assets.placing":     "Colocando",
  "assets.placed":      "Colocado en {target}",
  "assets.inUse":       "En uso",
  "assets.close":       "Cerrar",
  "assets.failed":      "Algo salio mal. Intentalo de nuevo.",
  "assets.loadFailed":  "No se pudieron cargar los recursos.",
  "assets.removed":     "Eliminado",

  // batch 10 - msm.*
  "msm.title":          "Generar medios",
  "msm.label":          "Medios",
  "msm.placeholder":    "Describe una imagen o pega texto para convertir en voz...",
  "msm.image":          "Imagen",
  "msm.speech":         "Voz",
  "msm.music":          "Musica",
  "msm.download":       "Descargar",
  "msm.imageAlt":       "Generado",
  "msm.imageGen":       "Generacion de imagen",
  "msm.speechGen":      "Generacion de voz",
  "msm.musicGen":       "Generacion de musica",
  "msm.notConfigured":  "{what} no esta configurado aun.",
  "msm.noCredits":      "Creditos insuficientes (se necesitan {acu} creditos).",
  "msm.more":           "mas",
  "msm.failedHttp":     "{what} fallo (HTTP {status}).",
  "msm.imgFailed":      "La generacion de imagen fallo.",
  "msm.speechFailed":   "La generacion de voz fallo.",
  "msm.musicFailed":    "La generacion de musica fallo.",
  "msm.length":         "Duracion",
  "msm.instrumental":   "Instrumental",
  "msm.library":        "Tus pistas",
  "msm.titleField":     "Titulo (opcional)",
  "msm.genreField":     "Estilo (opcional)",
  "msm.lyricsField":    "Letra (opcional)",
  "msm.musicTooLong":   "El proveedor de musica configurado llega a {max} minutos.",
  "msm.musicStillRunning": "Aun generando. La pista aparecera en tu biblioteca al terminar.",
  "msm.musicCancelled": "Esa generacion ya no esta en curso.",
  "msm.mfail.unsupported": "Esa combinacion no esta disponible. Prueba otros ajustes.",
  "msm.mfail.unavailable": "La musica no esta disponible ahora. No se te cobro.",
  "msm.mfail.timeout": "Esto tardo demasiado y se detuvo. No se te cobro.",
  "msm.mfail.validation": "La pista recibida no coincidia con tu solicitud, asi que se descarto. No se te cobro.",
  "msm.mfail.storage": "La pista no se pudo guardar. No se te cobro.",
  "msm.mfail.provider": "El servicio de musica no termino esta. No se te cobro.",
  "msm.cancel":         "Cancelar",
  "msm.stage.queued":     "En cola",
  "msm.stage.running":    "Generando",
  "msm.stage.generating": "Generando",
  "msm.stage.validating": "Comprobando el audio",
  "msm.stage.illustrating": "Creando la portada",
  "msm.coverUnavailable": "Cancion generada. La portada no esta disponible para esta pista.",
  "msm.play":           "Reproducir",
  "msm.downloadAudio":  "Descargar audio",
  "msm.downloadCover":  "Descargar portada",
  "msm.coverAlt":       "Portada de {title}",
  "msm.noCover":        "Sin portada",
  "msm.stage.saving":     "Guardando",
  "msm.video":          "Video",
  "msm.videoGen":       "Generacion de video",
  "msm.videoFailed":    "La generacion de video fallo.",
  "msm.videoRefused":   "El proveedor de video rechazo esa solicitud.",
  "msm.videoLength":    "Clip",
  "msm.videoTier":         "Calidad",
  "msm.videoTier.fast":    "Rapida",
  "msm.videoTier.quality": "Maxima calidad",
  "msm.videoAudio":     "Con sonido",
  "msm.vfail.unsupported": "Esa combinacion no esta disponible. Prueba otra duracion, tamano o calidad.",
  "msm.vfail.unavailable": "El video no esta disponible ahora. No se te cobro.",
  "msm.vfail.timeout":     "Esto tardo demasiado y se detuvo. No se te cobro.",
  "msm.vfail.validation":  "El clip recibido no coincidia con tu solicitud, asi que se descarto. No se te cobro.",
  "msm.vfail.storage":     "El clip no se pudo guardar. No se te cobro.",
  "msm.vfail.provider":    "El servicio de video no termino este. No se te cobro.",
  "msm.videoLibrary":   "Tus clips",
  "msm.videoHasAudio":  "con sonido",
  "msm.videoOption":    "{seconds}s ({acu} creditos)",
  "msm.videoLengths":   "Este proveedor genera clips de {lengths} segundos.",
  "msm.videoStillRunning": "Todavia generando. El clip aparecera en tu biblioteca cuando termine.",
  "msm.videoCancelled": "Esa generacion ya no esta en curso.",
  "msm.vstage.submitting": "Enviando tu solicitud",
  "msm.vstage.queued":     "En cola",
  "msm.vstage.generating": "Generando",
  "msm.vstage.retrieving": "Recogiendo el archivo",
  "msm.vstage.validating": "Comprobando el video",
  "msm.vstage.saving":     "Guardando",

  // batch 10 - wb.*
  "wb.dismiss":         "Cerrar resumen",
  "wb.teamHere":        "Tu equipo esta aqui",
  "wb.welcomeBack":     "Bienvenido de nuevo a {name}.",
  "wb.lastWorked":      "La ultima vez que trabajamos en esto fue {ago}.",
  "wb.productStands":   "Aqui es donde esta tu producto.",
  "wb.buildingToward":  "Construyendo hacia",
  "wb.whatNext":        "Lo que haria a continuacion",
  "wb.buildThis":       "Construir",

  // batch 10 - wcm.*
  "wcm.title":          "Conectar GitHub, tu base de datos e integraciones",
  "wcm.ariaLabel":      "Conectar integraciones",
  "wcm.connectLabel":   "Conectar",
  "wcm.sectionTitle":   "Conexiones",
  "wcm.database":       "Base de datos y datos",
  "wcm.integrations":   "Todas las integraciones",

  // batch 10 - erec.*
  "erec.title":         "Lo que recomienda tu equipo de ingenieria a continuacion",
  "erec.subtitle":      "Proximos pasos reales del estado actual de tu proyecto. Un toque y se construye.",
  "erec.buildThis":     "Construir esto",

  // batch 10 - bal.*
  // batch 11 - bst.* (durable build execution timeline)
  "bst.heading":        "Registro de ingenieria",
  "bst.historical":     "Antes en esta construccion",
  "bst.live":           "Ocurriendo ahora",
  "bst.interrupted":    "No termino",
  "bst.running":        "En curso",
  "bst.completed":      "Completado",
  "bst.failed":         "Fallo",
  "bst.attempts":       "Intentado {count} veces",
  "bst.unavailable":    "No se encontro un registro de ingenieria para esta construccion.",
  "bst.unreachable":    "Nuvra no pudo acceder al registro de ingenieria. Esto no significa que la construccion fallara.",
  "bst.forbidden":      "Esta construccion no esta disponible para ti.",
  "bst.loading":        "Leyendo el registro de ingenieria",
  "bst.noPhases":       "Esta construccion no registro ninguna fase.",
  "bst.retry":          "Intentar de nuevo",
  "bst.startedAt":      "Comenzo {time}",
  "bst.duration":       "Duro {duration}",

  "bal.builtProduct":    "Se construyo tu producto",
  "bal.buildingProduct": "Construyendo tu producto",
  "bal.builtData":       "Se construyo tu {nouns}",
  "bal.assembling":      "Ensamblando tu producto",
  "bal.openInFiles":     "Abrir en Archivos",

  // batch 10 - pp.*
  "pp.title":           "Pulso de orquestacion",
  "pp.releaseEvents":   "Eventos de publicacion",
  "pp.mobileBuilds":    "Compilaciones moviles",
  "pp.lastEvent":       "Ultimo evento",
  "pp.standingBy":      "Pipeline en espera - aun no hay eventos.",
  "pp.viewFull":        "Ver la inteligencia de plataforma completa",
  "pp.justNow":         "Ahora mismo",
  "pp.minsAgo":         "hace {n}m",
  "pp.hoursAgo":        "hace {n}h",
  "pp.daysAgo":         "hace {n}d",

  // batch 10 - wqa.*
  "wqa.heading":        "Construir y lanzar",
  "wqa.generateApp":    "Generar app",
  "wqa.shipBuild":      "Lanzar compilacion",
  "wqa.myProjects":     "Mis proyectos",
  "wqa.buyCredits":     "Comprar creditos",
  "wqa.credits":        "{n} creditos",

  // batch 10 - eaf.*
  "eaf.earlierStep":    "{n} paso anterior",
  "eaf.earlierSteps":   "{n} pasos anteriores",
  "eaf.now":            "ahora",

  // batch 10 - etl.*
  "etl.now":            "ahora",

  // batch 11 - dt.*
  "dt.title":           "Canal de despliegue",
  "dt.loading":         "Cargando historial de compilacion...",
  "dt.empty":           "Sin compilaciones aun. Inicia tu primera compilacion desde el Panel.",
  "dt.preview":         "Vista previa",
  "dt.statusLive":      "En vivo",
  "dt.statusFailed":    "Fallido",
  "dt.statusGenerating": "Generando",
  "dt.justNow":         "Ahora mismo",
  "dt.minsAgo":         "hace {n}m",
  "dt.hoursAgo":        "hace {n}h",
  "dt.daysAgo":         "hace {n}d",

  // batch 11 - iaf.*
  "iaf.title":          "Eventos de orquestacion",
  "iaf.loading":        "Cargando actividad...",
  "iaf.empty":          "Sin compilaciones aun",
  "iaf.emptyDesc":      "Los agentes de IA estan listos. Describe lo que quieres construir.",
  "iaf.shipped":        "Aplicacion publicada",
  "iaf.failed":         "Generacion fallida",
  "iaf.generating":     "Agentes de IA generando...",
  "iaf.inQueue":        "En cola",
  "iaf.justNow":        "Ahora mismo",
  "iaf.minsAgo":        "hace {n}m",
  "iaf.hoursAgo":       "hace {n}h",
  "iaf.daysAgo":        "hace {n}d",

  // batch 11 - aqa.*
  "aqa.platformStatus": "Estado de la plataforma",
  "aqa.platformDesc":   "Ver orquestacion, lanzamientos e infraestructura de compilacion en tiempo real",
  "aqa.debugAi":        "Depurar con IA",
  "aqa.debugDesc":      "Abre un espacio de proyecto para diagnosticar un fallo de compilacion o despliegue",

  // batch 11 - uqa.*
  "uqa.generateApp":    "Generar app",
  "uqa.deploy":         "Desplegar",
  "uqa.mobileBuild":    "Compilacion movil",
  "uqa.platform":       "Plataforma",
  "uqa.buyCredits":     "Comprar creditos",
  "uqa.credits":        "{n} creditos",

  // batch 11 - iap.*
  "iap.title":          "Alertas",
  "iap.healthy":        "Todos los sistemas funcionan correctamente. Sin alertas activas.",
  "iap.resolve":        "Resolver incidente",
  "iap.justNow":        "ahora mismo",
  "iap.minsAgo":        "hace {n}m",
  "iap.hoursAgo":       "hace {n}h",

  // batch 11 - cgb.*
  "cgb.disconnect":     "Desconectar",
  "cgb.installApp":     "Instalar GitHub App",
  "cgb.connect":        "Conectar GitHub",
  "cgb.notConfigured":  "GitHub no esta configurado en el servidor",

  // batch 11 - rcp.*
  "rcp.running":        "Ejecutando tu codigo",
  "rcp.rerunTitle":     "Volver a ejecutar con el codigo y ediciones mas recientes",
  "rcp.rerun":          "Volver a ejecutar",
  "rcp.loadError":      "No se pudo ejecutar el codigo.",
  "rcp.noCode":         "Sin codigo ejecutable aun. Construye el proyecto primero.",

  // batch 11 - bih.*
  "bih.notSignedIn":    "No has iniciado sesion",
  "bih.notSignedInDesc": "Inicia sesion e intenta de nuevo.",
  "bih.noCredits":      "Sin creditos restantes",
  "bih.noCreditsDesc":  "Agrega creditos o actualiza tu plan para seguir construyendo con Nuvra.",
  "bih.createError":    "No se pudo crear el proyecto",
  "bih.docCreateError": "No se pudo crear el documento",
  "bih.docCreated":     "Documento creado",
  "bih.docCreatedFallback": "Tu documento",
  "bih.docPages":       "paginas",
  "bih.unexpectedError": "Error inesperado. Por favor intenta de nuevo.",
  "bih.permissionError": "Tu cuenta puede no tener permiso o suficientes creditos. Revisa tu facturacion.",
  "bih.wentWrong":      "Algo salio mal",
  "bih.tryAgain":       "Por favor intenta de nuevo.",
  "composerFail.engineUnavailable": "El servicio de ingenieria de Nuvra no esta disponible ahora, asi que no se construyo nada y no se usaron creditos. Intentalo de nuevo en unos minutos.",
  "composerFail.needCredits":       "No tienes creditos suficientes para esta construccion. No se usaron creditos.",
  "composerFail.needPlan":          "Este tipo de producto no esta incluido en tu plan actual.",
  "composerFail.describeProduct":   "Describe el producto: que debe hacer y quien lo usa.",
  "composerFail.signedOut":         "Tu sesion ha expirado. Inicia sesion e intentalo de nuevo.",
  "composerFail.notAllowed":        "Tu cuenta no tiene acceso a esto.",
  "composerFail.busy":              "Nuvra esta atendiendo demasiadas construcciones ahora mismo. Intentalo de nuevo en un momento.",
  "composerFail.failed":            "No se pudo crear. No se construyo nada y no se usaron creditos. Por favor intenta de nuevo.",
  "bih.placeholder":    "Describe el producto a desarrollar...",
  "bih.ariaLabel":      "Describe tu idea de app",
  "bih.hint":           "Enter para enviar",
  "bih.attachImageInWorkspace":     "Las imagenes se adjuntan en el espacio del proyecto. Inicia el build y agregalas alli.",
  "bih.ex.saasMultiTenant":       "Crea una plataforma SaaS multiinquilino con autenticacion, suscripciones, equipos y control de acceso por roles",
  "bih.ex.inventory":             "Crea una plataforma de inventario y ventas con control de existencias, pedidos e informes",
  "bih.ex.customerPortal":        "Crea un portal de clientes con autenticacion, pagos, documentos y mensajeria",
  "bih.ex.marketplace":           "Crea un marketplace con vendedores, listados de productos, pedidos, pagos y resenas",
  "bih.ex.aiProduct":             "Crea una plataforma de productividad con IA, espacios de trabajo, proyectos y flujos inteligentes",
  "bih.ex.booking":               "Crea una plataforma de reservas con cuentas de cliente, disponibilidad, pagos y notificaciones",
  "bih.ex.internalOps":           "Crea una plataforma de operaciones internas con aprobaciones, informes, roles e historial de auditoria",
  "bih.ex.learning":              "Crea una plataforma de formacion con cursos, cuentas de alumno, seguimiento del progreso y suscripciones",
  "bih.ex.ecommerce":             "Crea una tienda online con catalogo de productos, carrito, pago y gestion de pedidos",
  "bih.ex.apiBackend":            "Crea una API REST con autenticacion, limites de uso, webhooks y documentacion",
  "bih.ex.dashboard":             "Crea un panel de analitica con datos conectados, graficos, filtros e informes programados",
  "bih.ex.mobileApp":             "Crea una app movil con cuentas, datos sin conexion, notificaciones push y pagos integrados",
  "bih.ex.game3d":               "Crea un juego de aventuras 3D con fisicas, camara en tercera persona, enemigos, coleccionables y progreso guardado",

  // batch 12 - rv.*
  "rv.title":           "Recibo",
  "rv.subtitle":        "Descarga o abre tu recibo en PDF.",
  "rv.downloadPdf":     "Descargar PDF",
  "rv.openTab":         "Abrir en nueva pestana",

  // batch 12 - th.*
  "th.loading":         "Cargando transacciones...",
  "th.empty":           "Sin transacciones aun",
  "th.creditsAdded":    "{n} creditos anadidos",
  "th.view":            "Ver",
  "th.download":        "Descargar",

  // batch 12 - rm.*
  "rm.title":           "Recibo",
  "rm.creditsAdded":    "{n} creditos anadidos",
  "rm.reference":       "Referencia",
  "rm.date":            "Fecha",
  "rm.plan":            "Plan",
  "rm.secureNote":      "Esta transaccion fue procesada de forma segura.",
  "rm.print":           "Imprimir",
  "rm.copyRef":         "Copiar ref.",
  "rm.refCopied":       "Referencia copiada",

  // batch 12 - acu.*
  "acu.title":          "Uso de computo",
  "acu.creditsUsed":    "Creditos usados",
  "acu.credits":        "{used} / {total} creditos",
  "acu.percentUsed":    "{n}% usado",
  "acu.remaining":      "{n} restantes",
  "acu.daysRemaining":  "Dias restantes",
  "acu.dailyRate":      "creditos / dia prom.",
  "acu.recommendations": "Recomendaciones",
  "acu.byProject":      "Uso por proyecto",
  "acu.general":        "General",
  "acu.high":           "Alto",

  // batch 12 - scc.*
  "scc.heading":        "Pagos por Stripe",
  "scc.enabled":        "Habilitado",
  "scc.connected":      "Tu cuenta de Stripe esta conectada y lista para recibir pagos.",
  "scc.description":    "Conecta una cuenta de Stripe para recibir pagos. Completaras una configuracion breve y segura en Stripe.",
  "scc.notEnabled":     "Stripe aun no esta habilitado.",
  "scc.startFailed":    "No se pudo iniciar la incorporacion.",
  "scc.continueSetup":  "Continuar configuracion de Stripe",
  "scc.connect":        "Conectar Stripe",

  // batch 12 - cd.*
  "cd.loading":         "Cargando ganancias...",
  "cd.empty":           "Sin ganancias del marketplace aun",
  "cd.payoutHold":      "Pago en espera",
  "cd.loadError":       "Error al cargar ganancias",
  "cd.totalEarned":     "Total ganado",
  "cd.pending":         "Pendiente (retenido)",
  "cd.available":       "Disponible",
  "cd.nextPayout":      "Proximo pago: {date}. {notice}",

  // batch 12 - pmf.*
  "pmf.heading":        "Metodo de pago",
  "pmf.update":         "Actualizar",
  "pmf.accountEnding":  "Cuenta terminada en {last4}",
  "pmf.bankCodeDisplay": "Codigo bancario {code}",
  "pmf.saved":          "Guardado.",
  "pmf.description":    "Las ganancias liquidadas se envian aqui automaticamente en cada dia de pago. Los datos se cifran.",
  "pmf.accountNumber":  "Numero de cuenta",
  "pmf.bankCodeLabel":  "Codigo bancario",
  "pmf.beneficiaryName": "Nombre del beneficiario",
  "pmf.country":        "Pais (codigo de 2 letras, opcional)",
  "pmf.saving":         "Guardando...",
  "pmf.saveMethod":     "Guardar metodo de pago",
  "pmf.cancel":         "Cancelar",
  "pmf.signInError":    "Inicia sesion para guardar un metodo de pago",
  "pmf.saveError":      "No se pudo guardar el metodo de pago",

  // batch 12 - mp.*
  "mp.applied":         "Aplicadas",
  "mp.pending":         "Pendientes",
  "mp.runPending":      "Ejecutar pendientes",
  "mp.dryRun":          "Ejecucion de prueba",
  "mp.up":              "ARRIBA",
  "mp.down":            "ABAJO",
  "mp.apply":           "Aplicar",
  "mp.rollback":        "Revertir",

  // batch 12 - svp.*
  "svp.createSnapshot": "Crear instantanea",
  "svp.description":    "Descripcion de la instantanea",
  "svp.dev":            "Dev",
  "svp.staging":        "Staging",
  "svp.prod":           "Prod",
  "svp.save":           "Guardar",
  "svp.modelsInSnapshot": "{n} modelos en la instantanea",
  "svp.restore":        "Restaurar",
  "svp.viewDiff":       "Ver diferencias",

  // batch 12 - qb.*
  "qb.visual":          "Visual",
  "qb.sql":             "SQL",
  "qb.sortBy":          "Ordenar por",
  "qb.noSort":          "Sin orden",
  "qb.asc":             "ASC",
  "qb.desc":            "DESC",
  "qb.filters":         "Filtros",
  "qb.addFilter":       "Anadir filtro",
  "qb.value":           "Valor",
  "qb.execute":         "Ejecutar",
  "qb.results":         "Resultados",
  "qb.rows":            "{n} filas",
  "qb.sqlPlaceholder":  "SELECT * FROM...",

  // batch 13 - ddt.*
  "ddt.loading":               "Cargando declaracion de despliegue...",
  "ddt.loadError":             "Error al cargar",
  "ddt.saveError":             "Error al guardar",
  "ddt.noDecl":                "No hay declaracion cargada",
  "ddt.title":                 "Declaracion de despliegue",
  "ddt.lastUpdated":           "Ultima actualizacion {date}",
  "ddt.saved":                 "Guardado",
  "ddt.saving":                "Guardando...",
  "ddt.save":                  "Guardar",
  "ddt.airGappedWarning":      "Despliegue aislado: las exportaciones de proyectos estan bloqueadas. Contacte a su equipo de infraestructura antes de activar.",
  "ddt.deploymentTarget":      "Destino de despliegue",
  "ddt.deploymentTargetHint":  "Controla los requisitos de infraestructura y las politicas de exportacion.",
  "ddt.keyMgmt":               "Gestion de claves",
  "ddt.keyMgmtHint":           "Como se gestionan las claves de cifrado para los datos de esta organizacion.",
  "ddt.hsmProvider":           "Proveedor HSM",
  "ddt.hsmProviderHint":       "Nombre o identificador de su proveedor HSM.",
  "ddt.strictResidency":       "Residencia estricta de datos",
  "ddt.strictResidencyHint":   "Garantiza que todos los datos permanezcan en la region primaria declarada.",
  "ddt.primaryRegion":         "Region primaria",
  "ddt.primaryRegionHint":     "Codigo de region ISO donde deben residir todos los datos de la organizacion.",
  "ddt.complianceFrameworks":  "Marcos de cumplimiento",
  "ddt.infraTitle":            "Requisitos de infraestructura",
  "ddt.sovereignBadge":        "Contexto soberano",
  "ddt.airGappedBadge":        "Aislado",
  "ddt.hsmBadge":              "HSM requerido",
  "ddt.byokBadge":             "BYOK",
  "ddt.strictResidencyBadge":  "Residencia estricta",
  "ddt.networkIsolation":      "Aislamiento de red",
  "ddt.onPremInfra":           "Infra local",
  "ddt.provisioning":          "Aprovisionamiento requerido antes del lanzamiento:",
  "ddt.opNotes":               "Notas operativas:",
  "ddt.targetNuvraCloud":      "Nuvra Cloud",
  "ddt.targetPrivateCloud":    "Nube privada",
  "ddt.targetOnPremise":       "En instalaciones",
  "ddt.targetSovereign":       "Soberano",
  "ddt.targetAirGapped":       "Aislado",
  "ddt.kmPlatform":            "Gestionado por la plataforma",
  "ddt.kmCustomerManaged":     "Gestionado por el cliente (BYOK)",
  "ddt.kmHsm":                 "Modulo de seguridad hardware",

  // batch 13 - sat.*
  "sat.loading":         "Cargando cuentas de servicio...",
  "sat.loadError":       "Error al cargar",
  "sat.createError":     "Error al crear",
  "sat.revokeError":     "Error al revocar",
  "sat.title":           "Cuentas de servicio",
  "sat.newAccount":      "Nueva cuenta",
  "sat.keyCreated":      "Cuenta de servicio creada. Guarde esta clave ahora.",
  "sat.keyOnce":         "No se mostrara de nuevo.",
  "sat.copied":          "Copiado",
  "sat.dismiss":         "Cerrar",
  "sat.newAccountTitle": "Nueva cuenta de servicio",
  "sat.name":            "Nombre",
  "sat.scopes":          "Permisos",
  "sat.expiresAt":       "Expira el (opcional)",
  "sat.cancel":          "Cancelar",
  "sat.creating":        "Creando...",
  "sat.create":          "Crear",
  "sat.empty":           "Sin cuentas de servicio todavia",
  "sat.revoked":         "revocada",
  "sat.created":         "Creada {date}",
  "sat.lastUsed":        "Ultimo uso {date}",
  "sat.expires":         "Expira {date}",
  "sat.revokeTitle":     "Revocar",

  // batch 13 - swt.*
  "swt.loading":         "Cargando webhooks...",
  "swt.loadError":       "Error al cargar",
  "swt.createError":     "Error al crear",
  "swt.deleteError":     "Error al eliminar",
  "swt.title":           "Webhooks del vendedor",
  "swt.addEndpoint":     "Agregar endpoint",
  "swt.webhookCreated":  "Webhook creado. Guarde el secreto de firma ahora.",
  "swt.secretOnce":      "No se mostrara de nuevo.",
  "swt.dismiss":         "Cerrar",
  "swt.newEndpoint":     "Nuevo endpoint webhook",
  "swt.url":             "URL",
  "swt.events":          "Eventos",
  "swt.allEventsHint":   "Vacio = recibir todos los eventos.",
  "swt.cancel":          "Cancelar",
  "swt.creating":        "Creando...",
  "swt.create":          "Crear",
  "swt.empty":           "Sin endpoints webhook todavia",
  "swt.disabled":        "desactivado",
  "swt.receivingAll":    "Recibiendo todos los eventos",
  "swt.created":         "Creado {date}",
  "swt.lastCalled":      "Ultima llamada {date}",
  "swt.testTitle":       "Enviar evento de prueba",
  "swt.deleteTitle":     "Eliminar",

  // batch 13 - eep.*
  "eep.prodArchTitle":         "Arquitectura lista para produccion",
  "eep.prodArch1":             "Entrega de activos estaticos con CDN via Cloudflare Pages",
  "eep.prodArch2":             "Integridad de suma de comprobacion SHA-256 en cada artefacto desplegado",
  "eep.prodArch3":             "El escaneo de seguridad controla cada compilacion. Los hallazgos criticos bloquean el despliegue",
  "eep.prodArch4":             "Control de admision: la puntuacion de validacion y el escaneo de seguridad deben pasar antes del despliegue",
  "eep.prodArch5":             "Certificacion de compilacion: se emite un certificado firmado por cada despliegue exitoso",
  "eep.secureInfraTitle":      "Infraestructura segura",
  "eep.secureInfra1":          "Cifrado de extremo a extremo para datos en transito y en reposo",
  "eep.secureInfra2":          "Alojado en infraestructura certificada SOC 2 Tipo II (Cloudflare Pages + Supabase)",
  "eep.secureInfra3":          "Analisis de seguridad estatico en cada compilacion: inyeccion SQL, XSS, eval() y deteccion de credenciales codificadas",
  "eep.secureInfra4":          "Politicas de seguridad a nivel de fila en todos los accesos a datos",
  "eep.secureInfra5":          "Gestion de secretos con almacenamiento en boveda cifrada",
  "eep.enterpriseDeployTitle": "Despliegue de nivel empresarial",
  "eep.enterpriseDeploy1":     "La sonda de salud HTTP con reintentos valida cada despliegue antes de marcarlo como activo",
  "eep.enterpriseDeploy2":     "Reversion automatica en controles de salud fallidos",
  "eep.enterpriseDeploy3":     "Control de gobernanza de despliegue: los fallos consecutivos bloquean nuevos despliegues automaticamente",
  "eep.enterpriseDeploy4":     "Verificacion de huella digital en vivo: suma de comprobacion desplegada comparada con el manifiesto de compilacion",
  "eep.enterpriseDeploy5":     "Almacenamiento de artefactos de compilacion en CDN global con URLs de vista previa compartibles",
  "eep.aiAutomationTitle":     "Automatizacion de ingenieria con IA",
  "eep.aiAutomation1":         "La IA genera y valida todo el codigo de la aplicacion en el pipeline de compilacion",
  "eep.aiAutomation2":         "Generacion automatizada de pruebas para cobertura de UI, API, datos y flujos de trabajo",
  "eep.aiAutomation3":         "Motor de reparacion autonomo: los fallos de validacion activan la reparacion de codigo especifica antes del despliegue",
  "eep.aiAutomation4":         "Clasificacion inteligente de errores y analisis de causa raiz",

  // batch 13 - lpp.*
  "lpp.describe":         "Describe lo que quieres construir",
  "lpp.generate":         "Generar",
  "lpp.analyzing":        "Analizando tu descripcion y planificando la arquitectura...",
  "lpp.secStandard":      "Estandar",
  "lpp.secElevated":      "Elevada",
  "lpp.secCritical":      "Critica",
  "lpp.securityBadge":    "Seguridad: {level}",
  "lpp.confidenceMatch":  "{n}% de coincidencia",
  "lpp.coreWorkflows":    "Flujos principales:",
  "lpp.buildApp":         "Construir esta app",
  "lpp.tryAnother":       "Intentar otra",
  "lpp.footer":           "Este es el planificador de arquitectura real de Nuvra. Registrate para generar la aplicacion completa y funcional.",
  "lpp.timeout":          "Tardo demasiado. Intentalo de nuevo.",
  "lpp.previewError":     "No se pudo crear una vista previa.",
  "lpp.prompt1":          "Construye un SaaS de gestion de proyectos con colaboracion en equipo",
  "lpp.prompt2":          "Crea un panel fintech con analiticas en tiempo real",
  "lpp.prompt3":          "Disena un marketplace de comercio electronico con herramientas para vendedores",

  // batch 13 - alp.*
  "alp.title":   "Registro de auditoria",
  "alp.entries": "{n} entradas",
  "alp.by":      "por {user}",

  // batch 14 - auth.*
  "auth.preparing": "Preparando tu experiencia...",
  "auth.checking":  "Verificando autenticacion...",

  // batch 14 - wm.*
  "wm.ariaLabel": "Creado con Nuvra - abrir detalles",
  "wm.builtWith": "Creado con",

  // batch 14 - agp.*
  "agp.endpoints":    "{n} endpoints",
  "agp.graphqlSchema": "Esquema GraphQL",

  // batch 14 - sfd.*
  "sfd.empty": "Envia un prompt para generar flujos del sistema",

  // batch 14 - prc.*
  "prc.loadError":  "No se pudo cargar el estado de los rieles de pago. Recarga para reintentar.",
  "prc.noRail":     "No hay via de pago configurada. Agrega FLUTTERWAVE_SECRET_KEY o una clave de Stripe.",
  "prc.webhookUrl": "URL de webhook de Flutterwave",
  "prc.title":         "Vias de pago",
  "prc.statusActive":  "Activa",
  "prc.statusReady":   "Configurada",
  "prc.statusMissing": "Sin configurar",
  "prc.webhookHint":   "Apunta el webhook de tu panel de Flutterwave aqui. Gestiona tanto los cargos de pago como la liquidacion de transferencias (pagos).",
  "prc.copyTitle":  "Copiar",

  // batch 14 - rn.*
  "rn.aiCredits":    "+{n} creditos de IA",
  "rn.upgradeTitle": "Mejora tu plan para seguir construyendo",
  "rn.viewPlans":    "Ver planes de pago",
  "rn.maybeLater":   "Quizas despues",

  // batch 14 - sd.*
  "sd.creditsRemaining": "creditos restantes",
  "sd.trial":            "Prueba",
  "sd.creditsUsed":      "Creditos de prueba usados",
  "sd.upgrade":          "Actualiza para seguir construyendo",
  "sd.creditsCompact":   "{n} creditos",

  // batch 14 - psb.*
  "psb.dismiss": "Cerrar",

  // batch 14 - dep.*
  "dep.title":         "Sistema de IA global",
  "dep.ai":            "IA",
  "dep.fixing":        "Reparando...",
  "dep.forceFix":      "Forzar reparacion",
  "dep.runtimeActive": "Tiempo de ejecucion activo",

  // batch 14 - ip.*
  "ip.search":    "Buscar integraciones...",
  "ip.configure": "Configurar",
  "ip.connect":   "Conectar",

  // batch 14 - wap.*
  "wap.totalRuns":       "Ejecuciones totales",
  "wap.avgSuccess":      "Exito promedio",
  "wap.avgDuration":     "Duracion promedio",
  "wap.activeWorkflows": "Flujos activos",
  "wap.runs":            "ejecuciones",
  "wap.success":         "exito",
  "wap.avgMs":           "Prom. {n}ms",
  "wap.fail":            "fallo",
  "wap.last":            "Ultimo: {date}",

  // batch 15 - acp.*
  "acp.heading":           "Contexto del Proyecto IA",
  "acp.routes":            "Rutas",
  "acp.footer":            "Este contexto se envia con cada solicitud de IA para que el asistente comprenda tu proyecto.",
  "acp.labelPages":        "Paginas",
  "acp.labelComponents":   "Componentes",
  "acp.labelDataModels":   "Modelos de datos",
  "acp.labelWorkflows":    "Flujos de trabajo",
  "acp.labelDesignTokens": "Tokens de diseno",

  // batch 15 - aip.*
  "aip.analyzing":        "Analizando...",
  "aip.score":            "Puntuacion de arquitectura",
  "aip.systems":          "Sistemas ({n}/{m})",
  "aip.issues":           "Problemas ({n})",
  "aip.recommendations":  "Recomendaciones ({n})",
  "aip.applyFix":         "Aplicar correccion",
  "aip.applied":          "Recomendacion aplicada",
  "aip.failedApply":      "Error al aplicar",
  "aip.autoFixFailed":    "Correccion automatica fallida",

  // batch 15 - cpp.*
  "cpp.heading":      "Vista previa de codigo",
  "cpp.generate":     "Generar",
  "cpp.materialize":  "Materializar",
  "cpp.emptyHeading": "No se ha generado codigo aun",
  "cpp.emptyDesc":    "Haz clic en Generar para codigo basado en contexto o en Materializar para archivos de arquitectura.",

  // batch 15 - ssp.*
  "ssp.platformStatus":  "Estado de la plataforma",
  "ssp.projectHealth":   "Salud del proyecto",
  "ssp.backgroundTasks": "Tareas en segundo plano",
  "ssp.recentLogs":      "Registros recientes",
  "ssp.checkingHealth":  "Verificando salud del servicio...",
  "ssp.noIssues":        "No se detectaron problemas",
  "ssp.noLogs":          "Sin registros aun",
  "ssp.outage":          "Interrupcion",
  "ssp.degraded":        "Degradado",
  "ssp.operational":     "Operacional",

  // batch 15 - vhp.*
  "vhp.noProject":   "No hay proyecto cargado",
  "vhp.heading":     "Historial de versiones ({n})",
  "vhp.noVersions":  "Sin versiones aun",
  "vhp.restored":    "Version restaurada",
  "vhp.description": "{n} bloques · {m} paginas",
  "vhp.scope":       "Guardado solo en este navegador. Se pierde al borrar los datos del sitio o cambiar de dispositivo.",

  // batch 15 - ce.*
  "ce.loading": "Cargando editor...",

  // batch 15 - aop.*
  "aop.idle":        "En espera",
  "aop.thinking":    "Analizando",
  "aop.editing":     "Editando",
  "aop.reviewing":   "Revisando",
  "aop.waiting":     "Esperando entrada",
  "aop.heading":     "Orquestacion IA",
  "aop.emptyTitle":  "Sin agentes IA activos",
  "aop.emptyDesc":   "Los agentes IA aparecen aqui cuando activas tareas de generacion o reparacion",
  "aop.active":      "{n} activos",
  "aop.inSession":   "{n} agentes en sesion",
  "aop.idleSection": "En espera",

  // batch 15 - rnp.*
  "rnp.heading":     "Historial de versiones",
  "rnp.loading":     "Cargando historial de versiones...",
  "rnp.empty":       "Sin eventos de version aun",
  "rnp.failedLoad":  "Error al cargar notas de version",
  "rnp.events":      "{n} eventos",
  "rnp.deployment":  "despliegue",
  "rnp.deployments": "despliegues",
  "rnp.build":       "compilacion",
  "rnp.builds":      "compilaciones",
  "rnp.submission":  "envio",
  "rnp.submissions": "envios",

  // batch 15 - sr.*
  "sr.title":      "Preparacion para la tienda",
  "sr.button":     "Tienda",
  "sr.ready":      "Listo",
  "sr.partial":    "Parcial",
  "sr.notReady":   "No listo",
  "sr.submit":     "Enviar",
  "sr.optimize":   "Optimizar",
  "sr.optimizing": "Optimizando...",
  "sr.highRisk":   "Alto riesgo: corrige los problemas antes de enviar",
  "sr.fixIssues":  "Corrige los problemas antes de enviar",

  // batch 15 - cm.*
  "cm.credentials":   "Credenciales",
  "cm.noCredentials": "Este conector no requiere credenciales.",
  "cm.required":      "requerido",
  "cm.alreadySet":    "ya configurado",
  "cm.viewDocs":      "Ver documentacion",
  "cm.update":        "Actualizar",
  "cm.saveEnable":    "Guardar y activar",
  "cm.test":          "Probar",
  "cm.disconnect":    "Desconectar",

  // batch 15 - ldp.*
  "ldp.heading":           "Documentos legales",
  "ldp.privacyPolicy":     "Politica de privacidad",
  "ldp.termsOfService":    "Terminos de servicio",
  "ldp.generatingPrivacy": "Generando politica de privacidad...",
  "ldp.generatingTerms":   "Generando terminos de servicio...",
  "ldp.dataCollected":     "Datos recopilados",
  "ldp.thirdParty":        "Servicios de terceros",
  "ldp.governing":         "Capacidades vigentes",

  // batch 15 - lav.*
  "lav.phaseIdle":         "Esperando",
  "lav.phaseAnalyzing":    "Analizando indicacion",
  "lav.phaseArchitecting": "Disenando arquitectura",
  "lav.phaseGenerating":   "Generando componentes",
  "lav.phaseConnecting":   "Estableciendo conexiones",
  "lav.phaseDeploying":    "Preparando despliegue",
  "lav.phaseComplete":     "Arquitectura lista",
  "lav.configuration":     "Configuracion",
  "lav.endpoints":         "Endpoints",
  "lav.tables":            "Tablas",
  "lav.files":             "Archivos",
  "lav.building":          "construyendo...",
  "lav.tableCount":        "{n} tablas",
  "lav.routeCount":        "{n} rutas",

  // batch 15 - awg.*
  "awg.heading":          "Generador de flujos IA",
  "awg.placeholder":      "Describe tu flujo de trabajo en lenguaje natural...",
  "awg.generating":       "Generando...",
  "awg.generateWorkflow": "Generar flujo",
  "awg.quickPrompts":     "Indicaciones rapidas",
  "awg.generated":        "Generado: {name}",
  "awg.nodesCreated":     "{n} nodos creados",
  "awg.suggestion1":      "Enviar correo de bienvenida tras registro de usuario",
  "awg.suggestion2":      "Procesar pago y generar factura",
  "awg.suggestion3":      "Resumen diario de analiticas para el equipo admin",
  "awg.suggestion4":      "Notificar a Slack en caso de fallo de despliegue",
  "awg.suggestion5":      "Flujo de aprobacion de usuarios para publicacion de contenido",

  // batch 16 - aarp.*
  "aarp.heading":        "Agentes IA",
  "aarp.scan":           "Escanear",
  "aarp.scanning":       "Escaneando...",
  "aarp.scanFailed":     "Error de escaneo",
  "aarp.approved":       "Aprobado",
  "aarp.rejected":       "Rechazado",
  "aarp.executed":       "Ejecutado",
  "aarp.failed":         "Fallido",
  "aarp.emptyTitle":     "Sin resultados de escaneo",
  "aarp.emptyDesc":      "Ejecuta un escaneo para obtener sugerencias de IA para refactorizacion, errores, optimizacion, cumplimiento y migracion.",
  "aarp.runFullScan":    "Ejecutar escaneo completo",
  "aarp.scanningAgents": "Escaneando con 5 agentes...",

  /* Pre-run credit estimate: what an action costs, what you have, what would remain. */
  "acp.upgrade": "Mejorar plan",
  "acp.cost": "Esto usa {n} creditos.",
  "acp.balance": "Tienes {n}.",
  "acp.remaining": "Te quedarian {n}.",
  "acp.short": "Esto necesita {n} creditos y tienes {have}. Recarga o mejora tu plan para ejecutarlo.",
  "acp.unknown": "No se pudo comprobar tu saldo de creditos ahora mismo.",
  "acp.action.full_app_gen": "Crear o editar una app",
  "acp.action.rebuild": "Reconstruccion dirigida",
  "acp.action.page_gen": "Generar una pagina",
  "acp.action.component_gen": "Generar un componente",
  "acp.action.targeted_fix_build": "Aplicar una correccion dirigida",
  "acp.action.auto_fix": "Corregir un problema",
  "acp.action.documentation": "Generar documentacion",
  "acp.action.ai_completion": "Preguntar sobre tu proyecto",
  "acp.action.agent_execution": "Ejecutar un agente autonomo",
  "acp.action.image_generation": "Generar una imagen",
  "acp.action.music_generation": "Generar musica",
  "acp.action.speech_generation": "Generar voz",
  "acp.action.voice_transcription": "Transcribir una nota de voz",
  "acp.action.deployment": "Desplegar en un host",
  "acp.action.project_export": "Exportar proyecto",
  "acp.action.aab": "Compilacion Android App Bundle (AAB)",
  "acp.action.twa": "Compilacion Android TWA",
  "acp.action.ipa": "Compilacion iOS (IPA)",
  "acp.action.refactor": "Refactorizar un componente",
  "acp.action.build_plan_preview": "Ver el plan de construccion",
  "acp.action.document_generated": "Generar un documento (longitud estandar)",
  "acp.action.document_chapter": "Generar un capitulo",
  "acp.action.document_render": "Renderizar un documento a PDF",
  "acp.action.document_image": "Generar una ilustracion del documento",
  "acp.action.document_export": "Exportar un documento (EPUB/DOCX/ZIP)",
  "aarp.creditCost":     "Creditos estimados:",
  "aarp.breakingChange": "Cambio ruptura",
  "aarp.approve":        "Aprobar",
  "aarp.reject":         "Rechazar",
  "aarp.execute":        "Ejecutar",
  "aarp.appliedSuccess": "Aplicado exitosamente",
  "aarp.allClear":       "Todo claro: no se encontraron problemas.",

  // batch 16 - op.*
  "op.stageArchitect":    "Arquitectura",
  "op.stageSchema":       "Base de datos",
  "op.stageBackend":      "Backend",
  "op.stageFrontend":     "Frontend",
  "op.stagePreview":      "Vista previa",
  "op.stageValidation":   "Validacion",
  "op.stageDeployment":   "Despliegue",
  "op.stagePublishing":   "Publicacion",
  "op.captionArchitect":  "Intencion a plano del sistema",
  "op.captionSchema":     "Generacion de esquema",
  "op.captionBackend":    "Rutas API y servicios",
  "op.captionFrontend":   "Componentes UI y paginas",
  "op.captionPreview":    "Contenedor en vivo",
  "op.captionValidation": "Auto-reparacion en fallo",
  "op.captionDeployment": "Empuje a produccion",
  "op.captionPublishing": "Envio a tienda",

  // batch 16 - car.*
  "car.heading":      "Actividad",
  "car.reconnecting": "Reconectando al servidor...",
  "car.disconnected": "Desconectado: eventos pausados",
  "car.emptyTitle":   "Sin actividad aun",
  "car.emptyDesc":    "Los eventos apareceran aqui cuando los colaboradores trabajen",

  // batch 16 - cpr.*
  "cpr.heading":       "Sesion",
  "cpr.collaborators": "Colaboradores",
  "cpr.aiAgents":      "Agentes IA",
  "cpr.emptyTitle":    "Sin participantes activos",
  "cpr.emptyWaiting":  "Esperando colaboradores",
  "cpr.notConnected":  "No conectado",

  // batch 16 - csp.*
  "csp.heading":            "Seguridad",
  "csp.blockRate":          "Tasa de bloqueo",
  "csp.coordinatorLoading": "Cargando coordinador...",
  "csp.crossNodeSync":      "Sincronizacion entre nodos",
  "csp.active":             "activo",
  "csp.offline":            "sin conexion",
  "csp.recentHighSeverity": "Alta severidad reciente",
  "csp.operations":         "Operaciones",
  "csp.assessed":           "Evaluados",
  "csp.blocked":            "Bloqueados",
  "csp.rateLimited":        "Limitados por tasa",
  "csp.validationFail":     "Fallo de validacion",
  "csp.quarantined":        "En cuarentena",
  "csp.aiBlocked":          "IA bloqueada",
  "csp.loadingMetrics":     "Cargando metricas...",
  "csp.detectorState":      "Estado del detector",
  "csp.users":              "usuarios",
  "csp.agents":             "agentes",
  "csp.replayTracker":      "Rastreador de repeticion",
  "csp.changeIds":          "IDs de cambio",
  "csp.distribution":       "Distribucion",

  // batch 16 - cpl.*
  "cpl.heading":              "Cumplimiento",
  "cpl.passed":               "aprobado",
  "cpl.nBlockers":            "{n} bloqueadores",
  "cpl.evaluating":           "Evaluando cumplimiento...",
  "cpl.evaluated":            "Evaluado",
  "cpl.detectedCapabilities": "Capacidades detectadas",
  "cpl.blockers":             "Bloqueadores ({n})",
  "cpl.requiresApproval":     "Requiere aprobacion ({n})",
  "cpl.warnings":             "Advertencias ({n})",
  "cpl.allClear":             "No se detectaron bloqueadores de cumplimiento",
  "cpl.errSignIn":            "Inicia sesion para evaluar el cumplimiento de este proyecto.",
  "cpl.errBuildFirst":        "Construye este proyecto primero para evaluar el cumplimiento.",
  "cpl.errGeneric":           "El cumplimiento no pudo evaluarse ahora. Intenta de nuevo pronto.",

  // batch 16 - smp.*
  "smp.heading":           "Metadatos de tienda",
  "smp.tabAppStore":       "App Store",
  "smp.tabPlayStore":      "Play Store",
  "smp.tabSeo":            "SEO",
  "smp.error":             "error",
  "smp.errors":            "errores",
  "smp.warning":           "advertencia",
  "smp.warnings":          "advertencias",
  "smp.ready":             "listo",
  "smp.loading":           "Creando metadatos de tienda...",
  "smp.validationsPassed": "Todas las validaciones pasaron",
  "smp.validation":        "Validacion ({n})",
  "smp.fieldTitle":        "Titulo",
  "smp.fieldSubtitle":     "Subtitulo",
  "smp.fieldKeywords":     "Palabras clave",
  "smp.fieldDescription":  "Descripcion",
  "smp.fieldShortDesc":    "Descripcion corta",
  "smp.fieldFullDesc":     "Descripcion completa",
  "smp.fieldMetaTitle":    "Titulo meta",
  "smp.fieldMetaDesc":     "Descripcion meta",
  "smp.fieldOgDesc":       "Descripcion OG",

  // batch 17 - ppc.*
  "ppc.heading":             "Politica de privacidad de Nuvra",
  "ppc.effectiveDate":       "Fecha de vigencia:",
  "ppc.lastUpdated":         "Ultima actualizacion:",
  "ppc.s1Title":             "1. Introduccion",
  "ppc.s1Intro":             "Nuvra (\"Nuvra\", \"nosotros\", \"nuestro\" o \"nos\") opera una plataforma de creacion de software impulsada por IA que permite a los usuarios disenar, generar, implementar y gestionar aplicaciones usando inteligencia artificial.",
  "ppc.s1Explains":          "Esta Politica de privacidad explica como recopilamos, usamos, divulgamos y protegemos la informacion cuando usa:",
  "ppc.s1Li1":               "el sitio web de Nuvra",
  "ppc.s1Li2":               "la plataforma de software de Nuvra",
  "ppc.s1Li3":               "servicios y APIs asociados",
  "ppc.s1Li4":               "cualquier aplicacion, herramienta o integracion relacionada",
  "ppc.s1Agree":             "Al usar Nuvra, acepta la recopilacion y el uso de informacion de acuerdo con esta politica.",
  "ppc.s2Title":             "2. Informacion que recopilamos",
  "ppc.s2Intro":             "Recopilamos informacion necesaria para operar la plataforma y brindar servicios.",
  "ppc.s2AccountTitle":      "2.1 Informacion de la cuenta",
  "ppc.s2AccountIntro":      "Cuando crea una cuenta, podemos recopilar:",
  "ppc.s2AccountLi1":        "nombre",
  "ppc.s2AccountLi2":        "correo electronico",
  "ppc.s2AccountLi3":        "credenciales de cuenta",
  "ppc.s2AccountLi4":        "informacion de perfil",
  "ppc.s2AccountLi5":        "datos de facturacion",
  "ppc.s2AccountLi6":        "informacion de la organizacion",
  "ppc.s2PlatformTitle":     "2.2 Datos de uso de la plataforma",
  "ppc.s2PlatformIntro":     "Cuando usa la plataforma de Nuvra, podemos recopilar:",
  "ppc.s2PlatformLi1":       "metadatos del proyecto",
  "ppc.s2PlatformLi2":       "registros de compilacion",
  "ppc.s2PlatformLi3":       "informacion de implementacion",
  "ppc.s2PlatformLi4":       "configuracion de la aplicacion",
  "ppc.s2PlatformLi5":       "solicitudes de API",
  "ppc.s2PlatformLi6":       "analiticas de uso de funciones",
  "ppc.s2PlatformOutro":     "Esto nos ayuda a mejorar la fiabilidad y el rendimiento.",
  "ppc.s2AiTitle":           "2.3 Datos de interaccion con IA",
  "ppc.s2AiIntro":           "Al usar funciones de IA como:",
  "ppc.s2AiLi1":             "Creador de software",
  "ppc.s2AiLi2":             "Modo de compilacion autonoma",
  "ppc.s2AiLi3":             "Asistente de IA",
  "ppc.s2AiLi4":             "Generacion de codigo",
  "ppc.s2AiLi5":             "Planificacion de arquitectura",
  "ppc.s2AiProcess":         "podemos procesar:",
  "ppc.s2AiPLi1":            "instrucciones",
  "ppc.s2AiPLi2":            "resultados generados",
  "ppc.s2AiPLi3":            "instrucciones de compilacion",
  "ppc.s2AiPLi4":            "comentarios del sistema",
  "ppc.s2AiOutro":           "Estos datos ayudan a Nuvra a generar y mejorar aplicaciones.",
  "ppc.s2TechTitle":         "2.4 Datos tecnicos",
  "ppc.s2TechIntro":         "Recopilamos automaticamente cierta informacion tecnica:",
  "ppc.s2TechLi1":           "direccion IP",
  "ppc.s2TechLi2":           "tipo de navegador",
  "ppc.s2TechLi3":           "tipo de dispositivo",
  "ppc.s2TechLi4":           "sistema operativo",
  "ppc.s2TechLi5":           "metricas de rendimiento",
  "ppc.s2TechLi6":           "registros de diagnostico",
  "ppc.s3Title":             "3. Como usamos la informacion",
  "ppc.s3Intro":             "Usamos la informacion recopilada para:",
  "ppc.s3Li1":               "operar y mantener la plataforma de Nuvra",
  "ppc.s3Li2":               "generar aplicaciones de software usando IA",
  "ppc.s3Li3":               "proporcionar servicios de infraestructura e implementacion",
  "ppc.s3Li4":               "procesar pagos y suscripciones",
  "ppc.s3Li5":               "mejorar la funcionalidad de la plataforma",
  "ppc.s3Li6":               "garantizar la seguridad y fiabilidad del sistema",
  "ppc.s3Li7":               "prevenir fraudes o abusos",
  "ppc.s3Li8":               "cumplir con las obligaciones legales",
  "ppc.s3NoSell":            "No vendemos datos personales a terceros.",
  "ppc.s4Title":             "4. Contenido y proyectos generados por IA",
  "ppc.s4Intro":             "Las aplicaciones generadas con Nuvra pueden incluir:",
  "ppc.s4Li1":               "interfaces de usuario",
  "ppc.s4Li2":               "APIs de backend",
  "ppc.s4Li3":               "bases de datos",
  "ppc.s4Li4":               "configuraciones de infraestructura",
  "ppc.s4Ownership":         "La propiedad del codigo del proyecto generado pertenece al usuario que lo creo, salvo que la ley aplicable o licencias de terceros lo restrinjan.",
  "ppc.s4Process":           "Nuvra puede procesar datos del proyecto unicamente para proporcionar funcionalidad a la plataforma y mejorar los servicios.",
  "ppc.s5Title":             "5. Almacenamiento de datos y seguridad",
  "ppc.s5Intro":             "Implementamos medidas de seguridad del sector incluyendo:",
  "ppc.s5Li1":               "transmision de datos cifrada",
  "ppc.s5Li2":               "controles de acceso",
  "ppc.s5Li3":               "proteccion de autenticacion",
  "ppc.s5Li4":               "supervision de infraestructura",
  "ppc.s5Li5":               "escaneo de vulnerabilidades",
  "ppc.s5Caveat":            "Sin embargo, ningun sistema de internet puede garantizar seguridad absoluta.",
  "ppc.s6Title":             "6. Retencion de datos",
  "ppc.s6Intro":             "Conservamos los datos solo el tiempo necesario para:",
  "ppc.s6Li1":               "prestar servicios",
  "ppc.s6Li2":               "mantener la integridad del sistema",
  "ppc.s6Li3":               "cumplir con las obligaciones legales",
  "ppc.s6Request":           "Los usuarios pueden solicitar la eliminacion de sus cuentas y datos asociados.",
  "ppc.s7Title":             "7. Transferencias internacionales de datos",
  "ppc.s7Intro":             "Nuvra puede procesar datos en multiples jurisdicciones.",
  "ppc.s7SafeguardsIntro":   "Cuando se requiere, usamos salvaguardas como:",
  "ppc.s7Li1":               "Clausulas contractuales estandar",
  "ppc.s7Li2":               "mecanismos legales de transferencia de datos",
  "ppc.s7Li3":               "opciones de alojamiento regional",
  "ppc.s7Outro":             "para proteger la informacion del usuario a traves de las fronteras.",
  "ppc.s8Title":             "8. Derechos del usuario",
  "ppc.s8Intro":             "Dependiendo de su jurisdiccion, puede tener derechos como:",
  "ppc.s8Li1":               "acceso a sus datos",
  "ppc.s8Li2":               "correccion de informacion inexacta",
  "ppc.s8Li3":               "eliminacion de informacion personal",
  "ppc.s8Li4":               "restriccion del procesamiento",
  "ppc.s8Li5":               "portabilidad de datos",
  "ppc.s8Li6":               "retirada del consentimiento",
  "ppc.s8Contact":           "Las solicitudes pueden enviarse a traves de la plataforma o contactando al soporte.",
  "ppc.s9Title":             "9. Cookies y tecnologias de seguimiento",
  "ppc.s9Intro":             "Nuvra puede usar cookies y tecnologias similares para:",
  "ppc.s9Li1":               "mantener sesiones de inicio de sesion",
  "ppc.s9Li2":               "mejorar la funcionalidad de la plataforma",
  "ppc.s9Li3":               "analizar patrones de uso",
  "ppc.s9Control":           "Los usuarios pueden controlar las cookies a traves de la configuracion del navegador.",
  "ppc.s10Title":            "10. Servicios de terceros",
  "ppc.s10Intro":            "Nuvra puede integrarse con servicios de terceros incluyendo:",
  "ppc.s10Li1":              "proveedores de infraestructura en la nube",
  "ppc.s10Li2":              "pasarelas de pago",
  "ppc.s10Li3":              "proveedores de modelos de IA",
  "ppc.s10Li4":              "herramientas de desarrollo",
  "ppc.s10Note":             "Estos servicios operan bajo sus propias politicas de privacidad.",
  "ppc.s11Title":            "11. Privacidad de menores",
  "ppc.s11NotFor":           "Los servicios de Nuvra no estan destinados a menores de 13 anos.",
  "ppc.s11NoCollect":        "No recopilamos a sabiendas informacion personal de menores.",
  "ppc.s12Title":            "12. Cambios a esta politica de privacidad",
  "ppc.s12Update":           "Podemos actualizar esta politica de vez en cuando para reflejar cambios legales u operativos.",
  "ppc.s12Posted":           "Las versiones actualizadas se publicaran con una fecha de vigencia revisada.",
  "ppc.s13Title":            "13. Informacion de contacto",
  "ppc.s13Intro":            "Para consultas o solicitudes de privacidad:",
  "ppc.s13Email":            "Correo electronico:",
  "ppc.s13EmailLink":        "hello@nuvrahq.com",

  // batch 18 - cmp.*, of.*, sg.*
  "cmp.placeholder":      "Describe el producto a desarrollar...",
  "cmp.submitLabel":      "Enviar a ingenieria",
  "cmp.ariaLabel":        "Compositor de mensajes",
  "cmp.stopEngineering":  "Detener ingenieria",
  "cmp.engineering":      "Ingenieria",
  "of.nodeAi":            "Motor IA",
  "of.nodeCodegen":       "Gen. Codigo",
  "of.nodeArch":          "Arquitecto",
  "of.nodeFrontend":      "Frontend",
  "of.nodeApi":           "Pasarela API",
  "of.nodeDb":            "Base de datos",
  "of.nodeDeploy":        "Despliegue",
  "of.nodeAuth":          "Autenticacion",
  "of.nodeMonitor":       "Monitor",
  "of.nucleus":           "NUCLEO",
  "sg.fields":            "campos",
  "sg.rels":              "rels",

  // ── Keys added by the main-branch merge (translated for parity) ──
  "nav.item.plugins": "Plugins",
  "nav.item.integrations": "Integraciones",
  "nav.item.connectors": "Integraciones",
  
  
  
  
  
  
  "pw.preview.hidePreview": "Ocultar panel de vista previa",
  "pw.preview.showPreview": "Mostrar panel de vista previa",
  "pw.preview.preview": "Vista previa",
  "pw.build.noLlm": "Motor de ingenieria no disponible.",
  "pw.build.noLlmHint": "No hay un proveedor de IA configurado. Las compilaciones estan en pausa hasta que el motor de IA este en linea. No se usaron creditos.",
  "pw.build.planLimit": "Limite mensual de compilaciones alcanzado.",
  "pw.build.planLimitHint": "Mejora tu plan para seguir construyendo.",
  "ver.search": "Buscar compilaciones",
  "ver.noMatch": "Ninguna compilacion coincide con tu busqueda.",
  "ext.error.unauthenticated": "Inicia sesion para explorar e instalar extensiones.",
  "ext.error.forbidden": "No tienes permiso para gestionar las extensiones de este proyecto.",
  "ext.error.notFound": "El registro de extensiones no esta disponible. Intentalo mas tarde.",
  "ext.error.server": "El servicio de extensiones no esta disponible. Intentalo en un momento.",
  "ext.noProjectTitle": "Abre esta pagina desde un proyecto para instalar y gestionar sus extensiones.",
  "ext.noProjectHint": "Puedes explorar el registro a continuacion.",
  "wh.title": "Webhooks",
  "wh.subtitle": "Envia notificaciones HTTP en tiempo real a tus endpoints cuando ocurren eventos en tu espacio de trabajo.",
  "wh.endpoints": "Endpoints",
  "wh.endpointsEmpty": "No hay endpoints de webhook configurados. Agrega uno para empezar a recibir eventos.",
  "wh.add": "Agregar endpoint",
  "wh.url": "URL del endpoint",
  "wh.urlPlaceholder": "https://tu-servidor.com/webhook",
  "wh.name": "Nombre",
  "wh.namePlaceholder": "Mi webhook",
  "wh.events": "Suscripciones a eventos",
  "wh.create": "Crear endpoint",
  "wh.cancel": "Cancelar",
  "wh.creating": "Creando",
  "wh.delete": "Eliminar",
  "wh.deleting": "Eliminando",
  "wh.enable": "Activar",
  "wh.disable": "Desactivar",
  "wh.enabled": "Activo",
  "wh.disabled": "En pausa",
  "wh.deliveries": "Historial de entregas",
  "wh.deliveriesEmpty": "Aun no hay entregas para este endpoint.",
  "wh.deliverySuccess": "Entregado",
  "wh.deliveryFailed": "Fallido",
  "wh.deliveryStatus": "Estado",
  "wh.deliveryAt": "Entregado el",
  "wh.deliveryEvent": "Evento",
  "wh.secretTitle": "Secreto de firma",
  "wh.secretDesc": "Usa este secreto para verificar las firmas de los webhooks. Se muestra solo una vez.",
  "wh.secretCopy": "Copiar secreto",
  "wh.secretCopied": "Copiado",
  "wh.secretClose": "He guardado el secreto",
  "wh.testSend": "Enviar evento de prueba",
  "wh.testing": "Enviando",
  "wh.testSent": "Evento de prueba enviado",
  "wh.loadError": "No se pudieron cargar los endpoints de webhook.",
  "wh.createError": "No se pudo crear el endpoint.",
  "wh.deleteError": "No se pudo eliminar el endpoint.",
  "wh.noOrg": "Los webhooks requieren un espacio de trabajo. Crea o unete a uno para gestionar endpoints de webhook.",
  "wh.eventTypes.build.completed": "build.completed",
  "wh.eventTypes.build.failed": "build.failed",
  "wh.eventTypes.deploy.completed": "deploy.completed",
  "wh.eventTypes.deploy.failed": "deploy.failed",
  "wh.eventTypes.project.created": "project.created",
  "wh.eventTypes.project.deleted": "project.deleted",
  "wh.eventTypes.member.joined": "member.joined",
  "wh.eventTypes.member.removed": "member.removed",
  "wh.loading": "Cargando endpoints",
  "team.title": "Equipo",
  "team.subtitle": "Gestiona los miembros del espacio de trabajo, sus roles e invitaciones.",
  "team.members": "Miembros",
  "team.membersEmpty": "Aun no hay miembros en el equipo.",
  "team.invite": "Invitar miembro",
  "team.inviteEmail": "Correo electronico",
  "team.inviteRole": "Rol",
  "team.inviteSend": "Enviar invitacion",
  "team.inviting": "Enviando",
  "team.invitations": "Invitaciones pendientes",
  "team.invitationsEmpty": "No hay invitaciones pendientes.",
  "team.role.admin": "Administrador",
  "team.role.member": "Miembro",
  "team.role.viewer": "Observador",
  "team.role.billing": "Facturacion",
  "team.remove": "Quitar",
  "team.removing": "Quitando",
  "team.cancel": "Cancelar",
  "team.revokeInvite": "Revocar",
  "team.loadError": "No se pudieron cargar los miembros del equipo.",
  "team.inviteError": "No se pudo enviar la invitacion.",
  "team.removeError": "No se pudo quitar al miembro.",
  "team.noOrg": "Aun no tienes un espacio de trabajo.",
  "team.createHint": "Nombra tu espacio de trabajo para invitar a tu equipo.",
  "team.workspaceNamePlaceholder": "Nombre del espacio",
  "team.createWorkspace": "Crear espacio",
  "team.workspaceNameShort": "El nombre debe tener al menos 2 caracteres.",
  "team.createError": "No se pudo crear el espacio de trabajo. Revisa tu conexion e intentalo de nuevo.",
  "team.retry": "Reintentar",
  "team.you": "Tu",
  "team.owner": "Propietario",
  "team.joinedAt": "Se unio",
  "team.loading": "Cargando equipo",
  "team.inviteSuccess": "Invitacion enviada.",
  "team.removeSuccess": "Miembro quitado.",
  "team.revokeSuccess": "Invitacion revocada.",
  "team.seats": "Puestos usados",
  "settings.privacy.controls.title": "Controles de privacidad y datos",
  "settings.privacy.controls.subtitle": "Controla el uso opcional de datos, las comunicaciones y los diagnósticos. Los cambios se guardan en tu cuenta de Nuvra cuando el almacenamiento de cuenta está disponible.",
  "settings.privacy.ai.title": "Datos para mejorar la IA",
  "settings.privacy.ai.desc": "Permite que Nuvra use datos de compilación anonimizados para mejorar el motor de IA.",
  "settings.privacy.analytics.title": "Analiticas del producto",
  "settings.privacy.analytics.desc": "Permitir que Nuvra recopile analiticas de uso anonimizadas para mejorar el producto.",
  "settings.privacy.marketing.title": "Comunicaciones de marketing",
  "settings.privacy.marketing.desc": "Recibir novedades del producto, consejos y anuncios por correo.",
  "settings.privacy.crash.title": "Informes de errores y fallos",
  "settings.privacy.crash.desc": "Enviar informes de error automaticamente para corregir fallos mas rapido.",
  "settings.privacy.saved": "Preferencias de privacidad guardadas.",
  "settings.privacy.saving": "Guardando",
  "settings.privacy.saveError": "No se pudieron guardar las preferencias. Intentalo de nuevo.",
  "settings.privacy.export.title": "Exportación de datos personales",
  "settings.privacy.export.desc": "Usa el centro de Exportación de datos de Nuvra para descargar los datos disponibles en tu cuenta.",
  "settings.privacy.export.btn": "Abrir Exportación de datos",
  "settings.privacy.regional.title": "Marcos de privacidad",
  "settings.privacy.regional.gdpr": "GDPR (UE/EEE)",
  "settings.privacy.regional.ccpa": "CCPA (California)",
  "settings.privacy.regional.ndpr": "NDPR (Nigeria)",
  "settings.privacy.policyLink": "Ver la politica de privacidad completa",
  "settings.appearance.title": "Apariencia",
  "settings.appearance.theme.title": "Tema",
  "settings.appearance.theme.dark": "Oscuro",
  "settings.appearance.theme.dark.desc": "La paleta oscura nativa de Nuvra. Ideal para sesiones largas.",
  "settings.appearance.theme.light": "Claro",
  "settings.appearance.theme.light.desc": "Una paleta clara para espacios iluminados.",
  "settings.appearance.theme.system": "Sistema",
  "settings.appearance.theme.system.desc": "Sigue la preferencia clara/oscura de tu sistema.",
  "settings.appearance.density.title": "Densidad de la interfaz",
  "settings.appearance.density.default": "Predeterminada",
  "settings.appearance.density.default.desc": "Espaciado estandar para la mayoria de pantallas.",
  "settings.appearance.density.compact": "Compacta",
  "settings.appearance.density.compact.desc": "Mas informacion, menos relleno.",
  "settings.appearance.saved": "Apariencia actualizada.",
  // Deployment Center (MANDATE 15)
  "dc.providers":          "Elige donde desplegar",
  "dc.soon":               "Pronto",
  "dc.preview":            "Vista previa",
  "dc.ship.liveAt":        "En vivo en",
  "dc.ship.notLive":       "El despliegue no llego a estar en vivo.",
  "dc.ship.signed":        "Firmado via {provider}.",
  "dc.ship.unsigned":      "Sin firma de codigo (no requerida para web).",
  "dc.ship.rollbackOk":    "Reversion disponible.",
  "dc.railway.title":      "Railway",
  "dc.railway.connected":  "Conectado como {account}",
  "dc.railway.disconnect": "Desconectar",
  "dc.railway.token":      "Token de API de Railway",
  "dc.railway.service":    "ID de servicio (opcional)",
  "dc.railway.env":        "ID de entorno (opcional)",
  "dc.railway.connect":    "Conectar Railway",
  "dc.cloud.title":        "Nuvra Cloud",
  "dc.cloud.prod":         "Produccion",
  "dc.cloud.live":         "en vivo",
  "dc.cloud.notLive":      "sin publicar",
  "dc.cloud.empty":        "Aun no hay despliegues. Publica tu ultima compilacion para verla aqui.",
  "dc.cloud.active":       "activo",
  "dc.cloud.rollback":     "Revertir a este despliegue",
  "dc.cloud.restart":      "Reiniciar este despliegue",
  "dc.cloud.logs":         "Registros del despliegue",
  "dc.env.title":          "Variables de entorno",
  "dc.env.desc":           "Cifradas en reposo. Los valores nunca se muestran despues de guardarse.",
  "dc.env.remove":         "Quitar",
  "dc.env.name":           "NOMBRE",
  "dc.env.value":          "valor",
  "dc.env.add":            "Agregar",
  "dc.error.generic":      "Algo salio mal. Intenta de nuevo.",
  "dc.history.title":      "Historial de despliegues",
  "dc.history.charged":    "{acu} creditos",
  "dc.history.note":       "Cada despliegue en todos los proveedores, con los creditos que consumio. Los despliegues fallidos se reembolsan.",
  "dc.history.status.failed":              "Fallido (reembolsado)",
  "dc.history.status.blocked_entitlement": "Limite del plan alcanzado",
  "dc.history.status.blocked_acu":         "Creditos insuficientes",
  "dc.history.status.refused":             "Rechazado",

  // Autorizacion de ejecucion: lo que Nuvra puede gastar y cambiar en este proyecto.
  "auth.title":            "Autorizacion de ejecucion",
  "auth.subtitle":         "Nuvra no puede gastar tus creditos ni cambiar este proyecto sin una autorizacion registrada para esa operacion exacta. Esto es lo que esta autorizado ahora, lo que ya ha usado y como detenerlo.",
  "auth.mode.enforce":     "Aplicando",
  "auth.mode.observe":     "Solo observando",
  "auth.ledger.durable":   "Registro duradero",
  "auth.ledger.memory":    "Registro en memoria (solo este proceso)",
  "auth.active.title":     "Autorizado ahora",
  "auth.active.none":      "Nada esta autorizado a gastar en este proyecto. Iniciar una construccion, un despliegue o un analisis crea una autorizacion para esa operacion.",
  "auth.history.title":    "Autorizaciones recientes",
  "auth.history.none":     "Todavia no hay autorizaciones registradas para este proyecto.",
  "auth.class.analysis":   "Analisis con IA",
  "auth.class.execution":  "Construir y cambiar",
  "auth.class.deployment": "Desplegar",
  "auth.class.rollback":   "Revertir",
  "auth.class.destructive": "Cambio destructivo",
  "auth.badge.production": "Produccion aprobada",
  "auth.badge.destructive": "Destructivo aprobado",
  "auth.remaining":        "Quedan {remaining} de {total} creditos",
  "auth.used":             "{used}/{total} creditos",
  "auth.revoke":           "Revocar",
  "auth.draws.none":       "No se ha retirado nada de esta autorizacion.",
  "auth.draw.open":        "{acu} retenidos",
  "auth.draw.settled":     "{acu} usados de {held} retenidos",
  "auth.draw.released":    "{acu} devueltos",
  "auth.error.load":       "No se pudieron cargar las autorizaciones.",
  "auth.error.revoke":     "No se pudo revocar esa autorizacion.",

  // Nuvra Support (superficie de cliente)
  "support.title":                "Soporte de Nuvra",
  "support.subtitle":             "Describe que esta fallando. El soporte de Nuvra lo investiga, resuelve lo que puede hacer con seguridad y suma a un ingeniero de soporte a la misma conversacion cuando no puede.",
  "support.newConversation":      "Nueva conversacion",
  "support.startTopic":           "Iniciar una conversacion sobre {topic}",
  "support.subjectPlaceholder":   "De que se trata?",
  "support.describePlaceholder":  "Describe el problema. Que estabas haciendo, que esperabas y que ocurrio en su lugar?",
  "support.start":                "Iniciar conversacion",
  "support.starting":             "Iniciando",
  "support.cancel":               "Cancelar",
  "support.emptyTitle":           "Como podemos ayudarte?",
  "support.emptyBody":            "Inicia una conversacion y el soporte de Nuvra la revisara. Si no puede resolver el problema con seguridad, un ingeniero de soporte se suma a la misma conversacion y ve todo lo que ya contaste.",
  "support.loading":              "Cargando",
  "support.composerPlaceholder":  "Escribe a Soporte de Nuvra…",
  "support.send":                 "Enviar",
  "support.sending":              "Enviando",
  "support.working":              "El soporte de Nuvra lo esta revisando",
  "support.sendFailed":           "No enviado",
  "support.retry":                "Reintentar",
  "support.reconnecting":         "Reconectando",
  "support.backToList":           "Volver a las conversaciones",
  "support.attachFiles":          "Adjuntar archivos",
  "support.attachRejected.type":    "ese tipo de archivo no se acepta. Envia un PNG, JPEG, GIF, WebP o PDF.",
  "support.attachRejected.size":    "ese archivo supera los 10 MB.",
  "support.attachRejected.session": "tu sesion expiro. Inicia sesion de nuevo para adjuntar archivos.",
  "support.attachRejected.failed":  "ese archivo no se pudo adjuntar. Intentalo de nuevo.",
  "support.removeAttachment":     "Quitar",
  "support.uploading":            "Subiendo",
  "support.attachmentLimit":      "Puedes adjuntar hasta 5 archivos a un mensaje.",
  "support.attachmentUnavailable": "no se pudo cargar",
  "support.withEngineer":         "Un ingeniero de soporte esta en esto",
  "support.listWithEngineer":     "con un ingeniero de soporte",
  "support.resolvedNote":         "Esta conversacion esta resuelta. Responde abajo cuando quieras para reabrirla.",
  "support.openFailed":           "No se pudo iniciar la conversacion",
  "support.status.open":          "Abierta",
  "support.status.answered":      "Respondida",
  "support.status.escalated":     "Con un ingeniero",
  "support.status.resolved":      "Resuelta",
  "nav.item.support":             "Soporte",
  "support.homeHeading": "Soporte de ingenieria que entiende tu espacio de trabajo.",
  "support.homeBody": "Cuenta a Nuvra que intentas hacer o que no funciona. Lo investigamos, resolvemos lo que se puede resolver con seguridad e incorporamos a un ingeniero de soporte cuando hace falta ayuda humana.",
  "support.topicsHeading": "Temas frecuentes",
  "support.topic.builds": "Compilaciones",
  "support.topic.deployments": "Despliegues",
  "support.topic.projects": "Proyectos",
  "support.topic.billing": "Facturacion",
  "support.topic.account": "Cuenta",
  "support.starter.account": "Necesito ayuda con mi cuenta: ",
  "support.starter.billing": "Tengo una pregunta sobre facturacion: ",
  "support.starter.projects": "Tengo un problema con un proyecto: ",
  "support.starter.deployments": "Tengo un problema con un despliegue: ",
  "support.starter.builds": "Tengo un problema con una compilacion: ",
  "support.searchPlaceholder": "Buscar conversaciones",
  "support.filterLabel": "Filtrar conversaciones",
  "support.filter.all": "Todas",
  "support.filter.open": "Abiertas",
  "support.filter.waiting": "Esperan tu respuesta",
  "support.filter.resolved": "Resueltas",
  "support.state.open": "Abierta",
  "support.state.waiting": "Espera tu respuesta",
  "support.state.with_engineer": "Con un ingeniero",
  "support.state.resolved": "Resuelta",
  "support.listEmpty": "Todavia no hay conversaciones.",
  "support.listNoMatch": "Ninguna conversacion coincide.",
  "support.untitled": "Solicitud de soporte",
  "support.markResolved": "Marcar resuelta",
  "support.threadLoadFailed": "No pudimos cargar esta conversacion.",
  "support.tryAgain": "Reintentar",
  "support.withEngineerNote": "Un ingeniero de soporte de Nuvra esta en esta conversacion. Puede ver todo lo que ya compartiste, asi que no necesitas repetirlo.",
  "support.category.how_to": "Como se hace",
  "support.category.bug": "Problema",
  "support.category.billing": "Facturacion",
  "support.category.account": "Cuenta",
  "support.category.policy_appeal": "Politica",
  "support.category.payout": "Pago",
  "support.category.dispute": "Disputa",
  "support.voice.you":            "Tu",
  "support.voice.ai":             "Soporte de Nuvra",
  "support.voice.engineer":       "Ingeniero de soporte",
  "support.voice.system":         "Nuvra",
  "help.supportPitch":            "Abre una conversacion con el soporte de Nuvra. El soporte investiga el problema y suma a un ingeniero a la misma conversacion cuando no puede resolverlo con seguridad.",
  "help.openSupport":             "Abrir el soporte de Nuvra",

  // Project infrastructure panel (what Nuvra can actually do with connected services).
  // The server sends stable ids and enums; every sentence below is the client's own copy,
  // which is why the panel needs no fallback to the server's English.
  "infra.triggerLabel": "Infraestructura",
  "infra.triggerTitle": "Lo que Nuvra puede hacer con tus servicios conectados",
  "infra.triggerAria": "Infraestructura del proyecto",
  "infra.heading": "Infraestructura",
  "infra.unreadable": "Nuvra no pudo leer las conexiones de este proyecto ahora mismo. Nada ha cambiado.",
  "infra.needsApproval": "(te pregunta primero)",
  "infra.ready": "Este proyecto tiene un destino donde construir.",
  "infra.notReady": "Conecta un repositorio para dar a este proyecto un destino donde construir.",
  "infra.autonomy.assisted": "Nuvra pregunta antes de cambiar algo.",
  "infra.autonomy.autonomous": "Nuvra trabaja por su cuenta, incluso en tu producto en produccion.",
  "infra.autonomy.autonomous_with_production_approval": "Nuvra trabaja por su cuenta y pregunta antes de cambiar tu producto en produccion.",
  "infra.cap.github.create_repo": "Crear un repositorio nuevo",
  "infra.cap.github.read_code": "Leer tu codigo fuente",
  "infra.cap.github.branch": "Crear ramas",
  "infra.cap.github.push": "Confirmar y enviar cambios",
  "infra.cap.github.pull_request": "Abrir solicitudes de cambio",
  "infra.cap.github.ci": "Ejecutar CI y leer los resultados",
  "infra.cap.supabase.inspect": "Inspeccionar tu esquema",
  "infra.cap.supabase.migrate": "Escribir migraciones",
  "infra.cap.supabase.apply": "Aplicar cambios a tu base de datos real",
  "infra.cap.vercel.preview": "Crear despliegues de vista previa",
  "infra.cap.vercel.production": "Desplegar a tu sitio en produccion",
  "infra.cap.vercel.logs": "Leer los registros de despliegue",
  "infra.cap.railway.deploy": "Desplegar tus servicios de backend",
  "infra.cap.railway.logs": "Leer los registros del servicio",
  "infra.state.github.not_configured": "GitHub aun no esta disponible en esta instalacion de Nuvra.",
  "infra.state.github.upgrade_required": "Mejora tu plan para que Nuvra trabaje con tus repositorios.",
  "infra.state.github.not_connected": "Conecta GitHub para que Nuvra guarde y actualice tu codigo.",
  "infra.state.github.no_resource_selected": "GitHub esta conectado. Elige un repositorio para este proyecto o deja que Nuvra cree uno.",
  "infra.state.github.paused": "Nuvra esta en pausa para este repositorio. Reanudalo cuando quieras que Nuvra continue.",
  "infra.state.github.connected": "Nuvra esta trabajando en este repositorio.",
  "infra.state.supabase.not_connected": "Conecta Supabase para que Nuvra gestione tu base de datos.",
  "infra.state.supabase.paused": "Nuvra esta en pausa para esta base de datos. Reanudalo cuando quieras que Nuvra continue.",
  "infra.state.supabase.connected": "Nuvra esta trabajando con esta base de datos.",
  "infra.state.vercel.not_connected": "Conecta Vercel para que Nuvra despliegue tu interfaz.",
  "infra.state.vercel.paused": "Nuvra esta en pausa para este sitio. Reanudalo cuando quieras que Nuvra continue.",
  "infra.state.vercel.connected": "Nuvra despliega en este sitio.",
  "infra.state.railway.not_connected": "Conecta Railway para que Nuvra ejecute tus servicios de backend.",
  "infra.state.railway.paused": "Nuvra esta en pausa para este servicio. Reanudalo cuando quieras que Nuvra continue.",
  "infra.state.railway.connected": "Nuvra ejecuta este servicio.",
  "settings.privacy.loading": "Cargando preferencias de privacidad...",
  "settings.privacy.synced": "Sincronizado con tu cuenta de Nuvra",
  "settings.privacy.localFallback": "Guardado en este dispositivo; la sincronización de cuenta no está disponible",
  "settings.privacy.notSynced": "Estado de sincronización de cuenta no disponible",
  "settings.privacy.data.title": "Tus datos",
  "settings.privacy.data.desc": "Revisa los datos que Nuvra pone a disposición de tu cuenta y usa los controles de exportación cuando necesites una copia.",
  "settings.privacy.regional.subtitle": "Los requisitos de privacidad varían según la ubicación. Estas referencias ayudan a entender los marcos que pueden aplicarse a tu uso de Nuvra.",
  "settings.privacy.rightsHint": "Puedes cambiar tus opciones de privacidad en cualquier momento",
  "settings.systemStatus.subtitle": "Señales de salud en vivo de la API, los proveedores de IA, la infraestructura de compilación, la base de datos y el servicio de incidentes de Nuvra.",
  "settings.systemStatus.refresh": "Actualizar",
  "settings.systemStatus.monitoring": "Monitoreo en vivo activado",
  "settings.systemStatus.lastChecked": "Comprobado {time}",
  "settings.systemStatus.incidentsSubtitle": "Los incidentes activos se actualizan en tiempo real cuando está disponible.",
  "settings.systemStatus.noIncidentsHint": "No hay incidentes activos registrados actualmente por Nuvra.",
  "settings.systemStatus.recentIncidents": "Incidentes recientes",
  "settings.systemStatus.pageChecked": "Página comprobada {time}",
  "settings.systemStatus.autoRefresh": "Se actualiza automáticamente cada 30 segundos",
  "settings.privacy.regional.gdpr.desc": "Derechos de privacidad de la UE/EEE y marco de protección de datos.",
  "settings.privacy.regional.ccpa.desc": "Derechos de privacidad de California y controles sobre datos del consumidor.",
  "settings.privacy.regional.ndpr.desc": "Derechos de privacidad de Nigeria y marco de protección de datos.",
  // Phase 6 build journey spine
  "bj.heading": "Recorrido de construcción",
  "bj.understand": "Entender",
  "bj.plan": "Planificar",
  "bj.build": "Construir",
  "bj.verify": "Verificar",
  "bj.live": "En línea",
  "bj.complete": "Completado",
  "bj.active": "En curso",
  "bj.failed": "Requiere atención",
  "bj.pending": "Sin iniciar",
  "bj.liveHint": "El estado en directo se basa en el trabajo que Nuvra ha informado realmente, no en el tiempo transcurrido.",

  "landing.showcase.eyebrow": "MIRA A NUVRA CONSTRUIR",
  "landing.showcase.h2a": "De la idea",
  "landing.showcase.h2b": "a producción",
  "landing.showcase.desc": "Observa el recorrido de ingeniería que Nuvra está diseñado para ejecutar: resolver la intención, planificar el sistema, construir por etapas, verificar el resultado y llevar el producto hacia producción.",
  "landing.showcase.cta": "Comenzar a construir",
  "landing.showcase.step1.label": "Empieza con una idea",
  "landing.showcase.step1.title": "Describe el sistema que quieres.",
  "landing.showcase.step1.desc": "Empieza en lenguaje natural. El planificador público de Nuvra convierte la idea en una vista concreta de arquitectura antes de iniciar una construcción autenticada.",
  "landing.showcase.step2.label": "Arquitectura primero",
  "landing.showcase.step2.title": "Convierte la intención en un plano del sistema.",
  "landing.showcase.step2.desc": "Los requisitos, servicios, datos, interfaces, roles y límites de seguridad se estructuran antes de que avance la ejecución del código.",
  "landing.showcase.step3.label": "Mira cómo se construye",
  "landing.showcase.step3.title": "Construye las capas en una secuencia controlada.",
  "landing.showcase.step3.desc": "Frontend, backend, datos y seguridad avanzan por etapas delimitadas en lugar de una sola generación opaca.",
  "landing.showcase.step4.label": "Verifica y repara",
  "landing.showcase.step4.title": "La validación permanece dentro del ciclo.",
  "landing.showcase.step4.desc": "Las comprobaciones aportan evidencia para la siguiente etapa. Cuando se detecta un fallo compatible, una reparación dirigida puede cerrar el ciclo antes de continuar.",
  "landing.showcase.step5.label": "Avanza hacia producción",
  "landing.showcase.step5.title": "Previsualiza, gobierna y publica.",
  "landing.showcase.step5.desc": "El resultado no es un simple volcado de código. Nuvra está diseñado para llevar el producto por previsualización, despliegue, monitorización y control de producción gobernado.",
};
